import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { bankService, userService, astraService, functions, auth, founderAdminAuth, founderAdminFunctions, normalizePhone, firestore, getSharedRecaptchaVerifier, resetSharedRecaptchaVerifier } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { ConfirmationResult, signInWithPhoneNumber } from 'firebase/auth';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';
import { detectClientPlatform, getPlatformAppDownloadLabel, getPlatformAppDownloadUrl } from '../utils/clientPlatform';
import { AppStoreBadges, BottomAppShowcase } from './public/PublicPrimitives';
import Logo, { CleroWordmarkSvg, CleroS } from './Logo';
import PhoneInput from './PhoneInput';
import WebPaymentHeroCard, { deriveClientMerchantLogoUrl, resolveCategoryIcon, isPdfMediaUri } from './WebPaymentHeroCard';
import CleroWebLoginBackground from './CleroWebLoginBackground';
import {
    ShieldCheck,
    ArrowRight,
    Lock,
    Smartphone,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    Receipt,
    Building2,
    Calendar,
    LogOut,
    X,
    Store,
    Utensils,
    Coffee,
    ShoppingBag,
    Car,
    User,
    UserX,
    Unlink,
    SearchX,
    Sliders,
    SlidersHorizontal,
} from 'lucide-react';
import PlaidLinkLauncher from './PlaidLinkLauncher';
import AstraAuthorizationSheet from './AstraAuthorizationSheet';
import PayerIdentityVerificationSheet from './PayerIdentityVerificationSheet';
import { getIdentitySubmissionErrorMessage } from '../services/identityFormValidation.mjs';
import AppBankAccountPickerWeb from './AppBankAccountPickerWeb';
import WebCta from './WebCta';
import WebReviewItemsView from './WebReviewItemsView';
import { WebItemIconAvatar } from './WebItemIconAvatar';
import { WebParticipantsView, WebParticipantRow } from './WebParticipantsView';
import { WebAppTakeoverBanner } from './WebAppTakeoverBanner';
import { WebOrganizerBlockedTakeover } from './WebOrganizerBlockedTakeover';
import { WebPaymentSuccessOverlay } from './WebPaymentSuccessOverlay';
import { WebReceiptViewerModal } from './WebReceiptViewerModal';
import { WebUserMenuDropdown } from './WebUserMenuDropdown';
import { getParticipantInitials, resolveWebParticipantIdentity, buildParticipantOrderMap, sortShareEntries } from '../utils/webParticipantAvatar';
import { useSheetLifecycle } from '../hooks/useSheetLifecycle';
import {
    triggerUiHaptic,
    triggerSuccessHaptic,
    triggerErrorHaptic,
    triggerImpactHaptic,
    triggerSelectionHaptic,
} from '../utils/webHaptics';
import { clearFounderPreviewSession, readFounderPreviewSession } from '../services/founderPreview';
import { clearSentryRuntimePhone, setSentryRuntimePhone, withSentrySpan } from '../services/sentry';
import {
    PAYMENT_OUTAGE_MESSAGE,
    paymentsAreDisabled,
    resolvePaymentOutageMessage,
} from '../services/paymentOutage';
import { openVenmoPayment } from '../services/venmo';
import { getLocalParticipantName } from '../services/localParticipantNames';
import {
    computeViewerEditableShareState,
    materializeSessionItemShares,
    removeSessionViewerShareOptimistic,
    replaceSessionViewerShareOptimistic,
    resolveSessionViewerShare,
    sumShareFractions,
    formatFractionAsShareString,
    isEqualSplit,
    roundClaimShareFraction,
} from '../services/itemShares';
import {
    computeParticipantTaxTipBreakdown,
    selectViewerTaxTipBreakdown,
} from '../services/receiptTaxTipBreakdown';
import {
    buildCanonicalIdentityFields,
    formatFirstAndLastInitial,
    formatOrganizerFirstName,
    formatPhoneForDisplay,
    resolveCanonicalIdentityName,
    resolveParticipantDisplayName,
    splitIdentityName,
} from '../services/participantIdentity';
import {
    getIdentityVerificationStatus,
    getIdentityVerificationWaitOutcome,
    getPaymentCapabilityStatus,
    isFailedAchRestrictionRetryTarget,
    isIdentityReviewOrRestrictionBlockingPayments,
    PaymentCapabilityStatus,
} from '../services/identityVerification';
import {
    clearPayerSetupResumeState,
    emptyPayerIdentityDraft,
    PayerIdentityDraft,
    readPayerSetupResumeState,
    writePayerSetupResumeState,
} from '../services/payerSetupState';
import { IdentityVerificationBlockedCard, IdentityVerificationBlockedModal } from './IdentityVerificationBlockedCard';
import { useWebPaymentsFeatureFlag } from '../hooks/useWebPaymentsEnabled';
import { useCleroPlusEnabled } from '../hooks/useCleroPlusEnabled';
import {
    getClientPaymentLimitCents as getUserAvailablePaymentLimitCents,
    getMaxBaseAmountForTotalLimit,
    finalizePaymentBankReconnect,
    hasActiveCleroPlusAccess,
    resolveOrganizerParticipantAmount,
    resolveFailedPayerAmounts,
    resolvePayerParticipantDisplayName,
    resolveWebPaymentQuote,
    shouldShowPayerPaymentFooter,
    shouldShowOrganizerParticipant,
} from '../services/webPaymentFlow.mjs';

const ITEM_ICON_EMOJI: Record<string, string> = {
    meal: '🍽️',
    drink: '🥤',
    coffee: '☕️',
    beer: '🍺',
    wine: '🍷',
    cocktail: '🍸',
    dessert: '🍰',
    icecream: '🍨',
    pizza: '🍕',
    burger: '🍔',
    salad: '🥗',
    soup: '🍲',
    noodles: '🍜',
    rice: '🍚',
    seafood: '🍣',
    snack: '🍟',
    market: '🛒',
    ticket: '🎟️',
    misc: '📦',
    generic: '•',
};

const PAYMENT_STATUS_REFRESH_INTERVAL_MS = 1_000;
const PAYMENT_STATUS_REFRESH_MAX_ATTEMPTS = 12;
const waitForPaymentStatusRefresh = (milliseconds: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, milliseconds);
});

type Invite = any;
type Session = any;

const getItemIconEmoji = (iconKey?: string, itemName?: string) => {
    if (iconKey && ITEM_ICON_EMOJI[String(iconKey).toLowerCase()]) {
        return ITEM_ICON_EMOJI[String(iconKey).toLowerCase()];
    }
    const name = String(itemName || '').toLowerCase();
    if (name.includes('beer') || name.includes('ipa') || name.includes('brew')) return '🍺';
    if (name.includes('wine') || name.includes('cabernet') || name.includes('chardonnay')) return '🍷';
    if (name.includes('cocktail') || name.includes('margarita') || name.includes('martini')) return '🍸';
    if (name.includes('coffee') || name.includes('latte') || name.includes('espresso') || name.includes('tea')) return '☕️';
    if (name.includes('drink') || name.includes('soda') || name.includes('coke') || name.includes('water') || name.includes('juice')) return '🥤';
    if (name.includes('pizza') || name.includes('flatbread')) return '🍕';
    if (name.includes('burger') || name.includes('sandwich') || name.includes('slider')) return '🍔';
    if (name.includes('salad') || name.includes('caesar') || name.includes('greens')) return '🥗';
    if (name.includes('soup') || name.includes('ramen') || name.includes('pho') || name.includes('noodle')) return '🍜';
    if (name.includes('dessert') || name.includes('cake') || name.includes('pie') || name.includes('cookie') || name.includes('sweet')) return '🍰';
    if (name.includes('ice cream') || name.includes('gelato') || name.includes('sorbet')) return '🍨';
    if (name.includes('sushi') || name.includes('fish') || name.includes('salmon') || name.includes('seafood') || name.includes('shrimp')) return '🍣';
    if (name.includes('fries') || name.includes('nachos') || name.includes('chip') || name.includes('snack') || name.includes('appetizer')) return '🍟';
    if (name.includes('ticket') || name.includes('movie') || name.includes('show')) return '🎟️';
    if (name.includes('grocery') || name.includes('market') || name.includes('store') || name.includes('supermarket')) return '🛒';
    return '🍽️';
};

const PayerMerchantAvatar: React.FC<{
    session: any;
    merchantName: string;
    className?: string;
}> = ({ session, merchantName, className = "w-full h-full" }) => {
    const [failedReceipt, setFailedReceipt] = useState(false);
    const [failedLogo, setFailedLogo] = useState(false);

    const rawReceipt = session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl;
    const isReceiptPdf = isPdfMediaUri(rawReceipt);
    const validReceipt = !isReceiptPdf && rawReceipt && !rawReceipt.startsWith('file://') ? rawReceipt : null;

    const rawLogo = session?.merchantLogoUrl || session?.merchantLogoURL || deriveClientMerchantLogoUrl(merchantName);
    const validLogo = rawLogo && !rawLogo.startsWith('file://') ? rawLogo : null;

    const CategoryIcon = resolveCategoryIcon(merchantName, session?.sessionIcon);
    const customBg = session?.sessionIconColor || '#00A99D';

    if (validReceipt && !failedReceipt) {
        return (
            <img
                src={validReceipt}
                alt={merchantName}
                className={`${className} object-cover`}
                onError={() => setFailedReceipt(true)}
            />
        );
    }
    if (validLogo && !failedLogo) {
        return (
            <img
                src={validLogo}
                alt={merchantName}
                className={`${className} object-contain p-1`}
                onError={() => setFailedLogo(true)}
            />
        );
    }
    return (
        <div className={`${className} flex items-center justify-center text-white`} style={{ backgroundColor: customBg }}>
            <CategoryIcon className="w-5 h-5 stroke-[2.2]" />
        </div>
    );
};

const roundClaimShareFractionLocal = (value: number) => Number(Math.max(0, Number(value || 0)).toFixed(4));
const cloneClaimDraftSession = (sourceSession: any) => ({
    ...(sourceSession || {}),
    items: Array.isArray(sourceSession?.items)
        ? sourceSession.items.map((item: any) => ({
            ...(item || {}),
            ...(item?.shares && typeof item.shares === 'object' ? { shares: { ...item.shares } } : {}),
            ...(item?.baseShares && typeof item.baseShares === 'object' ? { baseShares: { ...item.baseShares } } : {}),
        }))
        : [],
    claimLogs: Array.isArray(sourceSession?.claimLogs)
        ? sourceSession.claimLogs.map((log: any) => ({ ...(log || {}) }))
        : [],
});

interface Props {
    token: string;
    isSimulator?: boolean;
    triggerConfetti?: () => void;
}

type LogFounderPayerPreviewEventResult = {
    ok: true;
};

type EndFounderPayerPreviewSessionResult = {
    sessionId: string;
};

const LockIcon = ({ size = 20, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
);

const CheckIcon = ({ size = 20, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

const XIcon = ({ size = 20, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

const MinusIcon = ({ size = 18, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const PlusIcon = ({ size = 18, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const ReceiptIcon = ({ size = 20, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
        <path d="M16 8H8" />
        <path d="M16 12H8" />
        <path d="M13 16H8" />
    </svg>
);

const VenmoBadge = ({ compact = false }: { compact?: boolean }) => (
    <span className={`inline-flex items-center justify-center rounded-full bg-white text-[#008CFF] font-black ${compact ? 'w-5 h-5 text-[11px]' : 'w-7 h-7 text-sm'}`}>
        V
    </span>
);

const getInitials = (name: string) => {
    const safe = (name || '').trim();
    if (!safe) return '?';
    const parts = safe.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const formatItemHistoryTimestamp = (value: any) => {
    if (!value) return '';
    const date = (() => {
        if (typeof value?.toDate === 'function') return value.toDate();
        if (typeof value?.seconds === 'number') {
            const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
            return new Date((value.seconds * 1000) + Math.round(nanos / 1_000_000));
        }
        if (typeof value?._seconds === 'number') {
            const nanos = typeof value?._nanoseconds === 'number' ? value._nanoseconds : 0;
            return new Date((value._seconds * 1000) + Math.round(nanos / 1_000_000));
        }
        return new Date(value);
    })();
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const PayerTopNav: React.FC<{
    currentUser: any;
    currentUserProfile?: any;
    onLogout: () => void;
    loggingOut: boolean;
    onBack?: () => void;
    appDeepLink?: string;
}> = ({
    currentUser,
    currentUserProfile,
    onLogout,
    loggingOut,
    onBack,
    appDeepLink,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    const userPhone = currentUser?.phoneNumber ? formatPhoneForDisplay(currentUser.phoneNumber) : 'Verified User';
    const userDisplayName = currentUserProfile?.displayName || currentUserProfile?.name || (currentUserProfile?.firstName ? `${currentUserProfile.firstName} ${currentUserProfile.lastName || ''}`.trim() : '') || currentUser?.displayName || userPhone;
    const userPhotoUrl = currentUserProfile?.photoURL || currentUserProfile?.photoUrl || currentUser?.photoURL || null;
    const userInitial = userDisplayName && userDisplayName !== userPhone
        ? getParticipantInitials(userDisplayName)
        : (currentUser?.phoneNumber ? currentUser.phoneNumber.slice(-2) : 'U');
    const userHandle = currentUserProfile?.username || currentUserProfile?.handle || '';

    return (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none pt-3.5 px-4 sm:px-6">
            <header className="pointer-events-auto w-full max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm flex items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    {onBack ? (
                        <button
                            type="button"
                            onClick={onBack}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-700 hover:bg-slate-50 transition-all active:scale-95"
                            aria-label="Back"
                        >
                            <ChevronLeft className="w-4 h-4 stroke-[2.5]" />
                        </button>
                    ) : null}
                    
                    <div className="flex items-center pl-1 py-0.5">
                        <CleroWordmarkSvg height={22} width={76} color="#0D9488" className="shrink-0" />
                    </div>
                </div>

                {/* Right Action Area */}
                <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
                    {currentUser ? (
                        <div className="relative" ref={menuRef}>
                            <button
                                type="button"
                                onClick={() => setIsMenuOpen((prev) => !prev)}
                                className="w-8 h-8 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white overflow-hidden shadow-xs hover:scale-105 transition-all active:scale-95 cursor-pointer"
                                aria-label="User menu"
                            >
                                {userPhotoUrl ? (
                                    <img src={userPhotoUrl} alt={userDisplayName} className="w-full h-full object-cover" />
                                ) : (
                                    userInitial
                                )}
                            </button>

                            <WebUserMenuDropdown
                                isOpen={isMenuOpen}
                                onClose={() => setIsMenuOpen(false)}
                                userDisplayName={userDisplayName}
                                userPhone={userPhone}
                                username={userHandle}
                                userPhotoUrl={userPhotoUrl}
                                userInitial={userInitial}
                                onLogout={onLogout}
                                loggingOut={loggingOut}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center gap-2.5 sm:gap-4">
                            <a
                                href="/support"
                                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                            >
                                Help Center
                            </a>
                            <a
                                href={appDeepLink || 'https://clero.money/#hero-downloads'}
                                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                            >
                                Download App
                            </a>
                        </div>
                    )}
                </div>
            </header>
        </div>
    );
};

const getItemHistoryTimestampMs = (value: any, fallback: number) => {
    if (!value) return fallback;
    if (typeof value?.toDate === 'function') {
        const date = value.toDate();
        return Number.isNaN(date?.getTime?.()) ? fallback : date.getTime();
    }
    if (typeof value?.seconds === 'number') {
        const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
        return (value.seconds * 1000) + Math.round(nanos / 1_000_000);
    }
    if (typeof value?._seconds === 'number') {
        const nanos = typeof value?._nanoseconds === 'number' ? value._nanoseconds : 0;
        return (value._seconds * 1000) + Math.round(nanos / 1_000_000);
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? fallback : parsed;
};

const getPaymentLimitsSnapshot = (userData: any): any | null => {
    if (!userData || typeof userData !== 'object') return null;
    const snapshot = userData?.paymentLimits;
    return snapshot && typeof snapshot === 'object' ? snapshot : null;
};

const coerceCents = (value: any): number => {
    const cents = Number(value);
    if (!Number.isFinite(cents) || cents < 0) return 0;
    return Math.round(cents);
};

const formatPaymentLimitDollars = (cents: number): string => `$${(coerceCents(cents) / 100).toFixed(2)}`;

const formatLimitResetAt = (value: any): string | null => {
    if (!value) return null;
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

type LimitExceededErrorDetails = {
    limitType: 'daily' | 'weekly' | 'monthly' | 'single' | 'platform';
    limitAmountCents: number;
    resetDate: string | null;
};

type ClaimablePaymentConfirmationDetails = {
    code: string;
    reason: string | null;
    message: string | null;
    claimWindowDays: number;
    counterpartyDisplayName: string | null;
    flow: 'direct_pay' | 'split_single' | 'split_batch' | null;
};

type PaymentErrorCopy = {
    title: string;
    body: string;
    category:
        | 'service_unavailable'
        | 'sign_in_required'
        | 'bank_action_required'
        | 'verification_required'
        | 'support_required'
        | 'validation'
        | 'generic';
    retryable: boolean;
    retryAfterSec?: number | null;
};

type PaymentNoticeState = PaymentErrorCopy & {
    actionLabel?: string;
    onAction?: (() => void) | null;
};

type InsufficientFundsErrorDetails = {
    bankName: string;
    bankLast4: string;
    requiredAmountCents: number | null;
    effectiveBalanceCents: number | null;
    balanceSource: string | null;
    plaidAccountId: string | null;
};

const resolveErrorCode = (error: any): string => String(
    error?.code ||
    error?.details?.code ||
    error?.customData?.details?.code ||
    error?.customData?.code ||
    ''
).trim().toLowerCase();

const flattenToText = (value: any): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((entry) => flattenToText(entry)).filter(Boolean).join(' ');
    if (typeof value === 'object') {
        return Object.values(value as Record<string, any>)
            .map((entry) => flattenToText(entry))
            .filter(Boolean)
            .join(' ');
    }
    return String(value || '');
};

const resolveErrorMessage = (error: any): string => {
    if (typeof error?.details === 'string') return String(error.details).trim();
    if (typeof error?.details?.message === 'string') return String(error.details.message).trim();
    const customDetailsText = flattenToText(error?.customData?.details || error?.details || '');
    if (customDetailsText.trim().length > 0) return customDetailsText.trim();
    return String(error?.message || error || '').trim();
};

const resolveErrorDetails = (error: any): Record<string, any> => {
    if (error?.details && typeof error.details === 'object' && !Array.isArray(error.details)) {
        return error.details as Record<string, any>;
    }
    if (error?.customData?.details && typeof error.customData.details === 'object' && !Array.isArray(error.customData.details)) {
        return error.customData.details as Record<string, any>;
    }
    return {};
};

const resolveRetryAfterSec = (error: any): number | null => {
    const candidates = [
        error?.retryAfterSec,
        error?.details?.retryAfterSec,
        error?.details?.retry_after_sec,
    ];
    for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value >= 0) return Math.floor(value);
    }
    return null;
};

const isPoBoxAddressError = (error: unknown): boolean => {
    const code = resolveErrorCode(error as any);
    const message = resolveErrorMessage(error as any).toLowerCase();
    return (
        code.includes('astra_invalid_po_box_address')
        || message.includes('address must not be a post office box')
        || message.includes('address must not be a p.o. box')
        || message.includes('address must not be a po box')
        || message.includes('po box address cannot be used')
        || message.includes('p.o. box address cannot be used')
        || message.includes('post office box address cannot be used')
        || message.includes('po box addresses are not accepted')
    );
};

const readNullableNumber = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const getClaimablePaymentConfirmationDetails = (error: unknown): ClaimablePaymentConfirmationDetails | null => {
    const details = resolveErrorDetails(error as any);
    const code = String(details.code || (error as any)?.code || '').trim().toLowerCase();
    const message = resolveErrorMessage(error as any);
    const normalizedMessage = message.toLowerCase();
    const fallbackMatched =
        normalizedMessage.includes('does not have payments enabled on clero yet')
        || normalizedMessage.includes('must claim payment before bank deposit');
    if (
        code !== 'claimable_fallback_confirmation_required'
        && code !== 'recipient_claim_required_confirmation'
        && !fallbackMatched
    ) {
        return null;
    }
    const claimWindowDays = Number(details.claimWindowDays || details.claim_window_days || 20);
    return {
        code: code || (fallbackMatched ? 'claimable_fallback_confirmation_required' : ''),
        reason: String(details.reason || '').trim() || null,
        message: message.trim() || null,
        claimWindowDays: Number.isFinite(claimWindowDays) && claimWindowDays > 0 ? Math.round(claimWindowDays) : 20,
        counterpartyDisplayName: String(details.counterpartyDisplayName || details.organizerName || details.recipientName || '').trim() || null,
        flow: (() => {
            const flow = String(details.flow || '').trim().toLowerCase();
            if (flow === 'direct_pay' || flow === 'split_single' || flow === 'split_batch') return flow;
            return null;
        })(),
    };
};

const getInsufficientFundsErrorDetails = (error: unknown): InsufficientFundsErrorDetails | null => {
    const code = resolveErrorCode(error as any);
    const message = resolveErrorMessage(error as any).toLowerCase();
    const details = resolveErrorDetails(error as any);
    const reasonCode = String(details.reasonCode || details.reason_code || '').trim().toLowerCase();
    const requiredAmountCents = readNullableNumber(details.requiredAmountCents);
    const effectiveBalanceCents = readNullableNumber(details.effectiveBalanceCents);
    const hasStructuredEvidence =
        code.includes('insufficient_funds')
        || reasonCode.includes('insufficient_funds')
        || requiredAmountCents !== null
        || effectiveBalanceCents !== null;
    if ((!hasStructuredEvidence && !message.includes('insufficient funds')) || !hasStructuredEvidence) {
        return null;
    }
    return {
        bankName: String(details.bankName || details.bankDisplayName || 'Connected Bank').trim() || 'Connected Bank',
        bankLast4: String(details.bankLast4 || details.last4 || details.mask || '').trim(),
        requiredAmountCents,
        effectiveBalanceCents,
        balanceSource: String(details.balanceSource || '').trim() || null,
        plaidAccountId: String(details.plaidAccountId || '').trim() || null,
    };
};

const mergeInsufficientFundsErrorDetails = (
    details: InsufficientFundsErrorDetails | null,
    fallback?: Partial<InsufficientFundsErrorDetails> | null
): InsufficientFundsErrorDetails | null => {
    if (!details && !fallback) return null;
    return {
        bankName: String(details?.bankName || fallback?.bankName || 'Connected Bank').trim() || 'Connected Bank',
        bankLast4: String(details?.bankLast4 || fallback?.bankLast4 || '').trim(),
        requiredAmountCents: details?.requiredAmountCents ?? fallback?.requiredAmountCents ?? null,
        effectiveBalanceCents: details?.effectiveBalanceCents ?? fallback?.effectiveBalanceCents ?? null,
        balanceSource: String(details?.balanceSource || fallback?.balanceSource || '').trim() || null,
        plaidAccountId: String(details?.plaidAccountId || fallback?.plaidAccountId || '').trim() || null,
    };
};

const parseLimitExceededErrorDetails = (error: any): LimitExceededErrorDetails | null => {
    if (!error) return null;
    const details = error?.details || error;
    const code = String(details?.code || error?.message || '').trim().toLowerCase();

    if (code.includes('limit_exceeded') || code.includes('volume_limit')) {
        let limitType: LimitExceededErrorDetails['limitType'] = 'weekly';
        let limitAmountCents = 200000;

        if (code.includes('daily')) {
            limitType = 'daily';
            limitAmountCents = 100000;
        } else if (code.includes('monthly')) {
            limitType = 'monthly';
            limitAmountCents = 800000;
        } else if (code.includes('single')) {
            limitType = 'single';
            limitAmountCents = 100000;
        } else if (code.includes('platform')) {
            limitType = 'platform';
            limitAmountCents = 1500000;
        }

        return {
            limitType,
            limitAmountCents: coerceCents(details?.remainingLimitCents || details?.limitAmountCents || limitAmountCents),
            resetDate: details?.nextAllowedAt || null,
        };
    }

    const message = String(error?.message || error?.details?.message || '').toLowerCase();
    if (message.includes('exceed') && message.includes('limit')) {
        let limitType: LimitExceededErrorDetails['limitType'] = 'weekly';
        let limitAmountCents = 200000;
        if (message.includes('daily')) {
            limitType = 'daily';
            limitAmountCents = 100000;
        } else if (message.includes('monthly')) {
            limitType = 'monthly';
            limitAmountCents = 800000;
        } else if (message.includes('single')) {
            limitType = 'single';
            limitAmountCents = 100000;
        } else if (message.includes('platform')) {
            limitType = 'platform';
            limitAmountCents = 1500000;
        }
        return { limitType, limitAmountCents, resetDate: null };
    }

    return null;
};

const getLimitTypeLabel = (type: LimitExceededErrorDetails['limitType'] | null) => {
    switch (type) {
        case 'daily': return 'daily';
        case 'weekly': return 'weekly';
        case 'monthly': return 'monthly';
        case 'single': return 'per-payment';
        case 'platform': return 'platform daily';
        default: return 'transfer';
    }
};

const mapPaymentErrorForUser = (
    error: unknown,
    fallback: { title: string; body: string }
): PaymentErrorCopy => {
    const retryAfterSec = resolveRetryAfterSec(error);
    const outageMessage = resolvePaymentOutageMessage(error);
    if (outageMessage) {
        return {
            title: 'Payments are temporarily unavailable',
            body: 'Our payment service is temporarily unavailable. Please try again in a few minutes.',
            category: 'service_unavailable',
            retryable: true,
            retryAfterSec,
        };
    }

    const code = resolveErrorCode(error);
    const message = resolveErrorMessage(error).toLowerCase();
    const nextAllowedAt = formatLimitResetAt(
        (error as any)?.details?.nextAllowedAt
        || (error as any)?.customData?.details?.nextAllowedAt
        || null
    );
    const nextAllowedHint = nextAllowedAt ? ` You can try again on ${nextAllowedAt}.` : '';

    if (
        code.includes('astra_rate_limited')
        || code.includes('resource-exhausted')
        || message.includes('rate-limiting')
        || message.includes('rate limited')
        || message.includes('too many requests')
        || message.includes('astra api 429')
    ) {
        const waitHint = retryAfterSec && retryAfterSec > 0
            ? ` Please try again in about ${retryAfterSec} seconds.`
            : ' Please try again in a moment.';
        return {
            title: 'Payments are temporarily busy',
            body: `We’re seeing high payment traffic right now.${waitHint}`,
            category: 'service_unavailable',
            retryable: true,
            retryAfterSec,
        };
    }

    if (isPoBoxAddressError(error)) {
        return {
            title: 'Address not accepted',
            body: 'PO Box addresses are not accepted for identity verification. Please use your residential street address.',
            category: 'validation',
            retryable: false,
        };
    }

    if (
        code.includes('astra_invalid_state')
        || message.includes('state must be a two letter abbreviation')
    ) {
        return {
            title: 'Check your state',
            body: 'Please use the 2-letter state code for your address (for example, CA).',
            category: 'validation',
            retryable: false,
        };
    }

    if (
        code.includes('astra_duplicate_user_detected')
        || code.includes('already-exists')
        || message.includes('already linked to an existing astra profile')
    ) {
        return {
            title: 'Identity already in use',
            body: 'This identity is already linked to an existing Clero account.',
            category: 'verification_required',
            retryable: false,
        };
    }

    if (
        code.includes('astra_user_intent_limit_reached')
        || message.includes('maximum of 3 userintents')
        || message.includes('maximum of 3 user intents')
    ) {
        return {
            title: 'We’re reviewing your verification',
            body: 'Our payments team has been alerted and is reviewing your verification setup. We will email you with next steps shortly.',
            category: 'support_required',
            retryable: false,
        };
    }

    if (
        code.includes('recipient_permanently_rejected')
        || code.includes('organizer_permanently_rejected')
        || message.includes('is not eligible to receive payments on clero at this time')
    ) {
        return {
            title: 'Payment unavailable',
            body: resolveErrorMessage(error) || fallback.body,
            category: 'support_required',
            retryable: false,
        };
    }

    if (
        code.includes('insufficient_funds')
        || message.includes('insufficient funds')
    ) {
        return {
            title: 'Insufficient funds',
            body: 'Your selected bank account does not have enough available balance for this payment.',
            category: 'validation',
            retryable: false,
        };
    }

    if (
        code.includes('astra_account_not_approved')
        || message.includes('not approved for payments')
        || message.includes('connect a bank account in your name')
    ) {
        return {
            title: 'Bank unavailable',
            body: 'This bank cannot be used for payments.',
            category: 'validation',
            retryable: false,
        };
    }

    if (code.includes('single_payment_limit_exceeded')) {
        return {
            title: 'Payment exceeds limit',
            body: `Bank payments are limited to $1,000 per transfer.${nextAllowedHint}`,
            category: 'validation',
            retryable: false,
        };
    }

    if (code.includes('user_daily_payment_volume_limit_exceeded')) {
        return {
            title: 'Daily limit reached',
            body: `This payment is above your remaining daily transfer limit.${nextAllowedHint}`,
            category: 'validation',
            retryable: false,
        };
    }

    if (code.includes('user_weekly_payment_volume_limit_exceeded')) {
        return {
            title: 'Weekly limit reached',
            body: `This payment is above your remaining weekly transfer limit.${nextAllowedHint}`,
            category: 'validation',
            retryable: false,
        };
    }

    if (code.includes('user_monthly_payment_volume_limit_exceeded')) {
        return {
            title: 'Monthly limit reached',
            body: `This payment is above your remaining monthly transfer limit.${nextAllowedHint}`,
            category: 'validation',
            retryable: false,
        };
    }

    if (code.includes('platform_daily_payment_volume_limit_exceeded')) {
        return {
            title: 'Payments are busy today',
            body: `Clero has reached today’s bank transfer capacity.${nextAllowedHint}`,
            category: 'service_unavailable',
            retryable: true,
        };
    }

    if (
        code.includes('unavailable')
        || code.includes('deadline-exceeded')
        || code.includes('resource-exhausted')
        || code.includes('functions/internal')
        || code === 'internal'
        || message.includes('quota')
        || message.includes('temporarily')
        || message.includes('service unavailable')
    ) {
        return {
            title: 'Payments are temporarily unavailable',
            body: 'We are having trouble with our payment service right now. Please try again shortly.',
            category: 'service_unavailable',
            retryable: true,
            retryAfterSec,
        };
    }

    if (
        code.includes('unauthenticated')
        || code.includes('permission-denied')
        || message.includes('not logged in')
        || message.includes('sign in')
    ) {
        return {
            title: 'Sign in required',
            body: 'Please sign in again to continue with this payment step.',
            category: 'sign_in_required',
            retryable: true,
        };
    }

    if (
        message.includes('relink')
        || message.includes('connect a bank')
        || message.includes('bank account')
    ) {
        return {
            title: 'Bank action needed',
            body: 'Please update or reconnect your bank account to continue.',
            category: 'bank_action_required',
            retryable: true,
        };
    }

    if (
        message.includes('identity verification')
        || message.includes('verify your identity')
        || message.includes('userintent')
        || message.includes('kyc')
        || message.includes('astra authorization')
    ) {
        return {
            title: 'Verification in progress',
            body: 'We could not complete verification right now. Please try again in a moment.',
            category: 'verification_required',
            retryable: true,
        };
    }

    if (
        code.includes('invalid-argument')
        || code.includes('failed-precondition')
        || message.includes('missing')
        || message.includes('invalid')
    ) {
        return {
            title: fallback.title,
            body: fallback.body,
            category: 'validation',
            retryable: true,
        };
    }

    return {
        title: fallback.title,
        body: fallback.body,
        category: 'generic',
        retryable: true,
        retryAfterSec,
    };
};

const isPaymentCanceledError = (error: any) => {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || error || '').toLowerCase();
    return (
        code === 'canceled' ||
        code === 'cancelled' ||
        code === 'abort' ||
        code === 'aborted' ||
        message.includes('canceled') ||
        message.includes('cancelled') ||
        message.includes('abort')
    );
};

const resolveSafePaymentErrorMessage = (error: any, fallback: string) => {
    const outageMessage = resolvePaymentOutageMessage(error);
    if (outageMessage) {
        return outageMessage;
    }
    const raw = String(error?.message || error || '').trim();
    if (!raw) return fallback;
    const lower = raw.toLowerCase();

    // Never expose internal provider/runtime errors to users.
    if (
        lower.includes('undefined is not an object') ||
        lower.includes('cannot read properties of undefined') ||
        lower.includes('show().catch') ||
        lower.includes('ue.show')
    ) {
        return fallback;
    }
    const details = error?.details && typeof error.details === 'object' ? error.details : null;
    const detailCode = String(details?.limitCode || details?.code || '').trim().toLowerCase();
    const nextAllowedAt = formatLimitResetAt(details?.nextAllowedAt);
    if (detailCode === 'single_payment_limit_exceeded') {
        return 'This payment is above the $1,000 limit.';
    }
    if (detailCode === 'user_daily_payment_volume_limit_exceeded') {
        return nextAllowedAt
            ? `You reached today’s payment limit. Try again ${nextAllowedAt}.`
            : 'You reached today’s payment limit.';
    }
    if (detailCode === 'user_weekly_payment_volume_limit_exceeded') {
        return nextAllowedAt
            ? `You reached this week’s payment limit. Try again ${nextAllowedAt}.`
            : 'You reached this week’s payment limit.';
    }
    if (detailCode === 'user_monthly_payment_volume_limit_exceeded') {
        return nextAllowedAt
            ? `You reached this month’s payment limit. Try again ${nextAllowedAt}.`
            : 'You reached this month’s payment limit.';
    }
    if (detailCode === 'platform_daily_payment_volume_limit_exceeded') {
        return nextAllowedAt
            ? `Payments are paused for now. Try again ${nextAllowedAt}.`
            : 'Payments are paused for now.';
    }
    if (detailCode === 'payment_quote_changed') {
        return 'Your payment total changed. Review the latest amount and try again.';
    }
    if (
        detailCode.includes('insufficient_funds')
        || lower.includes('insufficient funds')
    ) {
        return 'Your selected bank account does not have enough available balance for this payment.';
    }
    if (
        detailCode.includes('organizer_permanently_rejected')
        || detailCode.includes('recipient_permanently_rejected')
    ) {
        return raw || 'This person is not verified to receive payments on Clero.';
    }
    if (
        detailCode.includes('astra_account_not_approved')
        || lower.includes('not approved for payments')
        || lower.includes('connect a bank account in your name')
        || lower.includes('cannot be used for payments')
    ) {
        return 'This bank cannot be used for payments.';
    }
    return raw;
};

const roundMoney = (value: number) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const toOptionalCents = (val: unknown): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const num = Number(val);
    return Number.isFinite(num) && num >= 0 ? Math.round(num) : null;
};
const resolveInviteTotalAmount = (inviteData: any) => {
    const totalOwedCents = Number(inviteData?.totalAmountOwedCents);
    if (Number.isFinite(totalOwedCents) && totalOwedCents >= 0) return roundMoney(totalOwedCents / 100);
    const derivedTotal = roundMoney(
        Math.max(0, Number(inviteData?.payeeBaseAmount || 0)) + Math.max(0, Number(inviteData?.payeeFeeAmount || 0))
    );
    if (derivedTotal > 0) return derivedTotal;
    const legacyTotal = Number(inviteData?.amount || 0);
    if (Number.isFinite(legacyTotal) && legacyTotal >= 0) return roundMoney(legacyTotal);
    return 0;
};
const isInviteFeeWaived = (inviteData: any) => {
    return inviteData?.feeWaivedByCleroPlus === true || inviteData?.payeeFeeWaivedByCleroPlus === true;
};

const resolveInvitePayeeFee = (inviteData: any, sessionData: any) => {
    void sessionData;
    if (isInviteFeeWaived(inviteData)) return 0;
    const feeOwedCents = Number(inviteData?.feeAmountOwedCents);
    if (Number.isFinite(feeOwedCents) && feeOwedCents >= 0) return roundMoney(feeOwedCents / 100);
    const explicitFee = Number(inviteData?.payeeFeeAmount ?? 0);
    if (Number.isFinite(explicitFee) && explicitFee >= 0) return roundMoney(explicitFee);
    return 0;
};
const resolveInviteBaselineFee = (inviteData: any, sessionData: any) => {
    const waivedCents = Number(inviteData?.waivedFeeAmountCents);
    if (Number.isFinite(waivedCents) && waivedCents > 0) return roundMoney(waivedCents / 100);
    const feeOwedCents = Number(inviteData?.feeAmountOwedCents);
    if (Number.isFinite(feeOwedCents) && feeOwedCents > 0) return roundMoney(feeOwedCents / 100);
    const explicitFee = Number(inviteData?.payeeFeeAmount ?? inviteData?.appliedPayeeFeeAmount ?? 0);
    if (Number.isFinite(explicitFee) && explicitFee > 0) return roundMoney(explicitFee);
    const isGroupSettlement = String(sessionData?.splitSource || '').trim().toLowerCase() === 'group_settlement' || Boolean(sessionData?.isGroupSettlement);
    if (isGroupSettlement) {
        const baseAmount = resolveInviteBaseAmount(inviteData, sessionData);
        if (baseAmount > 0) return Math.min(2.99, roundMoney(Math.ceil(baseAmount * 100 * 0.01) / 100));
    }
    return 0;
};
const resolveInviteBaseAmount = (inviteData: any, sessionData: any) => {
    void sessionData;
    const rawExplicitBase = inviteData?.payeeBaseAmount ?? inviteData?.appliedPayeeBaseAmount ?? inviteData?.baseAmount;
    if (rawExplicitBase !== undefined && rawExplicitBase !== null && rawExplicitBase !== '') {
        const explicitBase = Number(rawExplicitBase);
        if (Number.isFinite(explicitBase) && explicitBase >= 0) return roundMoney(explicitBase);
    }
    const baseOwedCents = Number(inviteData?.baseAmountOwedCents);
    if (Number.isFinite(baseOwedCents) && baseOwedCents >= 0) return roundMoney(baseOwedCents / 100);
    const totalAmount = resolveInviteTotalAmount(inviteData);
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) return 0;
    const explicitFee = Number(inviteData?.payeeFeeAmount || inviteData?.appliedPayeeFeeAmount || 0);
    if (Number.isFinite(explicitFee) && explicitFee >= 0) {
        return roundMoney(Math.max(0, totalAmount - explicitFee));
    }
    return roundMoney(totalAmount);
};
const resolveInviteProgress = (inviteData: any, sessionData: any) => {
    const feeWaived = isInviteFeeWaived(inviteData);
    const baseAmount = resolveInviteBaseAmount(inviteData, sessionData);
    const totalAmount = feeWaived ? baseAmount : resolveInviteTotalAmount(inviteData);
    const feeAmount = resolveInvitePayeeFee(inviteData, sessionData);
    const paidTotalCents = toOptionalCents(inviteData?.totalAmountPaidCents);
    const remainingTotalCents = toOptionalCents(inviteData?.totalAmountRemainingCents);
    const paidBaseCents = toOptionalCents(inviteData?.baseAmountPaidCents);
    const remainingBaseCents = toOptionalCents(inviteData?.baseAmountRemainingCents);
    const paidFeeCents = toOptionalCents(inviteData?.feeAmountPaidCents);
    const remainingFeeCents = toOptionalCents(inviteData?.feeAmountRemainingCents);
    const settledPaidBaseAmount = paidBaseCents !== null
        ? roundMoney(paidBaseCents / 100)
        : (paidTotalCents !== null
            ? roundMoney(Math.max(0, (paidTotalCents - (paidFeeCents !== null ? paidFeeCents : 0)) / 100))
            : (remainingBaseCents !== null
                ? roundMoney(Math.max(0, baseAmount - (remainingBaseCents / 100)))
                : 0));
    const settledRemainingBaseAmount = remainingBaseCents !== null
        ? roundMoney(Math.max(0, remainingBaseCents / 100))
        : roundMoney(Math.max(0, baseAmount - settledPaidBaseAmount));
    const settledPaidFeeAmount = feeWaived
        ? 0
        : (paidFeeCents !== null
            ? roundMoney(paidFeeCents / 100)
            : (remainingFeeCents !== null
                ? roundMoney(Math.max(0, feeAmount - (remainingFeeCents / 100)))
                : 0));
    const settledRemainingFeeAmount = feeWaived
        ? 0
        : (remainingFeeCents !== null
            ? roundMoney(Math.max(0, remainingFeeCents / 100))
            : roundMoney(Math.max(0, feeAmount - settledPaidFeeAmount)));
    const settledPaidAmount = feeWaived
        ? settledPaidBaseAmount
        : (paidTotalCents !== null
            ? roundMoney(paidTotalCents / 100)
            : (remainingTotalCents !== null
                ? roundMoney(Math.max(0, totalAmount - (remainingTotalCents / 100)))
                : roundMoney(settledPaidBaseAmount + settledPaidFeeAmount)));
    const settledRemainingAmount = feeWaived
        ? settledRemainingBaseAmount
        : (remainingTotalCents !== null
            ? roundMoney(Math.max(0, remainingTotalCents / 100))
            : roundMoney(Math.max(0, totalAmount - settledPaidAmount)));
    const paymentCollectionStatus = String(inviteData?.paymentCollectionStatus || '').trim().toLowerCase();
    const processingStatus = paymentCollectionStatus === 'processing'
        || (paymentCollectionStatus.length === 0 && String(inviteData?.status || '').trim().toLowerCase() === 'processing');
    const processingAmount = processingStatus ? roundMoney(Math.max(0, Number(inviteData?.processingPaymentTotalAmountCents || 0) / 100)) : 0;
    const processingBaseAmount = processingStatus ? roundMoney(Math.max(0, Number(inviteData?.processingPaymentBaseAmountCents || 0) / 100)) : 0;
    const processingFeeAmount = processingStatus ? roundMoney(Math.max(0, Number(inviteData?.processingPaymentFeeAmountCents || 0) / 100)) : 0;
    const failedAmount = roundMoney(Math.max(0, Number(inviteData?.failedPaymentTotalAmountCents || 0) / 100));
    const failedBaseAmount = roundMoney(Math.max(0, Number(inviteData?.failedPaymentBaseAmountCents || 0) / 100));
    const failedFeeAmount = roundMoney(Math.max(0, Number(inviteData?.failedPaymentFeeAmountCents || 0) / 100));
    const paidBaseAmount = roundMoney(Math.min(baseAmount, settledPaidBaseAmount + processingBaseAmount));
    const remainingBaseAmount = roundMoney(Math.max(0, baseAmount - paidBaseAmount));
    const paidFeeAmount = feeWaived ? 0 : roundMoney(Math.min(feeAmount, settledPaidFeeAmount + processingFeeAmount));
    const remainingFeeAmount = feeWaived ? 0 : roundMoney(Math.max(0, feeAmount - paidFeeAmount));
    const paidAmount = feeWaived ? paidBaseAmount : roundMoney(Math.min(totalAmount, settledPaidAmount + processingAmount));
    const remainingAmount = feeWaived ? remainingBaseAmount : roundMoney(Math.max(0, totalAmount - paidAmount));
    const inviteClaims = inviteData?.itemClaims && typeof inviteData.itemClaims === 'object' ? inviteData.itemClaims : {};
    const hasExplicitItemClaims = Object.keys(inviteClaims).length > 0;
    const claimItemsPending =
        Boolean(sessionData?.isItemClaimingEnabled)
        && totalAmount <= 0.0001
        && paidAmount <= 0.0001
        && !hasExplicitItemClaims;
    const isFeeWaivedSettled = feeWaived && (
        (baseAmount > 0.0001 && remainingBaseAmount <= 0.0001)
        || (paymentCollectionStatus === 'accepted' && remainingBaseAmount <= 0.009)
    );
    const isFullyPaid = !claimItemsPending && (
        settledRemainingAmount <= 0.0001
        || isFeeWaivedSettled
        || ['paid', 'verified', 'settled_offline'].includes(String(inviteData?.status || ''))
    );
    return {
        totalAmount,
        settledPaidAmount,
        settledRemainingAmount,
        paidAmount,
        remainingAmount,
        baseAmount,
        settledPaidBaseAmount,
        settledRemainingBaseAmount,
        paidBaseAmount,
        remainingBaseAmount,
        feeAmount,
        settledPaidFeeAmount,
        settledRemainingFeeAmount,
        paidFeeAmount,
        remainingFeeAmount,
        processingAmount,
        processingBaseAmount,
        processingFeeAmount,
        failedAmount,
        failedBaseAmount,
        failedFeeAmount,
        paymentCollectionStatus,
        isPaymentProcessing: processingAmount > 0.0001,
        isPartiallyPaid: paidAmount > 0 && remainingAmount > 0,
        isFullyPaid: !claimItemsPending && settledRemainingAmount <= 0.0001,
        claimItemsPending,
        hasFailedOutstanding: failedAmount > 0.0001,
    };
};

const ASTRA_RETURN_PATH = '/payer-astra-return';

const isAstraIdentityVerified = (userData: any) => getIdentityVerificationStatus(userData) === 'verified';

const hasAstraPaymentAuthorization = (userData: any) => {
    const authData = userData?.astra?.auth || {};
    return Boolean(
        authData?.accessTokenEncrypted ||
        authData?.refreshTokenEncrypted ||
        authData?.accessToken ||
        authData?.refreshToken ||
        userData?.astraAccessToken ||
        userData?.astraRefreshToken
    );
};

const isEligiblePaymentPlaidAccount = (account: any) => {
    const type = String(account?.accountType || account?.type || '').toLowerCase().trim();
    const subtype = String(account?.accountSubtype || account?.subtype || '').toLowerCase().trim();
    const descriptor = `${type} ${subtype}`.toLowerCase();
    if (descriptor.includes('credit') || descriptor.includes('loan') || descriptor.includes('liability')) return false;
    if (type && type !== 'depository') return false;
    if (subtype && subtype !== 'checking' && subtype !== 'savings') return false;
    return !isPlaidAccountPaymentIneligible(account);
};

const BLOCKED_ACCOUNT_STATUSES = new Set([
    'rejected',
    'failed',
    'blocked',
    'disabled',
    'ineligible',
    'restricted',
    'suspended',
    'closed',
    'removed',
]);

const isPlaidAccountPaymentIneligible = (account: any): boolean => {
    if (!account) return false;
    const reasonCode = String(account?.paymentSystemReasonCode || '').trim().toLowerCase();
    if (reasonCode === 'astra_account_not_approved') return true;

    const message = String(account?.paymentSystemMessage || '').trim().toLowerCase();
    if (
        message.includes('not approved for payments')
        || message.includes('connect a bank account in your name')
        || message.includes('cannot be used for payments')
    ) {
        return true;
    }

    const astraStatus = String(account?.astraStatus || account?.astra?.accountStatus || '').trim().toLowerCase();
    const astraEligibilityStatus = String(account?.astraEligibilityStatus || account?.astra?.eligibilityStatus || '').trim().toLowerCase();
    return BLOCKED_ACCOUNT_STATUSES.has(astraStatus) || BLOCKED_ACCOUNT_STATUSES.has(astraEligibilityStatus) || astraEligibilityStatus === 'ineligible';
};

const doesPlaidAccountRequireRelink = (account: any): boolean => {
    return (
        String(account?.paymentSystemStatus || '').trim().toLowerCase() === 'needs_bank_relink'
        || account?.needsReconnect === true
        || String(account?.bankStatus || '').trim().toLowerCase() === 'needs_update'
    );
};

const getPlaidAccountBankName = (account: any): string => {
    const raw = String(account?.bankName || account?.bankDisplayName || '').trim();
    return raw || 'Bank Account';
};

const getPlaidAccountLast4 = (account: any): string => {
    const digits = String(account?.bankLast4 || account?.last4 || '').replace(/\D/g, '');
    return digits ? digits.slice(-4) : '****';
};

const getPlaidAccountTypeLabel = (account: any): string => {
    const raw = String(account?.subtype || account?.accountType || account?.type || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw === 'checking') return 'Checking';
    if (raw === 'savings') return 'Savings';
    if (raw === 'depository') return '';
    return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const normalizeBankLogoUri = (value: unknown): string | null => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    if (raw.startsWith('<svg')) return `data:image/svg+xml;utf8,${encodeURIComponent(raw)}`;
    if (/^PHN2Zy/i.test(raw.replace(/\s+/g, ''))) return `data:image/svg+xml;base64,${raw}`;
    return `data:image/png;base64,${raw}`;
};

const getPlaidAccountLogo = (account: any): string | null => (
    normalizeBankLogoUri(account?.logo || account?.bankLogoUrl || account?.cardImageUrl || null)
);

const getPreferredPaymentPlaidAccount = (accounts: any[], preferredAccountIds: Array<string | null | undefined>) => {
    const eligibleAccounts = (Array.isArray(accounts) ? accounts : []).filter(isEligiblePaymentPlaidAccount);
    if (eligibleAccounts.length === 0) return null;

    for (const value of preferredAccountIds) {
        const preferredId = String(value || '').trim();
        if (!preferredId) continue;
        const match = eligibleAccounts.find((account) => (
            String(account?.id || '') === preferredId && !doesPlaidAccountRequireRelink(account)
        ));
        if (match) return match;
    }

    const firstHealthy = eligibleAccounts.find((account) => !doesPlaidAccountRequireRelink(account));
    if (firstHealthy) return firstHealthy;
    return eligibleAccounts[0] || null;
};

const getIdentityDraftFromUser = (userData: any): PayerIdentityDraft => {
    const draft = emptyPayerIdentityDraft();
    const canonicalName = resolveCanonicalIdentityName(userData, userData?.phone || '');
    const nameParts = splitIdentityName(canonicalName);
    return {
        ...draft,
        firstName: nameParts.firstName || '',
        lastName: nameParts.lastName || '',
        email: String(userData?.email || '').trim(),
    };
};

const hasCompleteIdentityDraft = (draft: PayerIdentityDraft) => Boolean(
    draft.firstName.trim()
    && draft.lastName.trim()
    && draft.email.trim()
    && draft.addressLine1.trim()
    && draft.city.trim()
    && draft.state.trim()
    && draft.postalCode.trim().length === 5
    && draft.dobMonth.trim().length >= 1
    && draft.dobDay.trim().length >= 1
    && draft.dobYear.trim().length === 4
    && draft.ssnLast4.trim().length === 4
);

const IDENTITY_DECISION_WAIT_TIMEOUT_MS = 60_000;
const IDENTITY_VERIFICATION_STAGES = [
    'Encrypting your details…',
    'Sending verification to Astra…',
    'Reviewing your identity…',
    'Checking account readiness…',
    'Finalizing your verification…',
] as const;
type IdentityVerificationWaitMode = 'idle' | 'active' | 'timed_out';

const PayerPage: React.FC<Props> = ({ token, isSimulator, triggerConfetti }) => {
    const isLocalhost = typeof window !== 'undefined' && Boolean(
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.endsWith('.local')
    );

    const payerConsole = isLocalhost
        ? console
        : { log: () => {}, warn: () => {}, error: () => {}, debug: () => {} };
    const detectedClientPlatform = useMemo(() => detectClientPlatform(), []);
    const iosDownloadUrl = IOS_INVITE_URL;
    const androidDownloadUrl = ANDROID_INVITE_URL;
    const openExternalLink = async (url: string) => {
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        if (opened) return;

        try {
            window.location.href = url;
            return;
        } catch {
            // continue to clipboard fallback
        }

        try {
            await navigator.clipboard.writeText(url);
            alert(`Unable to open automatically. Link copied:\n${url}`);
        } catch {
            alert(`Unable to open automatically. Use this link:\n${url}`);
        }
    };
    const [invite, setInvite] = useState<Invite | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const myNorm = normalizePhone(invite?.phone || '');
    const [loading, setLoading] = useState(true);
    const [paid, setPaid] = useState(false);
    const [optimisticPaid, setOptimisticPaid] = useState(false);
    const [payerHeroStage, setPayerHeroStage] = useState<'open' | 'counting_down' | 'paid'>('open');
    const [countdownDisplayAmount, setCountdownDisplayAmount] = useState<number | null>(null);
    const [lastPaidAmount, setLastPaidAmount] = useState<number>(0);
    const [paying, setPaying] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [payerPrivateSubscriptionEpoch, setPayerPrivateSubscriptionEpoch] = useState(0);
    const payerLogoutInProgressRef = useRef(false);
    const dataLoadedRef = useRef(false);
    const payerPrivateSubscriptionsRef = useRef<Set<() => void>>(new Set());
    const stopPayerPrivateSubscriptions = useCallback(() => {
        const subscriptions = Array.from(payerPrivateSubscriptionsRef.current);
        payerPrivateSubscriptionsRef.current.clear();
        subscriptions.forEach((unsubscribe: any) => {
            try {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            } catch {
                // Listener is already stopped. Logout must continue.
            }
        });
    }, []);
    const onPayerPrivateSubscriptionError = useCallback((context: string, error: any) => {
        const code = String(error?.code || '').trim().toLowerCase();
        if (code === 'permission-denied' && (payerLogoutInProgressRef.current || !auth.currentUser)) {
            return;
        }
        if (isLocalhost) {
            console.warn(`[PayerPage] ${context} subscription failed`, error);
        }
    }, [isLocalhost]);
    const [isSelectingItems, setIsSelectingItems] = useState(false);
    const hasAutoNavigatedReviewRef = useRef(false);

    const {
        enabled: webPaymentsEnabled,
        resolved: webPaymentsResolved,
    } = useWebPaymentsFeatureFlag();
    const cleroPlusEnabled = useCleroPlusEnabled();

    // UI States
    const [viewingReceipt, setViewingReceipt] = useState(false);
    const [processingItemId, setProcessingItemId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isParticipantsSheetOpen, setIsParticipantsSheetOpen] = useState(false);
    const [claimConflictModal, setClaimConflictModal] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    });
    const [claimDiscardModalOpen, setClaimDiscardModalOpen] = useState(false);
    const [isCommittingClaimDraft, setIsCommittingClaimDraft] = useState(false);
    const pendingSuccessHeroAnimationRef = useRef<{
        previousRemainingAmount: number;
        nextRemainingAmount: number;
        markFullyPaid: boolean;
    } | null>(null);
    const [payerOtpPhone, setPayerOtpPhone] = useState('');
    const [payerOtpCode, setPayerOtpCode] = useState('');
    const [payerOtpConfirmation, setPayerOtpConfirmation] = useState<ConfirmationResult | null>(null);
    const [payerOtpBusy, setPayerOtpBusy] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(30);
    const [merchantLogoFailed, setMerchantLogoFailed] = useState(false);
    const [canResend, setCanResend] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [modalMessage, setModalMessage] = useState<{ title: string; body: string; icon: string }>({
        title: '',
        body: '',
        icon: '',
    });
    const [previewSplitTitle, setPreviewSplitTitle] = useState<string>('');
    const [previewOrganizerName, setPreviewOrganizerName] = useState<string>('');
    const [previewOrganizerPhotoUrl, setPreviewOrganizerPhotoUrl] = useState<string | null>(null);
    const [previewPeopleCount, setPreviewPeopleCount] = useState<number | null>(null);
    const [previewTotalAmount, setPreviewTotalAmount] = useState<number | null>(null);
    const [previewReceiptThumbnailURL, setPreviewReceiptThumbnailURL] = useState<string | null>(null);
    const [previewReceiptImageURL, setPreviewReceiptImageURL] = useState<string | null>(null);
    const [previewMerchantLogoUrl, setPreviewMerchantLogoUrl] = useState<string | null>(null);
    const [previewSessionIcon, setPreviewSessionIcon] = useState<string | null>(null);
    const [previewSessionIconColor, setPreviewSessionIconColor] = useState<string | null>(null);
    const [previewNote, setPreviewNote] = useState<string | null>(null);
    const [previewCreatedAt, setPreviewCreatedAt] = useState<any>(null);
    const [previewLoading, setPreviewLoading] = useState<boolean>(true);
    const [previewNotFound, setPreviewNotFound] = useState<boolean>(false);
    const [authStep, setAuthStep] = useState<'preview' | 'phone' | 'otp'>('preview');
    const [isOrganizerRole, setIsOrganizerRole] = useState(false);
    const [isOtpFocused, setIsOtpFocused] = useState(false);
    const otpInputRef = useRef<HTMLInputElement | null>(null);
    const lastFailedOtpRef = useRef<string>('');

    // Bank States
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [authSessionUser, setAuthSessionUser] = useState<any>(() => auth.currentUser);
    const [payerProfile, setPayerProfile] = useState<any>(null);
    const [plaidAccounts, setPlaidAccounts] = useState<any[]>([]);
    const [selectedPaymentBankId, setSelectedPaymentBankId] = useState<string | null>(null);
    const [showPaymentBankDropdown, setShowPaymentBankDropdown] = useState(false);
    const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
    const [launchingPlaid, setLaunchingPlaid] = useState(false);
    const [showIdentityVerificationSheet, setShowIdentityVerificationSheet] = useState(false);
    const [showAstraAuthorizationSheet, setShowAstraAuthorizationSheet] = useState(false);
    const [identityDraft, setIdentityDraft] = useState<PayerIdentityDraft>(() => emptyPayerIdentityDraft());
    const [identityVerificationError, setIdentityVerificationError] = useState<string | null>(null);
    const [identityVerificationWaitMode, setIdentityVerificationWaitMode] = useState<IdentityVerificationWaitMode>('idle');
    const [identityVerificationStageIndex, setIdentityVerificationStageIndex] = useState(0);
    const [identityVerificationProgress, setIdentityVerificationProgress] = useState(8);
    const [refreshingIdentityStatus, setRefreshingIdentityStatus] = useState(false);
    const [identityBlockedStatusOverride, setIdentityBlockedStatusOverride] = useState<PaymentCapabilityStatus | null>(null);
    const [submittingIdentity, setSubmittingIdentity] = useState(false);
    const [authorizingAstra, setAuthorizingAstra] = useState(false);
    const [setupTransitionMessage, setSetupTransitionMessage] = useState<string | null>(null);
    const [showIdentityBlockedModal, setShowIdentityBlockedModal] = useState(false);
    const [insufficientFundsDetails, setInsufficientFundsDetails] = useState<InsufficientFundsErrorDetails | null>(null);
    const [limitExceededDetails, setLimitExceededDetails] = useState<LimitExceededErrorDetails | null>(null);
    const [showClaimablePaymentSheet, setShowClaimablePaymentSheet] = useState(false);
    const [claimablePaymentWindowDays, setClaimablePaymentWindowDays] = useState(20);
    const [showOrganizerRejectedSheet, setShowOrganizerRejectedSheet] = useState(false);
    const [organizerRejectedMessage, setOrganizerRejectedMessage] = useState('');
    const [showIneligibleBankSheet, setShowIneligibleBankSheet] = useState(false);
    const [paymentNotice, setPaymentNotice] = useState<PaymentNoticeState | null>(null);
    const [showPaymentInitiatedSuccessOverlay, setShowPaymentInitiatedSuccessOverlay] = useState(false);
    const [activityEntries, setActivityEntries] = useState<any[]>([]);

    // Participants Data
    const [participants, setParticipants] = useState<Record<string, { name: string; photoURL?: string; isOrganizer?: boolean }>>({});
    const [sessionInvites, setSessionInvites] = useState<Invite[]>([]);

    const getInvitePhone = (inv: any): string => {
        if (!inv) return '';
        const cand = String(inv.phone || inv.phoneSnapshot || inv.originalPhoneSnapshot || '').trim();
        if (cand) return cand;
        if (inv.id && (inv.id.startsWith('+') || /^\d+$/.test(inv.id))) return inv.id;
        return '';
    };

    const getInviteName = (inv: any): string => {
        if (!inv) return '';
        return String(inv.name || inv.profileName || inv.nameSnapshot || inv.displayName || '').trim();
    };

    const resolveParticipantPhoto = (phone: string, inv?: any) => {
        const p = normalizePhone(phone);
        if (!p) return undefined;
        const part = participants[p];
        const userPhoto = part?.photoURL || part?.photoUrl;
        if (userPhoto) return userPhoto;
        
        if (inv) {
            return inv.photoUrl || inv.avatarUrl || inv.profilePhotoUrl || inv.profileImageUrl || inv.image;
        }
        
        const foundInv = sessionInvites.find((i: any) => normalizePhone(getInvitePhone(i)) === p);
        if (foundInv) {
            return foundInv.photoUrl || foundInv.avatarUrl || foundInv.profilePhotoUrl || foundInv.profileImageUrl || foundInv.image;
        }
        return undefined;
    };

    const getPersistedParticipantName = (phoneOrKey: string): string => {
        if (!phoneOrKey) return '';
        const norm = normalizePhone(phoneOrKey);
        const rawDigits = phoneOrKey.replace(/[^\d]/g, '');

        if (norm && participants[norm]?.name && !participants[norm].name.startsWith('+') && !/^\d+$/.test(participants[norm].name)) {
            return participants[norm].name;
        }
        if (participants[phoneOrKey]?.name && !participants[phoneOrKey].name.startsWith('+') && !/^\d+$/.test(participants[phoneOrKey].name)) {
            return participants[phoneOrKey].name;
        }
        if (rawDigits) {
            const found = Object.keys(participants).find(k => k.replace(/[^\d]/g, '') === rawDigits);
            if (found && participants[found]?.name && !participants[found].name.startsWith('+') && !/^\d+$/.test(participants[found].name)) {
                return participants[found].name;
            }
        }

        const inv = sessionInvites.find((i: any) => {
            const iPhone = getInvitePhone(i);
            const iNorm = normalizePhone(iPhone);
            const iDigits = iPhone.replace(/[^\d]/g, '');
            return (norm && iNorm === norm) || (rawDigits && iDigits === rawDigits) || i.id === phoneOrKey;
        });
        const invName = getInviteName(inv);
        if (invName) return invName;

        if (phoneOrKey === 'organizer' || (norm && organizerPhoneNorm && norm === organizerPhoneNorm)) {
            if (session?.organizerName) return session.organizerName;
        }

        return '';
    };
    const [viewingItemParticipants, setViewingItemParticipants] = useState<string | null>(null); // itemId
    const [sliderDraftPercentByItem, setSliderDraftPercentByItem] = useState<Record<string, number>>({});
    const [optimisticSharesByItem, setOptimisticSharesByItem] = useState<Record<string, Record<string, any>>>({});
    const [draftRebalanceModeByItem, setDraftRebalanceModeByItem] = useState<Record<string, 'equalize' | undefined>>({});
    const [claimDraftSession, setClaimDraftSession] = useState<any | null>(null);
    const claimDraftWasOpenRef = useRef(false);
    const claimDraftSnapshotRef = useRef<{
        sessionVersion: number;
        itemVersionById: Record<string, number>;
        shareFractionByItemId: Record<string, number>;
        shareModeByItemId: Record<string, 'equal' | 'custom' | ''>;
    }>({
        sessionVersion: 0,
        itemVersionById: {},
        shareFractionByItemId: {},
        shareModeByItemId: {},
    });
    const [isBreakdownCollapsed, setIsBreakdownCollapsed] = useState(false);
    const [expandedBreakdownItems, setExpandedBreakdownItems] = useState<Record<string, boolean>>({});
    const [showOtherBreakdownItems, setShowOtherBreakdownItems] = useState(false);
    const [showDetailedSummaryBreakdown, setShowDetailedSummaryBreakdown] = useState(false);
    const [selectedPaymentRail, setSelectedPaymentRail] = useState<'bank_ach' | 'venmo'>(paymentsAreDisabled ? 'venmo' : 'bank_ach');
    const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
    const [isClosingPaymentModal, setIsClosingPaymentModal] = useState(false);

    const handleClosePaymentModal = useCallback(() => {
        setIsClosingPaymentModal(true);
        setShowPaymentBankDropdown(false);
        setTimeout(() => {
            setIsClosingPaymentModal(false);
            setShowConfirmPaymentModal(false);
        }, 220);
    }, []);

    useEffect(() => {
        if (!showConfirmPaymentModal) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClosePaymentModal();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showConfirmPaymentModal, handleClosePaymentModal]);
    const [partialPaymentEnabled, setPartialPaymentEnabled] = useState(false);
    const [partialPaymentInput, setPartialPaymentInput] = useState('');
    const [bankReconnectContext, setBankReconnectContext] = useState<{
        account: any;
        sourceAccountId: string;
        sourcePlaidItemId: string;
        sourceAccountType: string;
    } | null>(null);
    const [paymentLimitsSnapshot, setPaymentLimitsSnapshot] = useState<any | null>(null);
    const [purchaseSummaryReceiptBroken, setPurchaseSummaryReceiptBroken] = useState(false);
    const [founderPreviewSession, setFounderPreviewSession] = useState(() => readFounderPreviewSession());
    const hasLoggedFounderPageView = useRef(false);
    const paymentSubmissionLockRef = useRef(false);

    const isFounderPreview = founderPreviewSession?.mode === 'payer_view' && founderPreviewSession.inviteToken === token;
    const eligiblePaymentBanks = useMemo(
        () => plaidAccounts.filter(isEligiblePaymentPlaidAccount),
        [plaidAccounts]
    );
    const ineligiblePaymentBanks = useMemo(
        () => plaidAccounts.filter((account) => isPlaidAccountPaymentIneligible(account)),
        [plaidAccounts]
    );
    const selectedPaymentBank = useMemo(() => (
        eligiblePaymentBanks.find((account) => String(account?.id || '') === String(selectedPaymentBankId || ''))
        || getPreferredPaymentPlaidAccount(eligiblePaymentBanks, [
            payerProfile?.paymentDefaults?.lastUsedPayerPlaidAccountId,
        ])
    ), [eligiblePaymentBanks, payerProfile?.paymentDefaults?.lastUsedPayerPlaidAccountId, selectedPaymentBankId]);
    const hasLinkedPaymentMethod = Boolean(selectedPaymentBank);
    const hasOnlyIneligiblePaymentBanks = !hasLinkedPaymentMethod && eligiblePaymentBanks.length === 0 && ineligiblePaymentBanks.length > 0;
    const needsIdentityVerification = !isAstraIdentityVerified(payerProfile || {});
    const hasPaymentAuthorization = hasAstraPaymentAuthorization(payerProfile || {});
    const payerPaymentTarget = {
        sessionId: session?.id || null,
        inviteId: invite?.id || invite?.phone || null,
    };
    const payerPaymentCapabilityStatus = getPaymentCapabilityStatus(payerProfile || {}, payerPaymentTarget);
    const paymentsBlockedByIdentity = isIdentityReviewOrRestrictionBlockingPayments(payerProfile || {}, payerPaymentTarget);
    const isCurrentSplitRestrictedFailedPayment = isFailedAchRestrictionRetryTarget(
        payerProfile || {},
        payerPaymentTarget
    );
    const isSetupBusy = launchingPlaid || submittingIdentity || authorizingAstra;
    const isSetupBusyRef = useRef(isSetupBusy);
    isSetupBusyRef.current = isSetupBusy;
    const dismissConfirmPaymentModalImmediately = useCallback(() => {
        setShowConfirmPaymentModal(false);
        setShowPaymentBankDropdown(false);
    }, []);
    const presentPaymentNotice = useCallback((
        error: unknown,
        fallback: { title: string; body: string },
        extras?: { actionLabel?: string; onAction?: (() => void) | null }
    ) => {
        const mapped = mapPaymentErrorForUser(error, fallback);
        setPaymentNotice({
            ...mapped,
            actionLabel: extras?.actionLabel,
            onAction: extras?.onAction || null,
        });
        setErrorMessage(mapped.body);
        return mapped;
    }, []);
    const syncPaymentLimits = useCallback(async (uid: string, fallbackUserData?: any) => {
        const fallbackSnapshot = getPaymentLimitsSnapshot(fallbackUserData) || getPaymentLimitsSnapshot(payerProfile);
        if (fallbackSnapshot) {
            setPaymentLimitsSnapshot(fallbackSnapshot);
        }
        if (!uid) return fallbackSnapshot;
        try {
            const syncMyPaymentLimits = httpsCallable(functions, 'syncMyPaymentLimits');
            const response = await syncMyPaymentLimits({});
            const liveSnapshot = (response.data as any)?.paymentLimits || null;
            if (liveSnapshot) {
                setPaymentLimitsSnapshot(liveSnapshot);
                setPayerProfile((prev) => ({ ...(prev || {}), paymentLimits: liveSnapshot }));
                return liveSnapshot;
            }
        } catch (error) {
            payerConsole.warn('[PayerPage] syncMyPaymentLimits failed', error);
        }
        return fallbackSnapshot;
    }, [payerConsole, payerProfile]);

    useEffect(() => {
        const snapshot = getPaymentLimitsSnapshot(payerProfile);
        if (snapshot) {
            setPaymentLimitsSnapshot(snapshot);
        }
    }, [payerProfile]);

    const logFounderPreviewEvent = async (eventType: string, details: Record<string, unknown> = {}) => {
        if (!isFounderPreview || !founderPreviewSession?.auditSessionId || !founderAdminAuth.currentUser) return;
        try {
            const logFounderPayerPreviewEvent = httpsCallable<
                { sessionId: string; eventType: string; details?: Record<string, unknown> },
                LogFounderPayerPreviewEventResult
            >(founderAdminFunctions, 'logFounderPayerPreviewEvent');
            await logFounderPayerPreviewEvent({
                sessionId: founderPreviewSession.auditSessionId,
                eventType,
                details,
            });
        } catch (error) {
            payerConsole.warn('[PayerPage] Founder audit log failed', { eventType, error });
        }
    };

    const buildAstraReturnUrl = useCallback((step: 'identity' | 'payment_authorization') => {
        const params = new URLSearchParams();
        params.set('token', token);
        if (invite?.phone) params.set('phone', normalizePhone(invite.phone));
        params.set('step', step);
        if (selectedPaymentBankId) params.set('plaidAccountId', selectedPaymentBankId);
        return `${window.location.origin}${ASTRA_RETURN_PATH}?${params.toString()}`;
    }, [invite?.phone, selectedPaymentBankId, token]);

    const continueDocumentVerification = useCallback(async () => {
        if (!invite?.phone) return;
        try {
            setAuthorizingAstra(true);
            const result = await astraService.createDocumentVerificationLink({
                phone: invite.phone,
                redirectUri: buildAstraReturnUrl('identity'),
            });
            if (!result?.onboardingUrl) {
                throw new Error('Astra document verification link was not created.');
            }
            window.location.assign(result.onboardingUrl);
        } catch (error: any) {
            payerConsole.error('[PayerPage] Document verification continue failed', error);
            setErrorMessage(resolveSafePaymentErrorMessage(error, 'Unable to continue identity verification.'));
        } finally {
            setAuthorizingAstra(false);
        }
    }, [astraService, buildAstraReturnUrl, invite?.phone, payerConsole]);

    const persistSetupResume = useCallback((nextStep: 'identity' | 'payment_authorization' | 'confirm') => {
        if (!invite?.phone) return;
        writePayerSetupResumeState({
            token,
            phone: normalizePhone(invite.phone),
            nextStep,
            selectedPaymentBankId,
            updatedAt: Date.now(),
        });
    }, [invite?.phone, selectedPaymentBankId, token]);

    const routeToRequiredSetupStep = useCallback((
        next: {
            selectedAccount: any;
            needsIdentityVerification: boolean;
            hasPaymentAuthorization: boolean;
            userData?: any;
        } | null,
        preferredResumeStep?: 'identity' | 'payment_authorization' | 'confirm'
    ) => {
        setShowConfirmPaymentModal(false);
        setShowIdentityVerificationSheet(false);
        setShowAstraAuthorizationSheet(false);

        if (isIdentityReviewOrRestrictionBlockingPayments(next?.userData || {}, payerPaymentTarget)) {
            setShowIdentityBlockedModal(true);
            return;
        }

        if (!next?.selectedAccount) {
            setErrorMessage('Connect a bank to continue.');
            return;
        }

        if (preferredResumeStep === 'identity') {
            setShowIdentityVerificationSheet(true);
            return;
        }

        if (preferredResumeStep === 'payment_authorization') {
            if (next.needsIdentityVerification) {
                setShowIdentityVerificationSheet(true);
                return;
            }
            if (!next.hasPaymentAuthorization) {
                setShowAstraAuthorizationSheet(true);
                return;
            }
            setPartialPaymentEnabled(false);
            setPartialPaymentInput('');
            setShowConfirmPaymentModal(true);
            return;
        }

        if (next.needsIdentityVerification) {
            setShowIdentityVerificationSheet(true);
            return;
        }

        if (!next.hasPaymentAuthorization) {
            setShowAstraAuthorizationSheet(true);
            return;
        }

        setPartialPaymentEnabled(false);
        setPartialPaymentInput('');
        setShowConfirmPaymentModal(true);
    }, [payerPaymentTarget.inviteId, payerPaymentTarget.sessionId]);

    const refreshPayerSetupState = useCallback(async () => {
        if (!invite?.phone) return null;
        const resolveInviteUserUid = async () => {
            const boundUid = String((invite as any)?.participantUserUid || (invite as any)?.participantUid || '').trim();
            if (boundUid) return boundUid;
            const invitePhone = normalizePhone(String(invite.phone || (invite as any)?.phoneSnapshot || ''));
            if (!invitePhone) return null;
            const userDoc = await userService.resolveUserDocByPhone(invitePhone);
            return userDoc?.id || null;
        };
        const resolvedUid = await resolveInviteUserUid();
        const uid = auth.currentUser?.uid || resolvedUid;
        if (!uid) return null;
        const [userSnap, plaidAccountsSnap] = await Promise.all([
            getDoc(doc(firestore, 'users', uid)),
            getDocs(collection(firestore, 'users', uid, 'plaidAccounts')),
        ]);

        const userData = userSnap.exists() ? userSnap.data() : null;
        const accounts = plaidAccountsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }));
        const preferredAccount = getPreferredPaymentPlaidAccount(accounts, [
            selectedPaymentBankId,
            userData?.paymentDefaults?.lastUsedPayerPlaidAccountId,
        ]);

        setPayerProfile(userData);
        setPlaidAccounts(accounts);
        setSelectedPaymentBankId(preferredAccount?.id || null);
        const livePaymentLimits = await syncPaymentLimits(uid, userData);
        if (userData) {
            setIdentityDraft((prev) => {
                const seed = getIdentityDraftFromUser(userData);
                return {
                    ...seed,
                    ...prev,
                    firstName: prev.firstName || seed.firstName,
                    lastName: prev.lastName || seed.lastName,
                    email: prev.email || seed.email,
                };
            });
        }

        return {
            userData: userData ? { ...userData, ...(livePaymentLimits ? { paymentLimits: livePaymentLimits } : {}) } : userData,
            accounts,
            selectedAccount: preferredAccount,
            needsIdentityVerification: !isAstraIdentityVerified(userData || {}),
            hasPaymentAuthorization: hasAstraPaymentAuthorization(userData || {}),
        };
    }, [invite?.phone, selectedPaymentBankId, syncPaymentLimits]);

    const [isUninvitedToSplit, setIsUninvitedToSplit] = useState(false);
    const [isHeaderUserMenuOpen, setIsHeaderUserMenuOpen] = useState(false);
    const headerUserMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (headerUserMenuRef.current && !headerUserMenuRef.current.contains(e.target as Node)) {
                setIsHeaderUserMenuOpen(false);
            }
        };
        if (isHeaderUserMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isHeaderUserMenuOpen]);

    const refreshPayerInviteData = useCallback(async () => {
        if (!token || isSimulator) return null;
        const resolvePayerInvite = httpsCallable(functions, 'resolvePayerInvite');
        const result = await resolvePayerInvite({ capability: token, sessionId: token });
        const data = result.data as { invite?: Invite; session?: Session; participantSummaries?: Invite[]; role?: string; isUninvited?: boolean };
        if (data?.role === 'uninvited' || data?.isUninvited) {
            setIsUninvitedToSplit(true);
            return null;
        }
        if (!data.invite || !data.session) return null;
        setInvite(data.invite);
        setSession(data.session);
        setSessionInvites(Array.isArray(data.participantSummaries) ? data.participantSummaries : []);
        return data;
    }, [isSimulator, token]);

    const refreshPayerInviteUntilSettled = useCallback(async () => {
        for (let attempt = 0; attempt < PAYMENT_STATUS_REFRESH_MAX_ATTEMPTS; attempt += 1) {
            try {
                const data = await refreshPayerInviteData();
                if (!data?.invite || !data?.session) return;
                const progress = resolveInviteProgress(data.invite, data.session);
                const status = String(data.invite.status || '').trim().toLowerCase();
                if (['failed', 'declined'].includes(status)) setOptimisticPaid(false);
                if (progress.isFullyPaid || ['paid', 'verified', 'settled_offline', 'failed', 'declined'].includes(status)) {
                    return;
                }
            } catch (error) {
                payerConsole.warn('[PayerPage] Payment status refresh failed', error);
            }
            await waitForPaymentStatusRefresh(PAYMENT_STATUS_REFRESH_INTERVAL_MS);
        }
    }, [refreshPayerInviteData]);

    const runPaymentSuccessHeroAnimation = useCallback(async ({
        previousRemainingAmount,
        nextRemainingAmount,
        markFullyPaid,
    }: {
        previousRemainingAmount: number;
        nextRemainingAmount: number;
        markFullyPaid: boolean;
    }) => {
        setLastPaidAmount(previousRemainingAmount);
        if (markFullyPaid) {
            setPayerHeroStage('counting_down');
            setCountdownDisplayAmount(previousRemainingAmount);

            const startTime = performance.now();
            const duration = 1150; // ms matching mobile app
            await new Promise<void>((resolve) => {
                const step = (currentTime: number) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(1, elapsed / duration);
                    // Ease out cubic: 1 - (1 - progress)^3
                    const eased = 1 - Math.pow(1 - progress, 3);
                    const current = Math.max(0, previousRemainingAmount * (1 - eased));
                    setCountdownDisplayAmount(current);
                    if (progress < 1) {
                        requestAnimationFrame(step);
                    } else {
                        setCountdownDisplayAmount(0);
                        resolve();
                    }
                };
                requestAnimationFrame(step);
            });

            setPaid(true);
            setOptimisticPaid(true);
            setPayerHeroStage('paid');
            setCountdownDisplayAmount(null);
        }
    }, []);

    const resumePaymentSetupIfNeeded = useCallback(async () => {
        if (!invite?.phone) return;
        const resumeState = readPayerSetupResumeState(token);
        if (!resumeState) return;
        if (normalizePhone(resumeState.phone) !== normalizePhone(invite.phone)) {
            clearPayerSetupResumeState(token);
            return;
        }

        if (resumeState.selectedPaymentBankId) {
            setSelectedPaymentBankId(resumeState.selectedPaymentBankId);
        }

        try {
            setSetupTransitionMessage('Resuming bank pay setup...');
            const next = await refreshPayerSetupState();
            clearPayerSetupResumeState(token);
            routeToRequiredSetupStep(next, resumeState.nextStep);
        } finally {
            setSetupTransitionMessage(null);
        }
    }, [invite?.phone, refreshPayerSetupState, routeToRequiredSetupStep, token]);

    const hasCheckedAutoOpen = useRef(false);
    const inviteStatus = String((invite as any)?.status || '');
    const sessionStatus = String((session as any)?.status || '');
    const isDeclined = inviteStatus === 'declined';

    const exitFounderPreview = async () => {
        if (founderPreviewSession?.auditSessionId && founderAdminAuth.currentUser) {
            try {
                const endFounderPayerPreviewSession = httpsCallable<
                    { sessionId: string },
                    EndFounderPayerPreviewSessionResult
                >(founderAdminFunctions, 'endFounderPayerPreviewSession');
                await endFounderPayerPreviewSession({ sessionId: founderPreviewSession.auditSessionId });
            } catch (error) {
                payerConsole.warn('[PayerPage] Unable to end founder payer session audit', error);
            }
        }
        clearFounderPreviewSession();
        setFounderPreviewSession(null);
        setShowConfirmPaymentModal(false);
        setIsSelectingItems(false);
        const nextPath = founderPreviewSession?.returnPath || '/admin';
        window.history.pushState({}, '', nextPath);
        window.dispatchEvent(new Event('popstate'));
    };

    useEffect(() => {
        if (invite?.phone) {
            setSentryRuntimePhone(invite.phone);
            return;
        }

        if (auth.currentUser?.phoneNumber) {
            setSentryRuntimePhone(auth.currentUser.phoneNumber);
            return;
        }

        clearSentryRuntimePhone();
    }, [invite?.phone]);

    // Fetch public preview metadata (split title, organizer name, participant count, total amount)
    useEffect(() => {
        if (!token || isSimulator) {
            setPreviewLoading(false);
            return;
        }
        let isSubscribed = true;
        setPreviewLoading(true);
        const fetchPreview = async () => {
            try {
                const getPreview = httpsCallable<
                    { capability?: string; sessionId?: string },
                    {
                        splitTitle: string;
                        organizerName: string;
                        organizerPhotoUrl?: string | null;
                        peopleCount: number;
                        totalAmount?: number | null;
                        receiptThumbnailURL?: string | null;
                        receiptImageURL?: string | null;
                        merchantLogoUrl?: string | null;
                        sessionIcon?: string | null;
                        sessionIconColor?: string | null;
                        note?: string | null;
                        createdAt?: any;
                    }
                >(functions, 'getPayerInvitePreview');
                const isCapability = token.length >= 40;
                const res = await getPreview(isCapability ? { capability: token } : { sessionId: token, capability: token });
                if (isSubscribed && res?.data) {
                    if (res.data.splitTitle) setPreviewSplitTitle(res.data.splitTitle);
                    if (res.data.organizerName) setPreviewOrganizerName(res.data.organizerName);
                    if (res.data.organizerPhotoUrl) setPreviewOrganizerPhotoUrl(res.data.organizerPhotoUrl);
                    if (typeof res.data.peopleCount === 'number') setPreviewPeopleCount(res.data.peopleCount);
                    if (typeof res.data.totalAmount === 'number') setPreviewTotalAmount(res.data.totalAmount);
                    if (res.data.receiptThumbnailURL) setPreviewReceiptThumbnailURL(res.data.receiptThumbnailURL);
                    if (res.data.receiptImageURL) setPreviewReceiptImageURL(res.data.receiptImageURL);
                    if (res.data.merchantLogoUrl) setPreviewMerchantLogoUrl(res.data.merchantLogoUrl);
                    if (res.data.sessionIcon) setPreviewSessionIcon(res.data.sessionIcon);
                    if (res.data.sessionIconColor) setPreviewSessionIconColor(res.data.sessionIconColor);
                    if (res.data.note) setPreviewNote(res.data.note);
                    if (res.data.createdAt) setPreviewCreatedAt(res.data.createdAt);
                } else if (isSubscribed) {
                    setPreviewNotFound(true);
                }
            } catch (err) {
                payerConsole.warn('[PayerPage] invite preview load failed', err);
                if (isSubscribed) {
                    setPreviewNotFound(true);
                }
            } finally {
                if (isSubscribed) setPreviewLoading(false);
            }
        };
        void fetchPreview();
        return () => { isSubscribed = false; };
    }, [token, isSimulator]);

    // Auto-open Item Selection Logic
    useEffect(() => {
        if (!loading && session && invite && !hasCheckedAutoOpen.current) {
            const hasClaimableItems = Array.isArray(session.items) && session.items.length > 0;
            if (!hasClaimableItems) return;

            hasCheckedAutoOpen.current = true;
            const inviteProgress = resolveInviteProgress(invite, session);
            const effectivelyPaid = inviteProgress.isFullyPaid || ['paid', 'verified', 'settled_offline'].includes(String(invite.status || ''));
            const hasPartialPayment = inviteProgress.paidAmount > 0.0001 || inviteProgress.processingAmount > 0.0001;
            const myClaims = invite.itemClaims || {};
            const hasClaimed = Object.keys(myClaims).length > 0;

            const shouldAutoOpenClaimItems =
                !hasPartialPayment
                && (resolveInviteTotalAmount(invite) === 0 || !hasClaimed)
                && !effectivelyPaid
                && String(invite.status || '') !== 'declined';

            // Claim-items requests should open claim sheet directly, matching app flow.
            if (shouldAutoOpenClaimItems) {
                setIsSelectingItems(true);
            }
        }
    }, [loading, session, invite]);

    useEffect(() => {
        if (!invite || !session) return;
        const inviteProgress = resolveInviteProgress(invite, session);
        const inviteStatus = String(invite.status || '').trim().toLowerCase();
        const effectivelyPaid = !inviteProgress.hasFailedOutstanding
            && (inviteProgress.isFullyPaid || ['paid', 'verified', 'settled_offline'].includes(inviteStatus));
        setPaid((effectivelyPaid || optimisticPaid)
            && !inviteProgress.hasFailedOutstanding
            && !isCurrentSplitRestrictedFailedPayment);
    }, [invite, session, isCurrentSplitRestrictedFailedPayment, optimisticPaid]);

    useEffect(() => {
        if (!isCurrentSplitRestrictedFailedPayment) return;
        setOptimisticPaid(false);
        setPaid(false);
        void refreshPayerInviteData();
    }, [isCurrentSplitRestrictedFailedPayment, refreshPayerInviteData]);

    useEffect(() => {
        const refreshWhenVisible = () => {
            if (document.visibilityState !== 'visible' || !invite?.id) return;
            void refreshPayerInviteData();
        };
        document.addEventListener('visibilitychange', refreshWhenVisible);
        window.addEventListener('focus', refreshWhenVisible);
        return () => {
            document.removeEventListener('visibilitychange', refreshWhenVisible);
            window.removeEventListener('focus', refreshWhenVisible);
        };
    }, [invite?.id, refreshPayerInviteData]);

    useEffect(() => {
        if (!isFounderPreview || !session || !invite || hasLoggedFounderPageView.current) return;
        hasLoggedFounderPageView.current = true;
        void logFounderPreviewEvent('page_viewed', {
            inviteToken: token,
            sessionId: session.id || founderPreviewSession?.sessionId || null,
            invitePhone: invite.phone || null,
            inviteStatus: invite.status || null,
        });
    }, [isFounderPreview, session, invite, founderPreviewSession?.sessionId, token]);

    useEffect(() => {
        if (isDeclined) {
            setIsSelectingItems(false);
        }
    }, [isDeclined]);
    // Initial Fetch with Auth Guard
    useEffect(() => {
        const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' && !import.meta.env.PROD;

        let unsubInvite: (() => void) | null = null;
        let unsubSession: (() => void) | null = null;
        let authUnsub: (() => void) | null = null;

        if (isSimulator) {
            setLoading(true);
            const mockSession = {
                id: 'demo-session',
                amount: 120.00,
                title: 'Team Lunch',
                organizerName: 'Alex (Demo)',
                site: 'bistro.com',
                status: 'invites_generated',
                tax: 10.00,
                tip: 20.00,
                peopleCount: 4,
                items: [
                    { id: '1', name: 'Burger', amount: 15.00, shares: {} },
                    { id: '2', name: 'Fries', amount: 5.00, shares: {} },
                    { id: '3', name: 'Soda', amount: 3.00, shares: {} },
                    { id: '4', name: 'Steak', amount: 35.00, shares: {} },
                ]
            } as Session;

            const mockInvite = {
                id: 'demo-invite',
                token: 'demo-token',
                name: 'Friend',
                amount: 0,
                status: 'invited',
                phone: '555-0000',
                sessionId: 'demo-session',
                itemClaims: {}
            } as Invite;

            setInvite(mockInvite);
            setInvite(mockInvite);
            setSession(mockSession);
            setIsSelectingItems(mockInvite.amount === 0);
            setPayerProfile({
                name: 'Friend',
                email: 'friend@example.com',
                paymentDefaults: {
                    lastUsedPayerPlaidAccountId: 'demo-bank',
                },
                astra: {
                    userStatus: 'approved',
                    kycType: 'verified',
                    auth: { accessToken: 'demo' },
                },
            });
            setPlaidAccounts([{
                id: 'demo-bank',
                bankName: 'Chase Bank',
                bankLast4: '4242',
                accountType: 'depository',
                subtype: 'checking',
            }]);
            setSelectedPaymentBankId('demo-bank');
            setLoading(false);
            return;
        }

        // 1. Check if we already have a user synchronously (fast path)
        const currentUser = auth.currentUser;
        if (currentUser) {
            payerConsole.log("👤 [PayerPage] User already ready:", currentUser.phoneNumber);
            void startSubscriptions();
        }

        // 2. Also listen for updates (slow path / refresh)
        authUnsub = auth.onAuthStateChanged((user) => {
            setAuthSessionUser(user);
            if (user) {
                payerConsole.log("👤 [PayerPage] Auth restored:", user.phoneNumber);
                setPayerOtpConfirmation(null);
                setPayerOtpCode('');
                // Only start if not already started
                if (!unsubInvite) {
                    void startSubscriptions();
                }
            } else {
                payerConsole.log("👤 [PayerPage] No user authenticated.");
                unsubInvite = null;
                setLoading(false);
            }
        });

        // Helper to start subs
        async function startSubscriptions() {
            // Callable replaces raw Firestore listeners; retain sentinel to avoid duplicate auth callbacks.
            unsubInvite = () => {};
            setLoading(true);
            try {
                const resolvePayerInvite = httpsCallable(functions, 'resolvePayerInvite');
                const result = await resolvePayerInvite({ capability: token, sessionId: token });
                const data = result.data as { invite?: Invite; session?: Session; participantSummaries?: Invite[]; role?: string; isUninvited?: boolean };
                if (data?.role === 'uninvited' || data?.isUninvited) {
                    setIsUninvitedToSplit(true);
                    setLoading(false);
                    return;
                }
                if (data?.role === 'organizer' && data?.session) {
                    setIsOrganizerRole(true);
                    setIsUninvitedToSplit(false);
                    setSession(data.session);
                    setSessionInvites(Array.isArray(data.participantSummaries) ? data.participantSummaries : []);
                    setErrorMessage(null);
                    setPayerOtpConfirmation(null);
                    setPayerOtpCode('');
                    setOptimisticSharesByItem({});
                    dataLoadedRef.current = true;
                    setLoading(false);
                    return;
                }
                const inv = data.invite;
                const sess = data.session;
                if (!inv || !sess) throw new Error('Invite not found.');
                setIsUninvitedToSplit(false);
                setInvite(inv);
                setSessionInvites(Array.isArray(data.participantSummaries) ? data.participantSummaries : []);
                const inviteProgress = resolveInviteProgress(inv, null);
                const inviteStatus = String(inv.status || '').trim().toLowerCase();
                const pendingClaimResolution = resolveInviteTotalAmount(inv) <= 0.0001
                    && !['paid', 'verified', 'settled_offline'].includes(inviteStatus);
                const effectivelyPaid = !pendingClaimResolution && (
                    inviteProgress.isFullyPaid
                    || ['paid', 'verified', 'settled_offline'].includes(inviteStatus)
                );
                setPaid(effectivelyPaid);

                if (inv.status === 'failed' || inviteProgress.hasFailedOutstanding || String((inv as any)?.paymentCollectionStatus || '').trim().toLowerCase() === 'failed') {
                    setErrorMessage(inv.lastError || "Payment failed. Please try again.");
                } else if (!effectivelyPaid) {
                    setErrorMessage(null);
                }

                setSession(sess);
                setPayerOtpConfirmation(null);
                setPayerOtpCode('');
                setOptimisticSharesByItem({});
                dataLoadedRef.current = true;
                setLoading(false);
            } catch (error: any) {
                dataLoadedRef.current = true;
                payerConsole.warn('[PayerPage] secure invite resolve failed', error);
                const msg = resolveErrorMessage(error);
                if (msg.includes('Sign in with invited phone number')) {
                    setModalMessage({
                        title: 'Different Phone Number',
                        body: 'You signed in with a phone number that does not match this invite. Please sign in with the number where you received this link.',
                        icon: 'fa-user-slash',
                    });
                    setShowErrorModal(true);
                } else if (msg.includes('Invite link is no longer valid') || msg.includes('Invite not found')) {
                    setModalMessage({
                        title: 'Link Expired or Invalid',
                        body: 'This invite link has expired or is no longer valid. Please ask the organizer for a new link.',
                        icon: 'fa-unlink',
                    });
                    setShowErrorModal(true);
                } else if (msg.includes('already been used')) {
                    setModalMessage({
                        title: 'Link Already Used',
                        body: 'This invite link has already been claimed by another account.',
                        icon: 'fa-lock',
                    });
                    setShowErrorModal(true);
                }
                if (auth.currentUser) {
                    setErrorMessage('Unable to load bill details. Please try again.');
                } else {
                    setErrorMessage(null);
                }
                setLoading(false);
            }
        }

        // Safety Timeout logic
        const timeoutId = setTimeout(() => {
            // Only timeout if we are STILL loading and NO user is present
            // If user is present but Firestore is slow, 10s is reasonable to error out
            if (!dataLoadedRef.current && !auth.currentUser && !isFounderPreview) {
                setLoading(false);
                setErrorMessage(null);
                // NEW: If we've been sitting on this page for 10s without auth, 
                // something is wrong with the session.
                localStorage.removeItem('clero_last_session');
            } else if (!dataLoadedRef.current && auth.currentUser) {
                // If we HAVE a user but it's just slow, wait a bit longer or show a different message
                payerConsole.warn("[PayerPage] Still loading data after 10s, even though user is auth'd.");
            }
        }, 12000);

        return () => {
            clearTimeout(timeoutId);
            if (unsubInvite) unsubInvite();
            if (unsubSession) unsubSession();
            if (authUnsub) authUnsub();
        };
    }, [token, isFounderPreview, isSimulator]);

    useEffect(() => {
        if (isSimulator) return;

        const currentAuthUser = authSessionUser || auth.currentUser;
        const uid = String(currentAuthUser?.uid || '').trim();
        if (!uid) return;

        const phone = normalizePhone(currentAuthUser?.phoneNumber || invite?.phone || '');
        let unsubUser: (() => void) | null = null;
        let unsubPlaidAccounts: (() => void) | null = null;
        let unsubActivity: (() => void) | null = null;
        let cancelled = false;

        const start = async () => {
            const boundUid = String((invite as any)?.participantUserUid || (invite as any)?.participantUid || '').trim();
            const currentAuthUid = String(auth.currentUser?.uid || '').trim();
            payerConsole.log("[PayerPage] onSnapshot sub start. details:", {
                phone,
                boundUid,
                uid,
                currentAuthUid
            });
            if (!uid || cancelled || payerLogoutInProgressRef.current) return;

            const registerSubscription = (unsubscribe: () => void) => {
                if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) {
                    unsubscribe();
                    return false;
                }
                payerPrivateSubscriptionsRef.current.add(unsubscribe);
                return true;
            };

            const userRef = doc(firestore, 'users', uid);
            const plaidAccountsRef = collection(firestore, 'users', uid, 'plaidAccounts');

            unsubUser = onSnapshot(
                userRef,
                (snapshot) => {
                    if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) return;
                    const data = snapshot.exists() ? snapshot.data() : null;
                    setPayerProfile(data);
                    if (data) {
                        setIdentityDraft((prev) => {
                            const seed = getIdentityDraftFromUser(data);
                            return {
                                ...seed,
                                ...prev,
                                firstName: prev.firstName || seed.firstName,
                                lastName: prev.lastName || seed.lastName,
                                email: prev.email || seed.email,
                            };
                        });
                    }
                },
                (error) => onPayerPrivateSubscriptionError('user profile', error)
            );
            if (!registerSubscription(unsubUser)) unsubUser = null;

            if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) return;
            unsubPlaidAccounts = onSnapshot(
                plaidAccountsRef,
                (snapshot) => {
                    if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) return;
                    const accounts = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }));
                    setPlaidAccounts(accounts);
                },
                (error) => onPayerPrivateSubscriptionError('Plaid accounts', error)
            );
            if (!registerSubscription(unsubPlaidAccounts)) unsubPlaidAccounts = null;

            if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) return;
            const activityRef = collection(firestore, 'users', uid, 'activity');
            unsubActivity = onSnapshot(
                query(activityRef, limit(500)),
                (snapshot) => {
                    if (cancelled || payerLogoutInProgressRef.current || !auth.currentUser) return;
                    const validSessionIds = new Set(
                        [invite?.sessionId, (invite as any)?.sessionPath, session?.id, token]
                            .filter(Boolean)
                            .map((s) => String(s).trim())
                    );
                    const validInviteIds = new Set(
                        [invite?.id, token]
                            .filter(Boolean)
                            .map((s) => String(s).trim())
                    );
                    const rows = snapshot.docs
                        .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }))
                        .filter((data) => {
                            const type = String(data?.type || '').trim().toLowerCase();
                            if (type !== 'payment_sent' && type !== 'payment_failed_sent') return false;
                            const rowSessionId = String(data?.sessionId || '').trim();
                            const rowInviteId = String(data?.inviteId || '').trim();
                            if (rowSessionId) {
                                return validSessionIds.has(rowSessionId);
                            }
                            return rowInviteId ? validInviteIds.has(rowInviteId) : false;
                        })
                        .sort((a, b) => {
                            const bTime = Number(b.timestamp?.seconds || 0) * 1000 || Number(b.timestampMs || 0) || (b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0);
                            const aTime = Number(a.timestamp?.seconds || 0) * 1000 || Number(a.timestampMs || 0) || (a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0);
                            return bTime - aTime;
                        });
                    setActivityEntries(rows);
                    if (String(rows[0]?.type || '').trim().toLowerCase() === 'payment_failed_sent') {
                        setOptimisticPaid(false);
                        setPaid(false);
                        void refreshPayerInviteData();
                    }
                },
                (error) => onPayerPrivateSubscriptionError('activity', error)
            );
            if (!registerSubscription(unsubActivity)) unsubActivity = null;
        };

        void start();

        return () => {
            cancelled = true;
            [unsubUser, unsubPlaidAccounts, unsubActivity].forEach((unsubscribe) => {
                if (!unsubscribe) return;
                payerPrivateSubscriptionsRef.current.delete(unsubscribe);
                unsubscribe();
            });
        };
    }, [authSessionUser?.uid, invite?.sessionId, invite?.sessionPath, isSimulator, onPayerPrivateSubscriptionError, payerPrivateSubscriptionEpoch, refreshPayerInviteData]);

    useEffect(() => {
        const preferred = getPreferredPaymentPlaidAccount(eligiblePaymentBanks, [
            selectedPaymentBankId,
            payerProfile?.paymentDefaults?.lastUsedPayerPlaidAccountId,
        ]);
        if (!preferred?.id && selectedPaymentBankId) {
            setSelectedPaymentBankId(null);
            return;
        }
        if (preferred?.id && preferred.id !== selectedPaymentBankId) {
            setSelectedPaymentBankId(preferred.id);
        }
    }, [eligiblePaymentBanks, payerProfile?.paymentDefaults?.lastUsedPayerPlaidAccountId, selectedPaymentBankId]);

    useEffect(() => {
        if (!invite?.phone || loading) return;
        if (!window.location.search.includes('setup_resumed=1')) return;
        void resumePaymentSetupIfNeeded();
    }, [invite?.phone, loading, resumePaymentSetupIfNeeded]);

    // Fetch Participants (Invites + Organizer + Profiles) for Bubbles & Cards
    useEffect(() => {
        if (!session) return;

        const cleanPhone = (p: string) => p.replace(/[^\d+]/g, '');

        // 1. Fetch Organizer Details
        if (session.organizerPhone) {
            const orgPhone = cleanPhone(session.organizerPhone);
            userService.getUser(orgPhone).then((u) => {
                setParticipants((prev) => ({
                    ...prev,
                    [orgPhone]: {
                        name: resolveParticipantDisplayName({
                            phone: orgPhone,
                            viewerPhone: invite?.phone,
                            localName: getLocalParticipantName(orgPhone),
                            canonicalName: u?.name,
                            persistedName: session.organizerName || prev[orgPhone]?.name,
                            hasCleroAccount: Boolean(u),
                            fallbackLabel: 'Organizer',
                        }),
                        photoURL: u?.photoURL || u?.photoUrl,
                        isOrganizer: true
                    }
                }));
            }).catch(() => {
                setParticipants((prev) => ({
                    ...prev,
                    [orgPhone]: {
                        name: resolveParticipantDisplayName({
                            phone: orgPhone,
                            viewerPhone: invite?.phone,
                            localName: getLocalParticipantName(orgPhone),
                            persistedName: session.organizerName || prev[orgPhone]?.name,
                            fallbackLabel: 'Organizer',
                        }),
                        photoURL: prev[orgPhone]?.photoURL,
                        isOrganizer: true
                    }
                }));
            });
        }

        // 2. Populate basic participant summaries from invites
        setParticipants(prev => {
            const next = { ...prev };
            sessionInvites.forEach((item: any) => {
                const phone = normalizePhone(getInvitePhone(item));
                if (!phone) return;
                next[phone] = {
                    name: resolveParticipantDisplayName({
                        phone,
                        viewerPhone: invite?.phone,
                        localName: getLocalParticipantName(phone),
                        persistedName: getInviteName(item),
                        fallbackLabel: getInviteName(item) || formatPhoneForDisplay(phone),
                    }),
                    photoURL: item.photoUrl || item.photoURL || undefined,
                };
            });
            return next;
        });

        // 3. Dynamic batch profile and server photo resolution across all phones
        const allPhones = Array.from(new Set([
            ...sessionInvites.map((item: any) => normalizePhone(getInvitePhone(item))),
            session?.organizerPhone ? normalizePhone(session.organizerPhone) : '',
            ...Object.keys(session?.coverageMap || {}).map((ph) => normalizePhone(ph)),
        ].filter(Boolean)));

        if (allPhones.length > 0) {
            void (async () => {
                try {
                    let rows: any[] = [];
                    if (auth.currentUser) {
                        try {
                            const callable = httpsCallable(functions, 'resolveParticipantProfiles');
                            const res: any = await callable({ phones: allPhones });
                            rows = Array.isArray(res?.data?.results) ? res.data.results : [];
                        } catch (e) {}
                    }
                    const profileMap = new Map<string, { name?: string; photoURL?: string; hasCleroAccount?: boolean; isVerified?: boolean }>();
                    await Promise.all(allPhones.map(async (ph) => {
                        const rowMatch = rows.find((r: any) => normalizePhone(r.phone) === ph);
                        let photoURL = rowMatch?.photoURL || rowMatch?.photoUrl || '';
                        let targetName = rowMatch?.name || '';
                        let targetUid = rowMatch?.uid || '';
                        if (targetUid && !photoURL) {
                            try {
                                const cardSnap = await getDoc(doc(firestore, 'profileCards', targetUid)).catch(() => null);
                                if (cardSnap?.exists()) {
                                    photoURL = cardSnap.data()?.photoURL || cardSnap.data()?.photoUrl || '';
                                }
                            } catch (e) {}
                        }
                        if (targetUid && !photoURL) {
                            try {
                                const profSnap = await getDoc(doc(firestore, 'profiles', targetUid)).catch(() => null);
                                if (profSnap?.exists()) {
                                    photoURL = profSnap.data()?.photoURL || profSnap.data()?.photoUrl || profSnap.data()?.avatarUrl || '';
                                }
                            } catch (e) {}
                        }
                        if (!photoURL && !targetName) {
                            try {
                                const u = await userService.getUser(ph, targetUid || null);
                                if (u) {
                                    photoURL = u.photoURL || '';
                                    targetName = u.name || '';
                                }
                            } catch (e) {}
                        }
                        profileMap.set(ph, {
                            name: targetName,
                            photoURL,
                            hasCleroAccount: Boolean(rowMatch?.hasCleroAccount),
                            isVerified: Boolean(rowMatch?.isIdentityVerified || rowMatch?.isVerified),
                        });
                    }));

                    setParticipants((prev) => {
                        const next = { ...prev };
                        profileMap.forEach((prof, ph) => {
                            const existing = next[ph] || {};
                            next[ph] = {
                                ...existing,
                                name: existing.name || prof.name || '',
                                photoURL: prof.photoURL || existing.photoURL || undefined,
                            };
                        });
                        return next;
                    });
                } catch (err) {
                    console.error('Error resolving participant profiles:', err);
                }
            })();
        }
    }, [sessionInvites, invite?.phone, session?.organizerPhone, session?.coverageMap]);

    useEffect(() => {
        if (paymentsAreDisabled) {
            setSelectedPaymentRail('venmo');
        } else if (selectedPaymentRail !== 'bank_ach') {
            setSelectedPaymentRail('bank_ach');
        }
    }, [paymentsAreDisabled, selectedPaymentRail]);

    useEffect(() => {
        if (paid) setShowConfirmPaymentModal(false);
    }, [paid]);


    // Optimistic Update Helper
    const clearSliderDraftForItem = (itemId: string) => {
        setSliderDraftPercentByItem((prev) => {
            if (!(itemId in prev)) return prev;
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    };

    const isSettledLikeStatus = (status?: string) => {
        return status === 'paid' || status === 'verified' || status === 'settled_offline';
    };

    const lockedSharePhones = useMemo(
        () => sessionInvites
            .filter((participantInvite) => isSettledLikeStatus(participantInvite?.status))
            .map((participantInvite) => normalizePhone(participantInvite.phone || ''))
            .filter(Boolean),
        [sessionInvites],
    );

    const captureClaimDraftSnapshot = useCallback((sourceSession: any) => {
        const itemVersionById: Record<string, number> = {};
        const shareFractionByItemId: Record<string, number> = {};
        const shareModeByItemId: Record<string, 'equal' | 'custom' | ''> = {};
        (sourceSession?.items || []).forEach((item: any) => {
            const itemId = String(item?.id || '');
            if (!itemId) return;
            const shares = materializeSessionItemShares({
                item,
                claimLogs: sourceSession?.claimLogs,
                lockedSharePhones,
            });
            const myShare = resolveSessionViewerShare({
                shares,
                isOrganizer: false,
                viewerPhone: invite?.phone || '',
                organizerPhone: sourceSession?.organizerPhone || '',
            });
            const nextFraction = roundClaimShareFractionLocal(Number(myShare?.fraction || 0));
            shareFractionByItemId[itemId] = nextFraction;
            shareModeByItemId[itemId] = nextFraction > 0
                ? (String(myShare?.mode || 'equal') === 'custom' ? 'custom' : 'equal')
                : '';
            itemVersionById[itemId] = Number.isFinite(Number(item?.version)) ? Number(item.version) : 0;
        });
        claimDraftSnapshotRef.current = {
            sessionVersion: Number.isFinite(Number((sourceSession as any)?.version)) ? Number((sourceSession as any).version) : 0,
            itemVersionById,
            shareFractionByItemId,
            shareModeByItemId,
        };
    }, [invite?.phone, lockedSharePhones]);

    const resetClaimDraftFromLatestSession = useCallback(() => {
        setClaimDraftSession(cloneClaimDraftSession(session));
        captureClaimDraftSnapshot(session);
        setSliderDraftPercentByItem({});
        setOptimisticSharesByItem({});
        setDraftRebalanceModeByItem({});
    }, [captureClaimDraftSnapshot, session]);

    const optimisticSharesRef = useRef(optimisticSharesByItem);
    optimisticSharesRef.current = optimisticSharesByItem;
    const sliderDraftPercentRef = useRef(sliderDraftPercentByItem);
    sliderDraftPercentRef.current = sliderDraftPercentByItem;

    const sessionVersion = Number((session as any)?.version || 0);

    useEffect(() => {
        if (!isSelectingItems) {
            claimDraftWasOpenRef.current = false;
            setClaimDraftSession(null);
            return;
        }
        const hasDirtyEdits = Object.keys(sliderDraftPercentRef.current).length > 0 || Object.keys(optimisticSharesRef.current).length > 0;
        if (!claimDraftWasOpenRef.current || !hasDirtyEdits) {
            claimDraftWasOpenRef.current = true;
            resetClaimDraftFromLatestSession();
        }
    }, [isSelectingItems, resetClaimDraftFromLatestSession, sessionVersion]);

    // Auto-direct to Review Items on first open for itemized splits
    useEffect(() => {
        if (hasAutoNavigatedReviewRef.current) return;
        if (!session) return;
        const items = Array.isArray(session?.items) ? session.items : [];
        const isItemClaimingEnabled = Boolean((session as any)?.isItemClaimingEnabled) && items.length > 0;
        const isSettled = Boolean(paid || payerHeroStage === 'paid' || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || Boolean(invite?.isCoveredPaid));
        const organizerPhoneNorm = normalizePhone(String(session.organizerPhone || ''));
        const currentUserPhoneNorm = normalizePhone(auth.currentUser?.phoneNumber || '');
        const myNorm = normalizePhone(String(invite?.phone || ''));
        const isOrg = Boolean(organizerPhoneNorm && (currentUserPhoneNorm === organizerPhoneNorm || myNorm === organizerPhoneNorm));
        if (isItemClaimingEnabled && !isSettled && !isOrg) {
            hasAutoNavigatedReviewRef.current = true;
            setIsSelectingItems(true);
        }
    }, [session, paid, payerHeroStage, inviteStatus, invite?.phone, invite?.isCoveredPaid]);

    // Ensure review items sheet closes if viewer settles / pays
    useEffect(() => {
        const isSettled = Boolean(paid || payerHeroStage === 'paid' || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || Boolean(invite?.isCoveredPaid));
        if (isSettled && isSelectingItems) {
            setIsSelectingItems(false);
        }
    }, [paid, payerHeroStage, inviteStatus, invite?.isCoveredPaid, isSelectingItems]);

    const activeClaimSession = isSelectingItems && claimDraftSession ? claimDraftSession : session;

    const getEffectiveItemShares = useCallback((item: any) => {
        const itemId = String(item?.id || '');
        if (optimisticSharesByItem[itemId]) return optimisticSharesByItem[itemId];
        return item?.shares || item?.baseShares || {};
    }, [optimisticSharesByItem]);

    const resolveMyShare = (item: any) => {
        if (!invite) return null;
        return resolveSessionViewerShare({
            shares: getEffectiveItemShares(item),
            isOrganizer: false,
            viewerPhone: invite.phone || '',
            organizerPhone: activeClaimSession?.organizerPhone || '',
        });
    };

    const getItemClaimContext = (item: any) => {
        const shares = getEffectiveItemShares(item);
        const editableState = computeViewerEditableShareState({
            shares,
            isOrganizer: false,
            viewerPhone: invite?.phone || '',
            organizerPhone: activeClaimSession?.organizerPhone || '',
            lockedSharePhones,
        });
        const myShare = resolveSessionViewerShare({
            shares,
            isOrganizer: false,
            viewerPhone: invite?.phone || '',
            organizerPhone: activeClaimSession?.organizerPhone || '',
        });
        const hasLockedPaidShares = Object.keys(shares || {}).some((sharePhone) => (
            sharePhone !== 'organizer'
            && lockedSharePhones.includes(normalizePhone(sharePhone))
        ));
        const hasMyShare = Number(myShare?.fraction || 0) > 0.0001;
        const canJoinUnlockedPool = editableState.unclaimedFraction > 0.0001 || editableState.hasEqualizableOthers;
        const canEdit = hasMyShare || canJoinUnlockedPool;
        const isFullyFixed = !canEdit;
        const shouldEqualJoin = !hasMyShare && editableState.totalAssigned >= 1 - 0.0001 && editableState.hasEqualizableOthers;

        return {
            shares,
            myShare,
            editableState,
            hasLockedPaidShares,
            hasMyShare,
            canEdit,
            isFullyFixed,
            shouldEqualJoin,
        };
    };

    const buildClaimDraftMutations = useCallback(() => {
        const snapshot = claimDraftSnapshotRef.current;
        const list: Array<{
            itemId: string;
            operation: 'claim' | 'unclaim' | 'set_fraction';
            requestedFraction?: number;
            rebalanceMode?: 'equalize';
            baseItemVersion?: number;
        }> = [];
        (activeClaimSession?.items || []).forEach((item: any) => {
            const itemId = String(item?.id || '');
            if (!itemId) return;
            const effectiveShares = optimisticSharesByItem[itemId]
                || materializeSessionItemShares({
                    item,
                    claimLogs: activeClaimSession?.claimLogs,
                    lockedSharePhones,
                });
            const draftShare = resolveSessionViewerShare({
                shares: effectiveShares,
                isOrganizer: false,
                viewerPhone: invite?.phone || '',
                organizerPhone: activeClaimSession?.organizerPhone || '',
            });
            const nextFraction = roundClaimShareFractionLocal(Number(draftShare?.fraction || 0));
            const nextMode: 'equal' | 'custom' | '' = nextFraction > 0
                ? (String(draftShare?.mode || 'equal') === 'custom' ? 'custom' : 'equal')
                : '';
            const previousFraction = roundClaimShareFractionLocal(Number(snapshot.shareFractionByItemId[itemId] || 0));
            const previousMode: 'equal' | 'custom' | '' = snapshot.shareModeByItemId[itemId] || '';
            const hasRebalanceIntent = Boolean(draftRebalanceModeByItem[itemId]);
            const hasModeOnlyChange = nextFraction > 0 && previousFraction > 0 && nextMode !== previousMode;
            if (Math.abs(nextFraction - previousFraction) <= 0.0001 && !hasRebalanceIntent && !hasModeOnlyChange) {
                return;
            }
            const baseItemVersion = Number(snapshot.itemVersionById[itemId] || 0);
            if (nextFraction <= 0.0001) {
                list.push({ itemId, operation: 'unclaim', baseItemVersion });
                return;
            }
            if (previousFraction <= 0.0001) {
                list.push({
                    itemId,
                    operation: 'claim',
                    requestedFraction: nextFraction,
                    baseItemVersion,
                    ...(draftRebalanceModeByItem[itemId] ? { rebalanceMode: 'equalize' as const } : {}),
                });
                return;
            }
            list.push({
                itemId,
                operation: 'set_fraction',
                requestedFraction: nextFraction,
                baseItemVersion,
                ...(draftRebalanceModeByItem[itemId] ? { rebalanceMode: 'equalize' as const } : {}),
            });
        });
        return list;
    }, [activeClaimSession?.claimLogs, activeClaimSession?.items, activeClaimSession?.organizerPhone, draftRebalanceModeByItem, invite?.phone, lockedSharePhones, optimisticSharesByItem]);

    const hasClaimDraftChanges = useMemo(() => buildClaimDraftMutations().length > 0, [buildClaimDraftMutations]);

    const performOptimisticUpdate = (
        itemId: string,
        targetFraction: number,
        rebalanceMode?: 'equalize',
    ) => {
        if (!activeClaimSession || !invite) return;

        const item = (activeClaimSession.items || []).find((candidate: any) => String(candidate?.id || '') === String(itemId || ''));
        if (!item) return;
        const currentShares = getEffectiveItemShares(item);

        let newMyFraction = 0;
        let newItemAmount = 0;

        const nextShares = targetFraction > 0
            ? replaceSessionViewerShareOptimistic({
                shares: currentShares,
                fraction: targetFraction,
                isOrganizer: false,
                viewerPhone: invite.phone || '',
                organizerPhone: activeClaimSession.organizerPhone || '',
                name: resolveParticipantDisplayName({
                    phone: normalizePhone(invite.phone || ''),
                    viewerPhone: invite.phone,
                    localName: getLocalParticipantName(normalizePhone(invite.phone || '')),
                    persistedName: invite.name,
                    fallbackLabel: invite.name || 'Payer',
                }),
                forceEqualJoin: rebalanceMode === 'equalize',
                lockedSharePhones,
            })
            : removeSessionViewerShareOptimistic({
                shares: currentShares,
                isOrganizer: false,
                viewerPhone: invite.phone || '',
                organizerPhone: activeClaimSession.organizerPhone || '',
            });

        setOptimisticSharesByItem((prev) => ({
            ...prev,
            [itemId]: nextShares,
        }));

        const nextMyShare = resolveSessionViewerShare({
            shares: nextShares,
            isOrganizer: false,
            viewerPhone: invite.phone || '',
            organizerPhone: activeClaimSession.organizerPhone || '',
        });
        newMyFraction = Number(nextMyShare?.fraction || 0);
        newItemAmount = Number(item.amount || 0) * newMyFraction;

        // 2. Update Invite Claims & Amount
        setInvite(prevInvite => {
            if (!prevInvite) return prevInvite;

            const nextClaims: Record<string, { fraction: number; amount: number }> = { ...(prevInvite.itemClaims || {}) };

            if (newMyFraction > 0) {
                nextClaims[itemId] = { fraction: newMyFraction, amount: newItemAmount };
            } else {
                delete nextClaims[itemId];
            }

            // Recalculate Total
            const subtotal: number = Object.values(nextClaims).reduce((acc: number, c) => acc + Number(c.amount || 0), 0);

            // Use currently visible session to get total amount base
            const allItemsOption = activeClaimSession.items || [];
            const totalItemAmount = allItemsOption.reduce((acc: number, it: any) => acc + Number(it.amount || 0), 0) || 1;

            // Avoid division by zero
            const myRatio = totalItemAmount > 0 ? subtotal / totalItemAmount : 0;

            const tipVal = Number((activeClaimSession as any).tip || (activeClaimSession as any).tipAmount || 0);
            const taxVal = Number((activeClaimSession as any).tax || 0);

            // FIX: Proportional Tip/Tax
            let totalForInvite = Number(subtotal || 0);
            totalForInvite += (taxVal * myRatio);
            totalForInvite += (tipVal * myRatio); // Changed from (tipVal / peopleCount)

            const payeeBaseAmount = Number(totalForInvite.toFixed(2));
            const currentFee = 0;

            return {
                ...prevInvite,
                itemClaims: nextClaims,
                amount: Number(totalForInvite.toFixed(2)),
                payeeBaseAmount,
                payeeFeeAmount: currentFee,
                payeeFeeVersion: paymentsAreDisabled ? 'outage_zero_fee' : 'v3_ach_0p8_cap2'
            };
        });
    };

    const resolveClaimErrorMessage = (error: any, fallback: string) => {
        const message = String(error?.message || '').trim();
        const limitMatch = message.match(/Max (?:available|editable share is)\s+(\d+(?:\.\d+)?)%/i);
        if (limitMatch?.[1]) {
            const pct = Number.parseFloat(limitMatch[1]);
            const fraction = pct / 100;
            const shareText = formatFractionAsShareString(fraction);
            return `Limit reached. You can only claim up to ${shareText} on this item right now.`;
        }
        return fallback;
    };

    const handleClaim = async (
        itemId: string,
        fraction: number,
        rebalanceMode?: 'equalize',
        options?: { deferCommit?: boolean },
    ) => {
        if (!session || !invite || isDeclined) return;
        const targetItem = (session.items || []).find((it: any) => String(it?.id || '') === String(itemId || ''));
        if (!targetItem) return;
        const claimContext = getItemClaimContext(targetItem);
        if (!claimContext.canEdit && !claimContext.hasMyShare) {
            setErrorMessage('No remaining share is available on this item.');
            return;
        }
        const deferCommit = options?.deferCommit === true;
        if (!deferCommit) setProcessingItemId(itemId);
        setErrorMessage(null);

        // Optimistic Update
        performOptimisticUpdate(itemId, fraction, rebalanceMode);
        setDraftRebalanceModeByItem((prev) => {
            const next = { ...prev };
            if (rebalanceMode === 'equalize') next[itemId] = 'equalize';
            else delete next[itemId];
            return next;
        });
        if (deferCommit) return;

        try {
            const result = await bankService.commitClaimDraft({
                sessionId: session.id,
                inviteId: invite.id,
                baseSessionVersion: Number((session as any)?.version || 0),
                itemMutations: [{
                    itemId,
                    operation: 'claim',
                    requestedFraction: fraction,
                    ...(rebalanceMode ? { rebalanceMode } : {}),
                    baseItemVersion: Number((targetItem as any)?.version || 0),
                }],
            });
            const conflict = (result?.conflicts || []).find((entry: any) => String(entry?.itemId || '') === String(itemId || ''));
            if (conflict) {
                setOptimisticSharesByItem((prev) => {
                    if (!(itemId in prev)) return prev;
                    const next = { ...prev };
                    delete next[itemId];
                    return next;
                });
                setClaimConflictModal({
                    open: true,
                    message: String(conflict.message || 'This item changed while you were editing.'),
                });
                return;
            }
            void logFounderPreviewEvent('item_claimed', {
                sessionId: session.id,
                invitePhone: invite.phone || null,
                itemId,
                fraction,
                ...(rebalanceMode ? { rebalanceMode } : {}),
            });
        } catch (err: any) {
            payerConsole.error('Claim failed:', err);
            setOptimisticSharesByItem((prev) => {
                if (!(itemId in prev)) return prev;
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            setErrorMessage(resolveClaimErrorMessage(err, 'Failed to claim item'));
            // Revert handled by next snapshot
        } finally {
            setProcessingItemId(null);
        }
    };

    const handleUnclaim = async (itemId: string, options?: { deferCommit?: boolean }) => {
        if (!session || !invite || isDeclined) return;
        const targetItem = (session.items || []).find((it: any) => String(it?.id || '') === String(itemId || ''));
        if (!targetItem) return;
        const deferCommit = options?.deferCommit === true;
        if (!deferCommit) setProcessingItemId(itemId);
        setErrorMessage(null);

        // Optimistic Update
        performOptimisticUpdate(itemId, 0);
        setDraftRebalanceModeByItem((prev) => {
            if (!(itemId in prev)) return prev;
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
        if (deferCommit) return;

        try {
            const result = await bankService.commitClaimDraft({
                sessionId: session.id,
                inviteId: invite.id,
                baseSessionVersion: Number((session as any)?.version || 0),
                itemMutations: [{
                    itemId,
                    operation: 'unclaim',
                    baseItemVersion: Number((targetItem as any)?.version || 0),
                }],
            });
            const conflict = (result?.conflicts || []).find((entry: any) => String(entry?.itemId || '') === String(itemId || ''));
            if (conflict) {
                setOptimisticSharesByItem((prev) => {
                    if (!(itemId in prev)) return prev;
                    const next = { ...prev };
                    delete next[itemId];
                    return next;
                });
                setClaimConflictModal({
                    open: true,
                    message: String(conflict.message || 'This item changed while you were editing.'),
                });
                return;
            }
            void logFounderPreviewEvent('item_unclaimed', {
                sessionId: session.id,
                invitePhone: invite.phone || null,
                itemId,
            });
        } catch (err: any) {
            payerConsole.error('Unclaim failed:', err);
            setOptimisticSharesByItem((prev) => {
                if (!(itemId in prev)) return prev;
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            setErrorMessage(err.message || 'Failed to unclaim item');
        } finally {
            setProcessingItemId(null);
        }
    };

    const closeClaimSheet = useCallback((force = false) => {
        if (isCommittingClaimDraft && !force) return;
        if (!force && hasClaimDraftChanges) {
            setClaimDiscardModalOpen(true);
            return;
        }
        setIsSelectingItems(false);
        setClaimDraftSession(null);
        setSliderDraftPercentByItem({});
        setOptimisticSharesByItem({});
        setDraftRebalanceModeByItem({});
        setClaimDiscardModalOpen(false);
        claimDraftWasOpenRef.current = false;
    }, [hasClaimDraftChanges, isCommittingClaimDraft]);

    const commitClaimDraftAndClose = useCallback(async () => {
        if (!session || !invite || isDeclined || isCommittingClaimDraft) return;
        const itemMutations = buildClaimDraftMutations();
        if (itemMutations.length === 0) {
            closeClaimSheet(true);
            return;
        }
        setIsCommittingClaimDraft(true);
        setErrorMessage(null);
        try {
            const result = await bankService.commitClaimDraft({
                sessionId: session.id,
                inviteId: invite.id,
                baseSessionVersion: claimDraftSnapshotRef.current.sessionVersion,
                itemMutations,
            });
            const conflicts = Array.isArray(result?.conflicts) ? result.conflicts : [];
            if (conflicts.length > 0) {
                const lines = conflicts.slice(0, 5).map((conflict: any) => {
                    const itemName = (session.items || []).find((item: any) => String(item?.id || '') === String(conflict?.itemId || ''))?.name || 'Item';
                    return `• ${itemName}: ${String(conflict?.message || conflict?.reason || 'Updated by others')}`;
                });
                const extra = conflicts.length > 5 ? `\n• +${conflicts.length - 5} more` : '';
                setClaimConflictModal({
                    open: true,
                    message: `${lines.join('\n')}${extra}`,
                });
                return;
            }
            closeClaimSheet(true);
        } catch (error: any) {
            payerConsole.error('Claim draft commit failed:', error);
            setErrorMessage(String(error?.message || 'Could not save claim changes.'));
        } finally {
            setIsCommittingClaimDraft(false);
        }
    }, [bankService, buildClaimDraftMutations, closeClaimSheet, invite, isCommittingClaimDraft, isDeclined, payerConsole, session, token]);

    const renderedSessionItems = useMemo(
        () => (activeClaimSession?.items || []).map((item: any) => ({
            ...item,
            effectiveShares: getEffectiveItemShares(item),
        })),
        [activeClaimSession?.items, getEffectiveItemShares],
    );

    const viewerHasClaimedItems = useMemo(() => {
        return renderedSessionItems.some((item: any) => {
            const myShare = resolveMyShare(item);
            return Number(myShare?.fraction || 0) > 0.0001;
        });
    }, [renderedSessionItems, resolveMyShare]);

    const fireSuccessEffects = (markFullyPaid: boolean) => {
        setPaid(markFullyPaid);
        setShowConfirmPaymentModal(false);
        setPartialPaymentEnabled(false);
        setPartialPaymentInput('');
        triggerSuccessHaptic();
        try {
            confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#0d9488', '#2dd4bf', '#ffffff'] });
        } catch (err) {
            payerConsole.warn('[PayerPage] Confetti failed', err);
        }
    };

    const closeProcessingPaymentModal = () => {
        setShowConfirmPaymentModal(false);
        setPartialPaymentEnabled(false);
        setPartialPaymentInput('');
        setPaid(false);
    };

    const recordOfflineSettlement = async () => {
        if (!invite || !session) return;
        const markSplitSettled = httpsCallable(functions, 'markSplitSettled');
        await markSplitSettled({
            sessionId: String(session.id || ''),
            inviteId: String((invite as any).id || (invite as any).inviteId || '').trim(),
        });
    };

    const applyLocalVenmoSettlement = () => {
        setInvite((prevInvite) => {
            if (!prevInvite) return prevInvite;
            return {
                ...prevInvite,
                status: 'settled_offline',
                appliedPaymentMethod: 'venmo',
                appliedPaymentRail: 'venmo',
                paymentMethod: 'venmo',
            } as unknown as Invite;
        });
    };

    const reopenVenmoPayment = async () => {
        if (!invite || !session) throw new Error('Missing split payment details.');

        const organizerRecipient = normalizePhone(String(session.organizerPhone || ''));
        const note = `Clero Split: ${String(session.title || 'Split payment').trim()}`;
        const handoff = openVenmoPayment({
            recipient: organizerRecipient,
            amount: remainingOwedAmount,
            note,
        });

        if (handoff.openedInNewTab) {
            return handoff;
        }

        return handoff;
    };

    const handleVenmoPayment = async () => {
        if (!invite || !session || paid || paying) return;

        setPaying(true);
        setErrorMessage(null);
        void logFounderPreviewEvent('payment_attempted', {
            sessionId: session.id,
            invitePhone: invite.phone || null,
            rail: 'venmo',
            amount: remainingOwedAmount,
        });

        try {
            const handoff = await reopenVenmoPayment();
            if (!handoff.openedInNewTab) {
                payerConsole.warn('[PayerPage] Venmo popup did not return a window handle. Waiting on settlement sync while staying on page.');
            }

            try {
                const prevRemaining = remainingOwedAmount;
                await recordOfflineSettlement();
                applyLocalVenmoSettlement();
                closeProcessingPaymentModal();
                triggerSuccessHaptic();
                pendingSuccessHeroAnimationRef.current = {
                    previousRemainingAmount: prevRemaining,
                    nextRemainingAmount: 0,
                    markFullyPaid: true,
                };
                setShowPaymentInitiatedSuccessOverlay(true);
                void logFounderPreviewEvent('payment_succeeded', {
                    sessionId: session.id,
                    invitePhone: invite.phone || null,
                    rail: 'venmo',
                    amount: remainingOwedAmount,
                });
        } catch (settlementErr: any) {
            payerConsole.error('Venmo settlement sync failed:', settlementErr);
            presentPaymentNotice(settlementErr, {
                title: 'Settlement not confirmed',
                body: 'Venmo opened, but we could not mark this payment as settled. Please try again.',
            });
        }
    } catch (handoffErr: any) {
        payerConsole.error('Venmo payment handoff failed:', handoffErr);
        presentPaymentNotice(handoffErr, {
            title: 'Unable to open Venmo',
            body: 'Unable to open Venmo. Please try again.',
        });
    } finally {
        setPaying(false);
    }
    };

    const validateOrganizerBeforePayment = async () => {
        if (!invite || !session) return;
        const preflightOrganizer = httpsCallable(functions, 'validateOrganizerBeforePayment');
        const preflightResult: any = await preflightOrganizer({
            sessionId: String(session.id || ''),
            inviteId: String((invite as any).id || (invite as any).inviteId || '').trim(),
        });
        const preflightData = preflightResult?.data || {};
        if (!preflightData?.allowed) {
            const error: any = new Error(String(preflightData?.message || 'This split is no longer available.'));
            error.preflightData = preflightData;
            throw error;
        }
        return preflightData;
    };

    const continueSetupAfterRefresh = useCallback(async () => {
        try {
            setSetupTransitionMessage('Preparing your bank pay setup...');
            const next = await refreshPayerSetupState();
            await validateOrganizerBeforePayment();
            routeToRequiredSetupStep(next);
        } catch (error: any) {
            if (error?.preflightData?.reason === 'organizer_permanently_rejected') {
                const rawOrganizer = String(error?.preflightData?.organizerName || session?.organizerName || 'The organizer').trim();
                const organizerName = formatOrganizerFirstName(rawOrganizer) || 'Organizer';
                setOrganizerRejectedMessage(`${organizerName} is not verified to receive payments on Clero.`);
                setShowOrganizerRejectedSheet(true);
            } else {
                const mapped = mapPaymentErrorForUser(error, {
                    title: 'Request unavailable',
                    body: error?.message || 'Unable to verify this split right now.',
                });
                presentPaymentNotice(error, {
                    title: mapped.title,
                    body: mapped.body,
                }, mapped.retryable ? {
                    actionLabel: 'Retry',
                    onAction: () => {
                        setPaymentNotice(null);
                        void continueSetupAfterRefresh();
                    },
                } : undefined);
            }
        } finally {
            setSetupTransitionMessage(null);
        }
    }, [presentPaymentNotice, refreshPayerSetupState, routeToRequiredSetupStep, session?.organizerName]);

    useEffect(() => {
        if (identityVerificationWaitMode !== 'active') return;
        setIdentityVerificationStageIndex(0);
        setIdentityVerificationProgress(8);
        const stageTimer = window.setInterval(() => {
            setIdentityVerificationStageIndex((current) => (current + 1) % IDENTITY_VERIFICATION_STAGES.length);
        }, 1350);
        const progressTimer = window.setInterval(() => {
            setIdentityVerificationProgress((current) => Math.min(88, current + (current < 36 ? 9 : current < 58 ? 6 : 3)));
        }, 900);
        const timeoutTimer = window.setTimeout(() => {
            setIdentityVerificationWaitMode('timed_out');
            setIdentityBlockedStatusOverride('pending');
            setShowIdentityBlockedModal(true);
        }, IDENTITY_DECISION_WAIT_TIMEOUT_MS);
        return () => {
            window.clearInterval(stageTimer);
            window.clearInterval(progressTimer);
            window.clearTimeout(timeoutTimer);
        };
    }, [identityVerificationWaitMode]);

    useEffect(() => {
        if (identityVerificationWaitMode !== 'active' && identityVerificationWaitMode !== 'timed_out') return;
        const outcome = getIdentityVerificationWaitOutcome(payerPaymentCapabilityStatus);
        if (outcome === 'verified') {
            setIdentityVerificationProgress(100);
            setIdentityVerificationWaitMode('idle');
            setShowIdentityBlockedModal(false);
            setIdentityBlockedStatusOverride(null);
            void continueSetupAfterRefresh();
            return;
        }
        if (outcome === 'document_required' || outcome === 'permanently_rejected') {
            setIdentityVerificationWaitMode('idle');
            setIdentityBlockedStatusOverride(outcome);
            setShowIdentityBlockedModal(true);
        }
    }, [continueSetupAfterRefresh, identityVerificationWaitMode, payerPaymentCapabilityStatus]);

    const refreshIdentityVerificationStatus = useCallback(async () => {
        if (refreshingIdentityStatus) return;
        setRefreshingIdentityStatus(true);
        setIdentityVerificationError(null);
        try {
            const refreshAstraIdentity = httpsCallable(functions, 'refreshAstraIdentityVerificationStatus');
            await withSentrySpan(
                {
                    op: 'kyc.refresh_status',
                    name: 'kyc.refresh_astra_identity_status',
                    attributes: { context: 'web_payer_identity_poll' },
                },
                () => refreshAstraIdentity({}),
            );
            const next = await refreshPayerSetupState();
            const status = getPaymentCapabilityStatus(next?.userData || {}, payerPaymentTarget);
            const outcome = getIdentityVerificationWaitOutcome(status);
            if (outcome === 'verified') {
                setShowIdentityBlockedModal(false);
                setIdentityBlockedStatusOverride(null);
                setIdentityVerificationWaitMode('idle');
                await continueSetupAfterRefresh();
                return;
            }
            if (outcome === 'document_required' || outcome === 'permanently_rejected') {
                setIdentityVerificationWaitMode('idle');
                setIdentityBlockedStatusOverride(outcome);
                setShowIdentityBlockedModal(true);
                return;
            }
            setIdentityVerificationWaitMode('timed_out');
            setIdentityBlockedStatusOverride('pending');
            setShowIdentityBlockedModal(true);
        } catch (error: any) {
            payerConsole.error('[PayerPage] Identity status refresh failed', error);
            setIdentityVerificationError('We could not refresh your verification status right now. Please try again.');
        } finally {
            setRefreshingIdentityStatus(false);
        }
    }, [continueSetupAfterRefresh, payerConsole, payerPaymentTarget, refreshPayerSetupState, refreshingIdentityStatus]);

    const launchPlaidForPayment = useCallback(async () => {
        if (!invite?.phone || isSetupBusyRef.current) return;
        setBankReconnectContext(null);
        try {
            setLaunchingPlaid(true);
            setErrorMessage(null);
            const result = await bankService.createLinkToken({ phone: invite.phone, mode: 'payment_bank' });
            setPlaidLinkToken(String(result?.linkToken || '').trim() || null);
        } catch (error: any) {
            payerConsole.error('[PayerPage] Failed to create Plaid link token', error);
            const mapped = mapPaymentErrorForUser(error, {
                title: 'Unable to connect bank',
                body: 'Unable to connect your bank right now.',
            });
            presentPaymentNotice(error, {
                title: mapped.title,
                body: mapped.body,
            }, mapped.retryable ? {
                actionLabel: 'Retry',
                onAction: () => {
                    setPaymentNotice(null);
                    void launchPlaidForPayment();
                },
            } : undefined);
            setLaunchingPlaid(false);
        }
    }, [invite?.phone, isSetupBusy, payerConsole, presentPaymentNotice]);

    const openBankRelink = useCallback(async (account?: any) => {
        if (!invite?.phone || isSetupBusyRef.current) return;
        const sourceAccountId = String(account?.id || account?.accountId || '').trim();
        const reconnectSourcePlaidItemId = String(account?.plaidItemId || '').trim();
        if (!sourceAccountId || !reconnectSourcePlaidItemId) {
            await launchPlaidForPayment();
            return;
        }
        try {
            setLaunchingPlaid(true);
            setErrorMessage(null);
            await bankService.preparePaymentBankReconnect({ plaidAccountId: sourceAccountId });
            setBankReconnectContext({
                account,
                sourceAccountId,
                sourcePlaidItemId: reconnectSourcePlaidItemId,
                sourceAccountType: String(account?.accountType || account?.subtype || account?.type || '').trim(),
            });
            const result = await bankService.createLinkToken({
                phone: invite.phone,
                mode: 'payment_bank',
                reconnectSourcePlaidItemId,
                updateAccountSelectionEnabled: false,
            }, true);
            setPlaidLinkToken(String(result?.linkToken || '').trim() || null);
        } catch (error: any) {
            payerConsole.error('[PayerPage] Failed to create Plaid relink token', error);
            setBankReconnectContext(null);
            const mapped = mapPaymentErrorForUser(error, {
                title: 'Unable to relink bank',
                body: 'Unable to relink your bank right now.',
            });
            presentPaymentNotice(error, {
                title: mapped.title,
                body: mapped.body,
            }, mapped.retryable ? {
                actionLabel: 'Retry',
                onAction: () => {
                    setPaymentNotice(null);
                    void openBankRelink(account);
                },
            } : undefined);
            setLaunchingPlaid(false);
        }
    }, [invite?.phone, isSetupBusy, launchPlaidForPayment, payerConsole, presentPaymentNotice]);

    const handlePlaidSuccess = useCallback(async (publicToken: string) => {
        if (!invite?.phone) return;
        try {
            setSetupTransitionMessage('Updating your bank connection...');
            const exchangeResult = await bankService.exchangeToken(publicToken, {
                phone: invite.phone,
                mode: 'payment_bank',
                fastLink: true,
                postConnectTarget: 'payment_authorization',
                forceVisualRefresh: true,
                includeBankVisuals: true,
            });
            await finalizePaymentBankReconnect({
                context: bankReconnectContext,
                exchangeResult,
                hasPaymentAuthorization,
                phone: invite.phone,
                registerPaymentBank: astraService.registerPaymentBank,
                relinkPaymentBank: bankService.relinkPaymentBank,
                onRegistrationWarning: (error: unknown) => {
                    payerConsole.warn('[PayerPage] Reconnected bank registration needs relink fallback', error);
                },
            });
            setPlaidLinkToken(null);
            setBankReconnectContext(null);
            setLaunchingPlaid(false);
            setShowConfirmPaymentModal(false);
            await continueSetupAfterRefresh();
        } catch (error: any) {
            payerConsole.error('[PayerPage] Plaid link exchange failed', error);
            setPlaidLinkToken(null);
            setLaunchingPlaid(false);
            isSetupBusyRef.current = false;
            setSetupTransitionMessage(null);
            const mapped = mapPaymentErrorForUser(error, {
                title: 'Unable to finish bank setup',
                body: 'Unable to finish connecting your bank.',
            });
            presentPaymentNotice(error, {
                title: mapped.title,
                body: mapped.body,
            }, mapped.retryable ? {
                actionLabel: 'Retry',
                onAction: () => {
                    setPaymentNotice(null);
                    if (bankReconnectContext?.account) {
                        void openBankRelink(bankReconnectContext.account);
                    } else {
                        void launchPlaidForPayment();
                    }
                },
            } : undefined);
        }
    }, [bankReconnectContext, continueSetupAfterRefresh, hasPaymentAuthorization, invite?.phone, launchPlaidForPayment, openBankRelink, payerConsole, plaidLinkToken, presentPaymentNotice]);

    const handlePlaidExit = useCallback(() => {
        setPlaidLinkToken(null);
        setBankReconnectContext(null);
        setLaunchingPlaid(false);
    }, []);

    const handleSubmitIdentityVerification = useCallback(async () => {
        if (!invite?.phone || !hasCompleteIdentityDraft(identityDraft)) {
            setIdentityVerificationError('Fill out all identity fields to continue.');
            return;
        }

        try {
            setSubmittingIdentity(true);
            setErrorMessage(null);
            setIdentityVerificationError(null);
            setShowIdentityVerificationSheet(false);
            setShowIdentityBlockedModal(false);
            setIdentityBlockedStatusOverride(null);
            setIdentityVerificationWaitMode('active');
            const boundUid = String((invite as any)?.participantUserUid || (invite as any)?.participantUid || '').trim();
            const uid = boundUid || (await userService.resolveUserDocByPhone(normalizePhone(invite.phone)))?.id || null;
            if (!uid) throw new Error('Unable to resolve invite user.');
            const canonicalIdentity = buildCanonicalIdentityFields(
                `${identityDraft.firstName} ${identityDraft.lastName}`.trim()
            );
            await setDoc(doc(firestore, 'users', uid), {
                ...canonicalIdentity,
                email: identityDraft.email.trim(),
                birthYear: Number(identityDraft.dobYear),
            }, { merge: true });
            persistSetupResume('payment_authorization');
            const result = await astraService.startIdentityVerification({
                phone: invite.phone,
                legalName: `${identityDraft.firstName} ${identityDraft.lastName}`.trim(),
                address: {
                    line1: identityDraft.addressLine1.trim(),
                    ...(identityDraft.addressLine2?.trim() ? { line2: identityDraft.addressLine2.trim() } : {}),
                    city: identityDraft.city.trim(),
                    state: identityDraft.state.trim(),
                    postalCode: identityDraft.postalCode.trim(),
                },
                dob: {
                    month: Number(identityDraft.dobMonth),
                    day: Number(identityDraft.dobDay),
                    year: Number(identityDraft.dobYear),
                },
                ssnLast4: identityDraft.ssnLast4.trim(),
                redirectUri: buildAstraReturnUrl('identity'),
            });

            if (result?.status === 'retryable_rejected') {
                setIdentityVerificationWaitMode('idle');
                setShowIdentityVerificationSheet(true);
                setIdentityVerificationError(result?.message || 'Please check your information and try again.');
                return;
            }

            if (result?.status === 'permanently_rejected') {
                setIdentityVerificationWaitMode('idle');
                setIdentityBlockedStatusOverride('permanently_rejected');
                setShowIdentityVerificationSheet(false);
                await refreshPayerSetupState();
                setShowIdentityBlockedModal(true);
                return;
            }
            if (result?.status === 'document_required') {
                setIdentityVerificationWaitMode('idle');
                setIdentityBlockedStatusOverride('document_required');
                setShowIdentityVerificationSheet(false);
                await refreshPayerSetupState();
                setShowIdentityBlockedModal(true);
                return;
            }

            if (result?.onboardingUrl) {
                window.location.assign(result.onboardingUrl);
                return;
            }

        } catch (error: any) {
            payerConsole.error('[PayerPage] Identity verification failed', error);
            const providerMessage = resolveErrorMessage(error);
            setIdentityVerificationWaitMode('idle');
            setShowIdentityVerificationSheet(true);
            setIdentityVerificationError(getIdentitySubmissionErrorMessage(providerMessage, resolveErrorCode(error)));
        } finally {
            setSubmittingIdentity(false);
        }
    }, [astraService, buildAstraReturnUrl, continueSetupAfterRefresh, identityDraft, invite?.phone, payerConsole, persistSetupResume, refreshPayerSetupState]);

    const handleContinueToAstra = useCallback(async () => {
        if (!invite?.phone) return;
        if (!hasCompleteIdentityDraft(identityDraft)) {
            setShowAstraAuthorizationSheet(false);
            setShowIdentityVerificationSheet(true);
            return;
        }

        try {
            setAuthorizingAstra(true);
            setErrorMessage(null);
            const boundUid = String((invite as any)?.participantUserUid || (invite as any)?.participantUid || '').trim();
            const uid = boundUid || (await userService.resolveUserDocByPhone(normalizePhone(invite.phone)))?.id || null;
            if (!uid) throw new Error('Unable to resolve invite user.');
            const canonicalIdentity = buildCanonicalIdentityFields(
                `${identityDraft.firstName} ${identityDraft.lastName}`.trim()
            );
            await setDoc(doc(firestore, 'users', uid), {
                ...canonicalIdentity,
                email: identityDraft.email.trim(),
                birthYear: Number(identityDraft.dobYear),
            }, { merge: true });
            persistSetupResume('confirm');
            const result = await astraService.authorizePaymentUser({
                phone: invite.phone,
                legalName: `${identityDraft.firstName} ${identityDraft.lastName}`.trim(),
                address: {
                    line1: identityDraft.addressLine1.trim(),
                    ...(identityDraft.addressLine2?.trim() ? { line2: identityDraft.addressLine2.trim() } : {}),
                    city: identityDraft.city.trim(),
                    state: identityDraft.state.trim(),
                    postalCode: identityDraft.postalCode.trim(),
                },
                dob: {
                    month: Number(identityDraft.dobMonth),
                    day: Number(identityDraft.dobDay),
                    year: Number(identityDraft.dobYear),
                },
                ssnLast4: identityDraft.ssnLast4.trim(),
                consentCaptured: true,
                plaidAccountId: selectedPaymentBank?.id,
                redirectUri: buildAstraReturnUrl('payment_authorization'),
            });

            if (result?.status === 'document_required') {
                setShowAstraAuthorizationSheet(false);
                await refreshPayerSetupState();
                setShowIdentityBlockedModal(true);
                return;
            }

            if (result?.onboardingUrl) {
                window.location.assign(result.onboardingUrl);
                return;
            }

            if (selectedPaymentBank?.id) {
                await astraService.registerPaymentBank({
                    phone: invite.phone,
                    plaidAccountId: selectedPaymentBank.id,
                });
            }
            setShowAstraAuthorizationSheet(false);
            await continueSetupAfterRefresh();
        } catch (error: any) {
            payerConsole.error('[PayerPage] Astra authorization failed', error);
            presentPaymentNotice(error, {
                title: 'Unable to approve bank pay',
                body: 'Unable to approve bank pay.',
            });
        } finally {
            setAuthorizingAstra(false);
        }
    }, [buildAstraReturnUrl, continueSetupAfterRefresh, identityDraft, invite?.phone, payerConsole, persistSetupResume, presentPaymentNotice, selectedPaymentBank?.id]);

    // Payment Logic
    const handleSplitInsufficientFunds = useCallback((error: any) => {
        const parsedDetails = getInsufficientFundsErrorDetails(error);
        if (!parsedDetails) return false;
        const details = mergeInsufficientFundsErrorDetails(parsedDetails, {
            bankName: selectedPaymentBank ? getPlaidAccountBankName(selectedPaymentBank) : 'Connected Bank',
            bankLast4: selectedPaymentBank ? getPlaidAccountLast4(selectedPaymentBank) : '',
            requiredAmountCents: null,
            plaidAccountId: selectedPaymentBank?.id || null,
        });
        if (!details) return false;
        dismissConfirmPaymentModalImmediately();
        setInsufficientFundsDetails(details);
        return true;
    }, [dismissConfirmPaymentModalImmediately, selectedPaymentBank]);

    const handleInitiatePayment = async (options?: { claimableAcknowledged?: boolean }) => {
        const claimableAcknowledged = options?.claimableAcknowledged === true;
        if (!invite || !session || paid || paying || paymentSubmissionLockRef.current) return;
        if (paymentsBlockedByIdentity) {
            setShowIdentityBlockedModal(true);
            return;
        }
        if (paymentsAreDisabled) {
            await handleVenmoPayment();
            return;
        }
        if (!partialPaymentIsValid) {
            setErrorMessage(`Enter an amount between $0.01 and $${remainingOwedAmount.toFixed(2)}.`);
            return;
        }
        if (!paymentsAreDisabled && exceedsAvailablePaymentLimit) {
            dismissConfirmPaymentModalImmediately();
            setLimitExceededDetails({
                limitType: 'weekly',
                limitAmountCents: userAvailablePaymentLimitCents || 200000,
                resetDate: null,
            });
            return;
        }

        if (isSimulator) {
            triggerImpactHaptic('heavy');
            paymentSubmissionLockRef.current = true;
            setPaying(true);
            setTimeout(() => {
                const prevRemaining = remainingOwedAmount;
                const willFullyPay = paymentAmountCentsForSubmit >= Math.round(remainingOwedAmount * 100);
                const nextRemaining = willFullyPay ? 0 : Math.max(0, remainingOwedAmount - paymentAmountForSubmit);
                closeProcessingPaymentModal();
                triggerSuccessHaptic();
                pendingSuccessHeroAnimationRef.current = {
                    previousRemainingAmount: prevRemaining,
                    nextRemainingAmount: nextRemaining,
                    markFullyPaid: willFullyPay,
                };
                setShowPaymentInitiatedSuccessOverlay(true);
                setPaying(false);
                paymentSubmissionLockRef.current = false;
            }, 1000);
            return;
        }

        triggerImpactHaptic('heavy');
        paymentSubmissionLockRef.current = true;
        setPaying(true);
        setErrorMessage(null);
        void logFounderPreviewEvent('payment_attempted', {
            sessionId: session.id,
            invitePhone: invite.phone || null,
            rail: 'bank_ach',
            amount: paymentAmountForSubmit,
        });

        try {
            const next = await refreshPayerSetupState();
            if (isIdentityReviewOrRestrictionBlockingPayments(next?.userData || {}, payerPaymentTarget)) {
                setShowIdentityBlockedModal(true);
                return;
            }
            let organizerPreflightData: any = null;
            try {
                organizerPreflightData = await validateOrganizerBeforePayment();
                if (organizerPreflightData?.claimableFallbackRequired === true && !claimableAcknowledged) {
                    dismissConfirmPaymentModalImmediately();
                    setClaimablePaymentWindowDays(Number(organizerPreflightData?.claimWindowDays || 20) || 20);
                    setShowClaimablePaymentSheet(true);
                    return;
                }
            } catch (preflightError: any) {
                const preflightData = preflightError?.preflightData;
                if (preflightData?.reason === 'organizer_permanently_rejected') {
                    dismissConfirmPaymentModalImmediately();
                    const rawOrganizer = String(preflightData?.organizerName || session?.organizerName || 'The organizer').trim();
                    const organizerName = formatOrganizerFirstName(rawOrganizer) || 'Organizer';
                    setOrganizerRejectedMessage(`${organizerName} is not verified to receive payments on Clero.`);
                    setShowOrganizerRejectedSheet(true);
                    return;
                }
                presentPaymentNotice(preflightError, {
                    title: 'Request unavailable',
                    body: preflightError?.message || 'Unable to verify this split right now.',
                });
                return;
            }

            if (!next?.selectedAccount) {
                if (hasOnlyIneligiblePaymentBanks) {
                    setShowIneligibleBankSheet(true);
                    return;
                }
                await launchPlaidForPayment();
                return;
            }

            if (selectedPaymentBank && isPlaidAccountPaymentIneligible(selectedPaymentBank)) {
                dismissConfirmPaymentModalImmediately();
                setShowIneligibleBankSheet(true);
                return;
            }

            if (next.needsIdentityVerification) {
                setShowIdentityVerificationSheet(true);
                return;
            }

            if (!next.hasPaymentAuthorization) {
                setShowAstraAuthorizationSheet(true);
                return;
            }

            await bankService.payInvite({
                sessionId: session.id,
                inviteId: invite.id,
                plaidAccountId: selectedPaymentBank?.id || undefined,
                paymentAmountCents: paymentAmountCentsForSubmit,
                baseAmountCents: paymentBaseAmountCentsForSubmit,
                claimableAcknowledged: claimableAcknowledged || organizerPreflightData?.claimableFallbackRequired === true,
            }, { savePaymentMethod: false });
            const prevRemaining = remainingOwedAmount;
            const willFullyPay = paymentAmountCentsForSubmit >= Math.round(remainingOwedAmount * 100);
            const nextRemaining = willFullyPay ? 0 : Math.max(0, remainingOwedAmount - paymentAmountForSubmit);
            closeProcessingPaymentModal();
            setOptimisticPaid(willFullyPay);
            triggerSuccessHaptic();
            pendingSuccessHeroAnimationRef.current = {
                previousRemainingAmount: prevRemaining,
                nextRemainingAmount: nextRemaining,
                markFullyPaid: willFullyPay,
            };
            try {
                await refreshPayerInviteData();
            } catch (refreshError) {
                payerConsole.warn('[PayerPage] Payment started but invite refresh failed', refreshError);
            }
            setShowPaymentInitiatedSuccessOverlay(true);
            void logFounderPreviewEvent('payment_processing', {
                sessionId: session.id,
                invitePhone: invite.phone || null,
                rail: 'bank_ach',
                amount: paymentAmountForSubmit,
            });
        } catch (err: any) {
            triggerErrorHaptic();
            setOptimisticPaid(false);
            payerConsole.error('Payment failed:', err);
            if (isPaymentCanceledError(err)) {
                setErrorMessage(null);
                return;
            }
            const claimDetails = getClaimablePaymentConfirmationDetails(err);
            if (!claimableAcknowledged && claimDetails) {
                dismissConfirmPaymentModalImmediately();
                setClaimablePaymentWindowDays(claimDetails.claimWindowDays);
                setShowClaimablePaymentSheet(true);
                return;
            }
            if (!paymentsAreDisabled && handleSplitInsufficientFunds(err)) {
                return;
            }
            const limitDetails = parseLimitExceededErrorDetails(err);
            if (limitDetails) {
                dismissConfirmPaymentModalImmediately();
                setLimitExceededDetails(limitDetails);
                return;
            }
            const normalizedMessage = String(err?.message || err?.details?.message || '').toLowerCase();
            if (
                normalizedMessage.includes('complete astra verification and connect a plaid bank account to pay by ach')
                || normalizedMessage.includes('astra authorization is required before registering a payout bank')
            ) {
                setShowConfirmPaymentModal(false);
                await continueSetupAfterRefresh();
                return;
            }
            const mapped = mapPaymentErrorForUser(err, {
                title: 'Payments are temporarily unavailable',
                body: 'We could not start this payment right now. Please try again shortly.',
            });
            presentPaymentNotice(err, {
                title: mapped.title,
                body: mapped.body,
            }, mapped.category === 'bank_action_required'
                ? {
                    actionLabel: selectedPaymentBank ? 'Update bank' : 'Connect bank',
                    onAction: () => {
                        setPaymentNotice(null);
                        if (selectedPaymentBank) {
                            void openBankRelink(selectedPaymentBank);
                        } else {
                            void launchPlaidForPayment();
                        }
                    },
                }
                : mapped.category === 'verification_required'
                    ? {
                        actionLabel: 'Continue setup',
                        onAction: () => {
                            setPaymentNotice(null);
                            void continueSetupAfterRefresh();
                        },
                    }
                    : mapped.category === 'sign_in_required'
                        ? {
                            actionLabel: 'Refresh',
                            onAction: () => {
                                window.location.reload();
                            },
                        }
                        : mapped.retryable
                            ? {
                                actionLabel: 'Retry',
                                onAction: () => {
                                    setPaymentNotice(null);
                                    void handleInitiatePayment({ claimableAcknowledged });
                                },
                            }
                            : undefined
            );
        } finally {
            setPaying(false);
            paymentSubmissionLockRef.current = false;
        }
    };

    const handleDeclineSplit = async () => {
        if (!invite || !session || declining || paying) return;
        if (paid || inviteStatus === 'processing' || inviteStatus === 'declined' || sessionStatus === 'completed' || (session as any).settledOffline) {
            return;
        }

        const confirmed = window.confirm(
            "Are you sure you want to decline this split? The organizer will be notified and your assigned items will be unclaimed."
        );
        if (!confirmed) return;

        try {
            setDeclining(true);
            setErrorMessage(null);
            const declineSplitResource = httpsCallable(functions, 'declineSplit');
            await declineSplitResource({
                sessionId: session.id,
                inviteId: invite.id || token,
                phone: invite.phone,
            });
            void logFounderPreviewEvent('split_declined', {
                sessionId: session.id,
                invitePhone: invite.phone || null,
            });
        } catch (err: any) {
            payerConsole.error('Decline request failed:', err);
            setErrorMessage(err.message || 'Failed to decline request.');
        } finally {
            setDeclining(false);
        }
    };

    const handleRemoveBank = async () => {
        if (!invite || !selectedPaymentBank) return;
        setIsRemoving(true);
        try {
            const phone = invite.phone.replace(/[^\d+]/g, '');
            payerConsole.log("📡 [PayerPage] Attempting to unlink bank:", { phone, bankLinkId: selectedPaymentBank?.id });
            await bankService.unlinkPaymentBank({
                phone,
                bankLinkId: selectedPaymentBank?.id
            });
            setSelectedPaymentBankId(null);
            setIsRemoveModalOpen(false);
            await refreshPayerSetupState();
        } catch (error: any) {
            setErrorMessage(error.message || 'Failed to remove bank.');
        } finally {
            setIsRemoving(false);
        }
    };


    const appDeepLink = `clero://pay/${encodeURIComponent(token || '')}`;

    const formatDisplayPhone = (num: string) => {
        let cleaned = String(num || '').replace(/\D/g, '');
        if (cleaned.startsWith('1')) {
            cleaned = cleaned.slice(1);
        }
        cleaned = cleaned.slice(0, 10);
        if (cleaned.length <= 3) return cleaned;
        if (cleaned.length <= 6) return '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3);
        return '(' + cleaned.slice(0, 3) + ') ' + cleaned.slice(3, 6) + '-' + cleaned.slice(6, 10);
    };

    const handleResetOtp = () => {
        setPayerOtpConfirmation(null);
        setPayerOtpCode('');
        lastFailedOtpRef.current = '';
        setErrorMessage(null);
        setCanResend(false);
        setResendCountdown(30);
        setAuthStep('phone');
        resetSharedRecaptchaVerifier('recaptcha-container');
    };

    const handlePayerLogout = async () => {
        if (loggingOut || !auth.currentUser) return;
        try {
            payerLogoutInProgressRef.current = true;
            setLoggingOut(true);
            setErrorMessage(null);
            stopPayerPrivateSubscriptions();
            await auth.signOut();
            clearPayerSetupResumeState(token);
            localStorage.removeItem('clero_last_session');
            setIsOrganizerRole(false);
            setInvite(null);
            setSession(null);
            setSessionInvites([]);
            setAuthSessionUser(null);
            setPayerProfile(null);
            setPlaidAccounts([]);
            setSelectedPaymentBankId(null);
            setPaymentLimitsSnapshot(null);
            setPayerOtpPhone('');
            setAuthStep('preview');
            handleResetOtp();
            setShowIdentityVerificationSheet(false);
            setShowAstraAuthorizationSheet(false);
            setShowConfirmPaymentModal(false);
            setPaymentNotice(null);
        } catch (error) {
            payerConsole.error('[PayerPage] Logout failed', error);
            setErrorMessage('Unable to log out. Please try again.');
            if (auth.currentUser) {
                setPayerPrivateSubscriptionEpoch((current) => current + 1);
            }
        } finally {
            payerLogoutInProgressRef.current = false;
            setLoggingOut(false);
        }
    };

    const sendPayerOtp = async (forceSms = false) => {
        const phone = normalizePhone(payerOtpPhone);
        if (!phone || phone.replace(/\D/g, '').length < 10) {
            setErrorMessage('Please enter a valid phone number');
            return;
        }
        const verifier = getSharedRecaptchaVerifier('recaptcha-container', auth);
        if (!verifier) {
            setErrorMessage('Unable to start phone verification. Refresh and try again.');
            return;
        }
        try {
            setPayerOtpBusy(true);
            setErrorMessage(null);
            lastFailedOtpRef.current = '';
            const confirmation = await signInWithPhoneNumber(auth, phone, verifier);
            setPayerOtpConfirmation(confirmation);
            setAuthStep('otp');
            setCanResend(false);
            setResendCountdown(30);
            if (forceSms) {
                setPayerOtpCode('');
            }
        } catch (error: any) {
            payerConsole.error('Error sending OTP:', error);
            const msg = error?.message || 'Unable to send verification code.';
            setErrorMessage(msg);
            resetSharedRecaptchaVerifier('recaptcha-container');
        } finally {
            setPayerOtpBusy(false);
        }
    };

    const verifyPayerOtp = async (codeToVerify?: string) => {
        const code = (codeToVerify || payerOtpCode).trim();
        if (!payerOtpConfirmation || code.length !== 6 || payerOtpBusy || auth.currentUser) return;
        if (lastFailedOtpRef.current === code) return;

        const currentConfirmation = payerOtpConfirmation;
        try {
            setPayerOtpBusy(true);
            setErrorMessage(null);
            await currentConfirmation.confirm(code);
            setPayerOtpCode('');
            lastFailedOtpRef.current = '';
            setErrorMessage(null);
        } catch (error: any) {
            payerConsole.error('Error verifying OTP:', error);
            lastFailedOtpRef.current = code;
            if (auth.currentUser) {
                // If user is already authenticated, ignore verification ID reuse errors
                setErrorMessage(null);
                return;
            }
            setPayerOtpConfirmation(currentConfirmation);
            const rawMsg = String(error?.message || error?.code || '');
            if (rawMsg.includes('invalid-verification-code') || rawMsg.includes('invalid code') || rawMsg.includes('400')) {
                setErrorMessage('Incorrect verification code. Please check and try again.');
            } else {
                setErrorMessage(error?.message || 'Invalid verification code.');
            }
        } finally {
            setPayerOtpBusy(false);
        }
    };

    // Auto-verify OTP when 6 digits are entered
    useEffect(() => {
        const clean = payerOtpCode.trim();
        if (clean.length === 6 && payerOtpConfirmation && !payerOtpBusy && !auth.currentUser && lastFailedOtpRef.current !== clean) {
            payerConsole.log("🚀 [PayerPage] OTP complete, auto-submitting...");
            void verifyPayerOtp(clean);
        }
    }, [payerOtpCode, payerOtpConfirmation, payerOtpBusy]);

    // Resend countdown timer
    useEffect(() => {
        if (!payerOtpConfirmation || canResend) return;

        if (resendCountdown <= 0) {
            setCanResend(true);
            setResendCountdown(30);
            return;
        }

        const timer = window.setTimeout(() => {
            setResendCountdown((prev) => prev - 1);
        }, 1000);

        return () => window.clearTimeout(timer);
    }, [canResend, resendCountdown, payerOtpConfirmation]);

    // Match browser overscroll area with mesh background
    useEffect(() => {
        if (invite && session) return;
        const prevBodyBg = document.body.style.backgroundColor;
        const prevHtmlBg = document.documentElement.style.backgroundColor;
        const meshBase = '#f8fafc';

        document.body.style.backgroundColor = meshBase;
        document.documentElement.style.backgroundColor = meshBase;

        return () => {
            document.body.style.backgroundColor = prevBodyBg;
            document.documentElement.style.backgroundColor = prevHtmlBg;
        };
    }, [invite, session]);

    const isAnyOverlayActive = Boolean(
        isSelectingItems ||
        isParticipantsSheetOpen ||
        showConfirmPaymentModal ||
        viewingReceipt ||
        isRemoveModalOpen ||
        claimConflictModal.open ||
        showIneligibleBankSheet ||
        Boolean(paymentNotice) ||
        showPaymentInitiatedSuccessOverlay ||
        Boolean(viewingItemParticipants) ||
        showIdentityVerificationSheet ||
        showAstraAuthorizationSheet ||
        showIdentityBlockedModal ||
        showClaimablePaymentSheet ||
        showOrganizerRejectedSheet ||
        showErrorModal ||
        Boolean(setupTransitionMessage)
    );

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!isAnyOverlayActive) return;

        const prevBodyOverflow = document.body.style.overflow;
        const prevDocOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.overflow = prevDocOverflow;
        };
    }, [isAnyOverlayActive]);

    if (!webPaymentsResolved || (loading && !invite && !session && webPaymentsEnabled)) return (
        <div className="min-h-[100dvh] bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col items-center">
            {/* Top Navigation */}
            <PayerTopNav
                currentUser={authSessionUser || auth.currentUser}
                currentUserProfile={payerProfile}
                onLogout={() => { void handlePayerLogout(); }}
                loggingOut={loggingOut}
                appDeepLink={appDeepLink}
            />

            {/* Main Center Container */}
            <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-20 pb-36 space-y-4 animate-fadeIn">
                {/* Clero Brand Wordmark on Top of Centered Content */}
                <div className="flex justify-center pb-1">
                    <CleroWordmarkSvg width={96} color="#0D9488" />
                </div>

                {/* Hero Card Loading Skeleton */}
                <WebPaymentHeroCard loading={true} />

                {/* OR Divider */}
                <div className="relative flex items-center justify-center py-1">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200/70" />
                    </div>
                    <div className="relative bg-[#F8FAFC] px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none">
                        OR
                    </div>
                </div>

                {/* App Download Card */}
                <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none">
                    <div className="space-y-1">
                        <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Pay with Clero App</p>
                        <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                            Direct bank payments and instant bill splitting on mobile.
                        </p>
                    </div>
                    <div className="flex items-center justify-center pt-0.5">
                        <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                    </div>
                </div>

                {/* Trust Footer */}
                <div className="pt-2 pb-1 text-center select-none space-y-2">
                    <div className="flex items-center justify-center gap-2 text-[11px] font-medium text-slate-400">
                        <span>Powered by</span>
                        <img src="/plaid_logo.png" alt="Plaid" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                        <img src="/astra_logo.png" alt="Astra" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                    </div>
                    <p className="text-[10px] text-slate-400 font-medium text-center leading-relaxed">
                        By continuing, you agree to Clero's <a href="/terms" className="text-teal-600 font-semibold hover:underline">Terms</a> and <a href="/privacy" className="text-teal-600 font-semibold hover:underline">Privacy</a>
                    </p>
                </div>
            </div>

            {/* Sticky Bottom Footer */}
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F8FAFC]/95 backdrop-blur-md border-t border-slate-100/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <div className="max-w-md mx-auto space-y-2">
                    <button
                        disabled
                        className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight flex items-center justify-center gap-2 opacity-90 shadow-none cursor-default"
                    >
                        <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                        <span>Opening Request...</span>
                    </button>
                    <p className="text-xs text-slate-500 font-medium text-center">
                        Already have Clero?{' '}
                        <a href={appDeepLink} className="text-teal-600 font-bold hover:underline">
                            Open in App
                        </a>
                    </p>
                </div>
            </div>

            <div id="recaptcha-container" className="hidden"></div>
        </div>
    );

    const rawPreviewOrgName = previewOrganizerName || session?.organizerName || '';
    const displayPreviewOrgName = rawPreviewOrgName.trim();
    const resolvedOrgPhotoUrl = previewOrganizerPhotoUrl || session?.organizerPhotoUrl || (session as any)?.organizerPhotoURL || (session as any)?.organizerPhoto || null;

    if (!webPaymentsEnabled) return (
        <div className="min-h-screen bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col justify-between items-center">
            <PayerTopNav
                currentUser={authSessionUser || auth.currentUser}
                currentUserProfile={payerProfile}
                onLogout={() => { void handlePayerLogout(); }}
                loggingOut={loggingOut}
                appDeepLink={appDeepLink}
            />

            <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-2 pb-36 space-y-4 animate-fadeIn">
                {/* Clero Brand Wordmark on Top of Centered Content */}
                <div className="flex justify-center pb-1">
                    <CleroWordmarkSvg width={96} color="#0D9488" />
                </div>

                <div className="rounded-[2.25rem] bg-white p-6 sm:p-8 space-y-4 shadow-none">
                    <div className="text-center space-y-1.5">
                        <h2 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">Web payments coming soon</h2>
                        <p className="text-xs text-slate-500 font-semibold leading-relaxed">
                            Clero web payments are currently in preview. Download the app to complete payments securely and fast.
                        </p>
                    </div>

                    <WebPaymentHeroCard
                        splitTitle={previewSplitTitle || session?.title}
                        organizerName={displayPreviewOrgName}
                        organizerPhotoUrl={resolvedOrgPhotoUrl}
                        peopleCount={previewPeopleCount ?? session?.participantUids?.length}
                        totalAmount={previewTotalAmount ?? session?.total ?? session?.amount}
                        note={previewNote || session?.note}
                        createdAt={previewCreatedAt || session?.createdAt}
                        receiptThumbnailURL={previewReceiptThumbnailURL || session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl}
                        receiptImageURL={previewReceiptImageURL || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl}
                        merchantLogoUrl={previewMerchantLogoUrl || session?.merchantLogoUrl || session?.merchantLogoURL}
                        sessionIcon={previewSessionIcon || session?.sessionIcon}
                        sessionIconColor={previewSessionIconColor || session?.sessionIconColor}
                        loading={previewLoading && !previewSplitTitle}
                        onViewReceipt={() => setViewingReceipt(true)}
                    />
                </div>

                {/* OR Divider */}
                <div className="relative flex items-center justify-center py-1">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200/70" />
                    </div>
                    <div className="relative bg-[#F8FAFC] px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none">
                        OR
                    </div>
                </div>

                {/* App Download Card */}
                <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none">
                    <div className="space-y-1">
                        <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Pay with Clero App</p>
                        <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                            Direct bank payments and instant bill splitting on mobile.
                        </p>
                    </div>
                    <div className="flex items-center justify-center pt-0.5">
                        <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Footer */}
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F8FAFC]/95 backdrop-blur-md border-t border-slate-100/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <div className="max-w-md mx-auto space-y-2">
                    <a
                        href={appDeepLink}
                        className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-none"
                    >
                        <span>Open in Clero App</span>
                        <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                    </a>
                </div>
            </div>

            {/* Top-Level Receipt Modal Fallback */}
            <WebReceiptViewerModal
                visible={viewingReceipt}
                imageUrl={previewReceiptImageURL || previewReceiptThumbnailURL || session?.receiptImageURL || session?.receiptThumbnailURL || ''}
                onClose={() => setViewingReceipt(false)}
            />

            <div id="recaptcha-container" className="hidden"></div>
        </div>
    );

    if (isUninvitedToSplit) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col items-center">
                <PayerTopNav
                    currentUser={authSessionUser || auth.currentUser}
                    currentUserProfile={payerProfile}
                    onLogout={() => { void handlePayerLogout(); }}
                    loggingOut={loggingOut}
                    appDeepLink={appDeepLink}
                />
                <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-2 pb-36 space-y-4 animate-fadeIn">
                    {/* Clero Brand Wordmark on Top of Centered Content */}
                    <div className="flex justify-center pb-1">
                        <CleroWordmarkSvg width={96} color="#0D9488" />
                    </div>

                    <div className="rounded-[2.25rem] bg-white p-6 sm:p-8 space-y-4 shadow-none">
                        <div className="text-center space-y-1.5">
                            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2 text-slate-500">
                                <UserX className="w-6 h-6 stroke-[2]" />
                            </div>
                            <h2 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                You're not on this request yet
                            </h2>
                            <p className="text-sm text-slate-500 font-medium leading-relaxed">
                                Ask {displayPreviewOrgName ? displayPreviewOrgName.split(/\s+/)[0] : 'the organizer'} to add your phone number ({formatPhoneForDisplay(auth.currentUser?.phoneNumber || '') || 'this number'}) to the request.
                            </p>
                        </div>

                        <WebPaymentHeroCard
                            splitTitle={previewSplitTitle || session?.title}
                            organizerName={displayPreviewOrgName}
                            organizerPhotoUrl={resolvedOrgPhotoUrl}
                            peopleCount={previewPeopleCount ?? session?.participantUids?.length}
                            totalAmount={previewTotalAmount ?? session?.total ?? session?.amount}
                            note={previewNote || session?.note}
                            createdAt={previewCreatedAt || session?.createdAt}
                            receiptThumbnailURL={previewReceiptThumbnailURL || session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl}
                            receiptImageURL={previewReceiptImageURL || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl}
                            merchantLogoUrl={previewMerchantLogoUrl || session?.merchantLogoUrl || session?.merchantLogoURL}
                            sessionIcon={previewSessionIcon || session?.sessionIcon}
                            sessionIconColor={previewSessionIconColor || session?.sessionIconColor}
                            loading={previewLoading && !previewSplitTitle}
                            onViewReceipt={() => setViewingReceipt(true)}
                        />

                        <div className="pt-2 flex flex-col gap-2.5">
                            <button
                                type="button"
                                onClick={() => { void handlePayerLogout(); }}
                                className="w-full py-3.5 border border-slate-200 rounded-full font-bold text-xs uppercase tracking-wider text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>Switch Account ({formatPhoneForDisplay(auth.currentUser?.phoneNumber || '')})</span>
                            </button>
                            <a
                                href="/"
                                className="text-center text-xs font-bold text-teal-600 hover:text-teal-700 py-2"
                            >
                                Go to Clero Home
                            </a>
                        </div>
                    </div>

                    {/* OR Divider */}
                    <div className="relative flex items-center justify-center py-1">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-200/70" />
                        </div>
                        <div className="relative bg-[#F8FAFC] px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none">
                            OR
                        </div>
                    </div>

                    {/* App Download Card */}
                    <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none">
                        <div className="space-y-1">
                            <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Pay with Clero App</p>
                            <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                                Direct bank payments and instant bill splitting on mobile.
                            </p>
                        </div>
                        <div className="flex items-center justify-center pt-0.5">
                            <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                        </div>
                    </div>
                </div>

                {/* Sticky Bottom Footer */}
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F8FAFC]/95 backdrop-blur-md border-t border-slate-100/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div className="max-w-md mx-auto space-y-2">
                        <p className="text-xs text-slate-500 font-medium text-center">
                            Already have Clero?{' '}
                            <a href={appDeepLink} className="text-teal-600 font-bold hover:underline">
                                Open in App
                            </a>
                        </p>
                    </div>
                </div>

                {/* Top-Level Receipt Modal Fallback */}
                <WebReceiptViewerModal
                    visible={viewingReceipt}
                    imageUrl={previewReceiptImageURL || previewReceiptThumbnailURL || session?.receiptImageURL || session?.receiptThumbnailURL || ''}
                    onClose={() => setViewingReceipt(false)}
                />

                <div id="recaptcha-container" className="hidden"></div>
            </div>
        );
    }

    if (previewNotFound && !session) {
        return (
            <div className="min-h-[100dvh] bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col items-center">
                {/* Top Navigation */}
                <PayerTopNav
                    currentUser={authSessionUser || auth.currentUser}
                    currentUserProfile={payerProfile}
                    onLogout={() => { void handlePayerLogout(); }}
                    loggingOut={loggingOut}
                    appDeepLink={appDeepLink}
                />

                {/* Main Center Container */}
                <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-20 pb-16 space-y-4 animate-fadeIn">
                    {/* Clero Brand Wordmark on Top of Centered Content */}
                    <div className="flex justify-center pb-1">
                        <CleroWordmarkSvg width={96} color="#0D9488" />
                    </div>

                    {/* Not Found Card */}
                    <div className="rounded-[2.25rem] bg-white p-7 sm:p-8 text-center space-y-5 shadow-none border border-slate-100">
                        <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200/70 flex items-center justify-center text-slate-400 mx-auto">
                            <SearchX className="w-7 h-7 stroke-[1.75]" />
                        </div>

                        <div className="space-y-1.5">
                            <h3 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                Payment request not found
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed max-w-xs mx-auto">
                                This request link is invalid, expired, or may have been deleted by the organizer.
                            </p>
                        </div>

                        <div className="pt-2">
                            <a
                                href="/"
                                className="inline-flex items-center justify-center w-full py-3.5 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight hover:bg-slate-800 transition-all active:scale-[0.98]"
                            >
                                Go to Clero Home
                            </a>
                        </div>
                    </div>

                    {/* App Download Card */}
                    <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none border border-slate-100">
                        <div className="space-y-1">
                            <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Clero for iPhone & Android</p>
                            <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                                Direct bank payments and instant bill splitting on mobile.
                            </p>
                        </div>
                        <div className="flex items-center justify-center pt-0.5">
                            <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const formatDateOnly = (value: any) => {
        if (!value) return '';
        const date = value?.toDate ? value.toDate() : new Date(value?.seconds ? value.seconds * 1000 : value);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const myPhoneNorm = normalizePhone(getInvitePhone(invite) || invite?.phone || auth.currentUser?.phoneNumber || '');
    const organizerPhoneNorm = normalizePhone(String(session?.organizerPhone || ''));
    const currentUserPhoneNorm = normalizePhone(auth.currentUser?.phoneNumber || '');
    const organizerUidNorm = String(session?.organizerUid || session?.createdBy || '').trim();
    const currentAuthUid = String(auth.currentUser?.uid || '').trim();
    const isOrganizerViewer = Boolean(
        isOrganizerRole ||
        (organizerPhoneNorm && (
            (currentUserPhoneNorm && currentUserPhoneNorm === organizerPhoneNorm) ||
            (myPhoneNorm && myPhoneNorm === organizerPhoneNorm)
        )) ||
        (organizerUidNorm && currentAuthUid && organizerUidNorm === currentAuthUid)
    );

    if (isOrganizerViewer) {
        const rawItems = Array.isArray(session?.items) ? session.items : [];
        const itemsCount = rawItems.length;
        const participantsCount = session?.invites
            ? Object.keys(session.invites).length + 1
            : (previewPeopleCount || (Array.isArray(session?.participantUids) ? session.participantUids.length : 1));
        const createdDateStr = formatDateOnly(session?.createdAt || session?.date || previewCreatedAt || null) || undefined;
        const purchaseMerchantName = String(session?.site || session?.title || previewSplitTitle || 'Clero Split').trim() || 'Clero Split';

        return (
            <WebOrganizerBlockedTakeover
                sessionToken={token}
                sessionId={session?.id || token}
                appDeepLink={appDeepLink}
                merchantName={purchaseMerchantName}
                amount={Number(session?.totalAmount || session?.subtotal || session?.amount || previewTotalAmount || 0)}
                itemsCount={itemsCount}
                participantsCount={participantsCount}
                createdDate={createdDateStr}
                groupName={session?.groupName}
                onLogout={() => { void handlePayerLogout(); }}
                loggingOut={loggingOut}
            />
        );
    }

    if (!invite || !session) {
        if (auth.currentUser) {
            return (
                <div className="min-h-[100dvh] bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col items-center">
                    {/* Top Navigation */}
                    <PayerTopNav
                        currentUser={authSessionUser || auth.currentUser}
                        currentUserProfile={payerProfile}
                        onLogout={() => { void handlePayerLogout(); }}
                        loggingOut={loggingOut}
                        appDeepLink={appDeepLink}
                    />

                    {/* Main Center Container */}
                    <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-20 pb-36 space-y-4 animate-fadeIn">
                        {/* Clero Brand Wordmark on Top of Centered Content */}
                        <div className="flex justify-center pb-1">
                            <CleroWordmarkSvg width={96} color="#0D9488" />
                        </div>

                        {/* Request Preview Hero */}
                        <WebPaymentHeroCard
                            splitTitle={previewSplitTitle || session?.title}
                            organizerName={displayPreviewOrgName}
                            organizerPhotoUrl={resolvedOrgPhotoUrl}
                            peopleCount={previewPeopleCount ?? session?.participantUids?.length}
                            totalAmount={previewTotalAmount ?? session?.total ?? session?.amount}
                            note={previewNote || session?.note}
                            createdAt={previewCreatedAt || session?.createdAt}
                            receiptThumbnailURL={previewReceiptThumbnailURL || session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl}
                            receiptImageURL={previewReceiptImageURL || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl}
                            merchantLogoUrl={previewMerchantLogoUrl || session?.merchantLogoUrl || session?.merchantLogoURL}
                            sessionIcon={previewSessionIcon || session?.sessionIcon}
                            sessionIconColor={previewSessionIconColor || session?.sessionIconColor}
                            loading={previewLoading && !previewSplitTitle}
                            onViewReceipt={() => setViewingReceipt(true)}
                        />

                        {/* Signed In User Card */}
                        <div className="space-y-4">
                            <div className="bg-white rounded-[2rem] p-4 text-center">
                                <span className="text-xs font-bold text-slate-500 block mb-0.5">Signed in as</span>
                                <h3 className="text-lg font-black text-slate-900">
                                    {payerProfile?.displayName || payerProfile?.name || (payerProfile?.firstName ? `${payerProfile.firstName} ${payerProfile.lastName || ''}`.trim() : '') || formatPhoneForDisplay((authSessionUser || auth.currentUser)?.phoneNumber || '') || 'Verified User'}
                                </h3>
                                {(payerProfile?.displayName || payerProfile?.name || payerProfile?.firstName) && (authSessionUser || auth.currentUser)?.phoneNumber && (
                                    <p className="text-xs font-medium text-slate-400 mt-0.5">
                                        {formatPhoneForDisplay((authSessionUser || auth.currentUser)?.phoneNumber || '')}
                                    </p>
                                )}
                            </div>

                            {errorMessage && !errorMessage.toLowerCase().includes('sign in') && !errorMessage.toLowerCase().includes('view this bill') && (
                                <div className="p-3.5 rounded-2xl bg-red-50/90 border border-red-200/60 text-xs font-bold text-red-600 flex items-center justify-center gap-2 leading-relaxed">
                                    <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                                    <span>{errorMessage}</span>
                                </div>
                            )}

                            <div className="text-center">
                                <button
                                    onClick={() => { void handlePayerLogout(); }}
                                    disabled={loggingOut}
                                    className="text-xs font-bold text-slate-500 hover:text-slate-900 underline underline-offset-4 transition-colors"
                                >
                                    {loggingOut ? 'Logging out…' : 'Not you? Sign in with another number'}
                                </button>
                            </div>
                        </div>

                        {/* OR Divider */}
                        <div className="relative flex items-center justify-center py-1">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200/70" />
                            </div>
                            <div className="relative bg-[#F8FAFC] px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none">
                                OR
                            </div>
                        </div>

                        {/* App Download Card */}
                        <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none">
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Pay with Clero App</p>
                                <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                                    Direct bank payments and instant bill splitting on mobile.
                                </p>
                            </div>
                            <div className="flex items-center justify-center pt-0.5">
                                <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                            </div>
                        </div>

                        {/* Trust Footer */}
                        <div className="pt-2 pb-1 text-center select-none space-y-2">
                            <div className="flex items-center justify-center gap-2 text-[11px] font-medium text-slate-400">
                                <span>Powered by</span>
                                <img src="/plaid_logo.png" alt="Plaid" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                                <img src="/astra_logo.png" alt="Astra" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium text-center leading-relaxed">
                                By continuing, you agree to Clero's <a href="/terms" className="text-teal-600 font-semibold hover:underline">Terms</a> and <a href="/privacy" className="text-teal-600 font-semibold hover:underline">Privacy</a>
                            </p>
                        </div>
                    </div>

                    {/* Sticky Bottom Footer */}
                    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F8FAFC]/95 backdrop-blur-md border-t border-slate-100/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                        <div className="max-w-md mx-auto space-y-2">
                            <button
                                onClick={() => setAuthStep('phone')}
                                disabled={loading}
                                className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-none"
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                        <span>Opening Request...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span>Review & Pay</span>
                                        <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                                    </div>
                                )}
                            </button>
                            <p className="text-xs text-slate-500 font-medium text-center">
                                Already have Clero?{' '}
                                <a href={appDeepLink} className="text-teal-600 font-bold hover:underline">
                                    Open in App
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* Top-Level Receipt Modal Fallback */}
                    <WebReceiptViewerModal
                        visible={viewingReceipt}
                        imageUrl={previewReceiptImageURL || previewReceiptThumbnailURL || session?.receiptImageURL || session?.receiptThumbnailURL || ''}
                        onClose={() => setViewingReceipt(false)}
                    />

                    <div id="recaptcha-container" className="hidden"></div>
                </div>
            );
        }

        // Unauthenticated slide steps
        return (
            <div className="min-h-[100dvh] bg-[#F8FAFC] text-black font-sans selection:bg-teal-100 selection:text-teal-900 overflow-x-hidden relative flex flex-col items-center">
                {/* Top Navigation */}
                <PayerTopNav
                    currentUser={null}
                    currentUserProfile={payerProfile}
                    onLogout={() => { void handlePayerLogout(); }}
                    loggingOut={loggingOut}
                    onBack={authStep === 'phone' ? () => setAuthStep('preview') : authStep === 'otp' ? () => handleResetOtp() : undefined}
                    appDeepLink={appDeepLink}
                />

                {/* Main Center Container */}
                <div className="flex-1 w-full max-w-md px-4 sm:px-6 z-10 flex flex-col justify-center my-auto pt-20 pb-36 space-y-4 animate-fadeIn">
                    {/* Clero Brand Wordmark on Top of Centered Content across all auth steps */}
                    <div className="flex justify-center pb-1">
                        <CleroWordmarkSvg width={96} color="#0D9488" />
                    </div>

                    {authStep === 'preview' && (
                        <div className="space-y-4 animate-in fade-in duration-200">
                            <WebPaymentHeroCard
                                splitTitle={previewSplitTitle || session?.title}
                                organizerName={displayPreviewOrgName}
                                organizerPhotoUrl={resolvedOrgPhotoUrl}
                                peopleCount={previewPeopleCount ?? session?.participantUids?.length}
                                totalAmount={previewTotalAmount ?? session?.total ?? session?.amount}
                                note={previewNote || session?.note}
                                createdAt={previewCreatedAt || session?.createdAt}
                                receiptThumbnailURL={previewReceiptThumbnailURL || session?.receiptThumbnailURL || session?.receiptThumbnailUrl || session?.receipt?.thumbnailUrl || (session as any)?.aiData?.thumbnailDataUrl}
                                receiptImageURL={previewReceiptImageURL || session?.receiptImageURL || session?.receiptImageUrl || session?.receipt?.imageUrl}
                                merchantLogoUrl={previewMerchantLogoUrl || session?.merchantLogoUrl || session?.merchantLogoURL}
                                sessionIcon={previewSessionIcon || session?.sessionIcon}
                                sessionIconColor={previewSessionIconColor || session?.sessionIconColor}
                                loading={previewLoading && !previewSplitTitle}
                                onViewReceipt={() => setViewingReceipt(true)}
                            />

                            {/* OR Divider */}
                            <div className="relative flex items-center justify-center py-1">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-200/70" />
                                </div>
                                <div className="relative bg-[#F8FAFC] px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none">
                                    OR
                                </div>
                            </div>

                            {/* App Download Card */}
                            <div className="rounded-[2rem] bg-white p-5 text-center space-y-3 shadow-none">
                                <div className="space-y-1">
                                    <p className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">Pay with Clero App</p>
                                    <p className="text-[11px] sm:text-xs text-slate-500 font-medium leading-relaxed">
                                        Direct bank payments and instant bill splitting on mobile.
                                    </p>
                                </div>
                                <div className="flex items-center justify-center pt-0.5">
                                    <AppStoreBadges badgeHeightClass="h-8 sm:h-9" />
                                </div>
                            </div>

                            {/* Trust Footer */}
                            <div className="pt-2 pb-1 text-center select-none space-y-2">
                                <div className="flex items-center justify-center gap-2 text-[11px] font-medium text-slate-400">
                                    <span>Powered by</span>
                                    <img src="/plaid_logo.png" alt="Plaid" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                                    <img src="/astra_logo.png" alt="Astra" className="h-3 object-contain opacity-50 grayscale hover:grayscale-0 transition-all" />
                                </div>
                                <p className="text-[10px] text-slate-400 font-medium text-center leading-relaxed">
                                    By continuing, you agree to Clero's <a href="/terms" className="text-teal-600 font-semibold hover:underline">Terms</a> and <a href="/privacy" className="text-teal-600 font-semibold hover:underline">Privacy</a>
                                </p>
                            </div>
                        </div>
                    )}

                    {authStep === 'phone' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-200">
                            <div className="rounded-[2.25rem] bg-white p-6 sm:p-8 space-y-6 shadow-none">
                                <form onSubmit={(e) => { e.preventDefault(); void sendPayerOtp(); }} className="space-y-6">
                                    <div className="text-center space-y-1">
                                        <h3 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                            Enter your phone
                                        </h3>
                                        <p className="text-xs sm:text-sm text-slate-500 font-semibold">
                                            Review and pay your share of this request
                                        </p>
                                    </div>

                                    <PhoneInput
                                        required
                                        value={payerOtpPhone}
                                        onChange={(val) => setPayerOtpPhone(val)}
                                        disabled={payerOtpBusy}
                                        label="Phone number"
                                        autoFocus
                                    />

                                    {errorMessage && !errorMessage.toLowerCase().includes('sign in') && !errorMessage.toLowerCase().includes('view this bill') && (
                                        <div className="p-3.5 rounded-2xl bg-red-50/90 border border-red-200/60 text-xs font-bold text-red-600 flex items-center justify-center gap-2 leading-relaxed">
                                            <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                                            <span>{errorMessage}</span>
                                        </div>
                                    )}

                                    <div className="space-y-1 text-center">
                                        <p className="text-xs text-slate-500 font-bold">We'll text you a 6-digit code to continue.</p>
                                        <p className="text-[11px] text-slate-400 font-medium">Your number is only used to verify your request access.</p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {authStep === 'otp' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-200">
                            <div className="rounded-[2.25rem] bg-white p-6 sm:p-8 space-y-6 shadow-none">
                                <form onSubmit={(e) => { e.preventDefault(); void verifyPayerOtp(); }} className="space-y-6">
                                    <div className="text-center space-y-1">
                                        <h3 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                            Verify your phone
                                        </h3>
                                        <p className="text-xs sm:text-sm text-slate-500 font-semibold">
                                            Code sent to {formatDisplayPhone(payerOtpPhone)}{' '}
                                            <button
                                                type="button"
                                                onClick={handleResetOtp}
                                                className="text-teal-600 font-bold hover:underline inline-flex items-center gap-0.5 ml-1"
                                            >
                                                Edit
                                            </button>
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <div
                                            className="relative flex items-center justify-center cursor-text"
                                            onClick={() => otpInputRef.current?.focus()}
                                        >
                                            {/* Invisible native input for perfect iOS/Android/Web keyboard, SMS auto-fill, and paste */}
                                            <input
                                                ref={otpInputRef}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength={6}
                                                autoComplete="one-time-code"
                                                autoFocus
                                                value={payerOtpCode}
                                                onChange={(e) => {
                                                    const clean = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                    if (clean !== lastFailedOtpRef.current) {
                                                        lastFailedOtpRef.current = '';
                                                    }
                                                    setPayerOtpCode(clean);
                                                    if (errorMessage) setErrorMessage(null);
                                                }}
                                                onFocus={() => setIsOtpFocused(true)}
                                                onBlur={() => setIsOtpFocused(false)}
                                                disabled={payerOtpBusy}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />

                                            {/* 6 Individual Box Slots matching Clero App AuthOtpStepCard tokens */}
                                            <div className="flex items-center justify-between gap-2 sm:gap-2.5 w-full">
                                                {[0, 1, 2, 3, 4, 5].map((index) => {
                                                    const digit = payerOtpCode[index] || '';
                                                    const isCurrent = isOtpFocused && (payerOtpCode.length === index || (index === 5 && payerOtpCode.length === 6));
                                                    const isFilled = Boolean(digit);
                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`flex-1 h-13 sm:h-14 rounded-2xl flex items-center justify-center font-black text-xl sm:text-2xl transition-all select-none border ${
                                                                isCurrent
                                                                    ? 'border-teal-600 ring-4 ring-teal-500/15 bg-white text-slate-900 scale-[1.02]'
                                                                    : isFilled
                                                                    ? 'border-slate-300 bg-white text-slate-900 shadow-xs'
                                                                    : 'border-slate-200/80 bg-slate-50 text-slate-300'
                                                            }`}
                                                        >
                                                            {digit ? (
                                                                <span>{digit}</span>
                                                            ) : isCurrent ? (
                                                                <div className="w-0.5 h-6 bg-teal-600 animate-pulse" />
                                                            ) : (
                                                                <span className="text-slate-300 text-lg font-bold">•</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between px-1">
                                            <span className="text-xs font-semibold text-slate-400">Didn't get the code?</span>
                                            <button
                                                type="button"
                                                disabled={!canResend || payerOtpBusy}
                                                onClick={() => void sendPayerOtp(true)}
                                                className="text-xs font-bold text-teal-600 hover:text-teal-700 disabled:text-slate-400 transition-colors"
                                            >
                                                {canResend ? 'Resend code' : `Resend in ${resendCountdown}s`}
                                            </button>
                                        </div>
                                    </div>

                                    {errorMessage && !errorMessage.toLowerCase().includes('sign in') && !errorMessage.toLowerCase().includes('view this bill') && (
                                        <div className="p-3.5 rounded-2xl bg-red-50/90 border border-red-200/60 text-xs font-bold text-red-600 flex items-center justify-center gap-2 leading-relaxed">
                                            <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                                            <span>{errorMessage}</span>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sticky Bottom Footer */}
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#F8FAFC]/95 backdrop-blur-md border-t border-slate-100/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div className="max-w-md mx-auto space-y-2">
                        {authStep === 'preview' && (
                            <button
                                onClick={() => setAuthStep('phone')}
                                className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight hover:bg-slate-800 transition-all active:scale-[0.98] flex items-center justify-center gap-2 group shadow-none"
                            >
                                <span>Review & Pay</span>
                                <ArrowRight className="w-4 h-4 stroke-[2.5] transition-transform group-hover:translate-x-0.5" />
                            </button>
                        )}

                        {authStep === 'phone' && (
                            <button
                                type="button"
                                onClick={() => void sendPayerOtp()}
                                disabled={payerOtpBusy || payerOtpPhone.replace(/\D/g, '').length < 10}
                                className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-none"
                            >
                                {payerOtpBusy ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                        <span>Sending Code...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span>Send Verification Code</span>
                                        <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                                    </div>
                                )}
                            </button>
                        )}

                        {authStep === 'otp' && (
                            <button
                                type="button"
                                onClick={() => void verifyPayerOtp()}
                                disabled={payerOtpBusy || payerOtpCode.trim().length !== 6}
                                className="w-full py-4 bg-[#07111B] text-white rounded-full font-bold text-sm tracking-tight hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-none"
                            >
                                {payerOtpBusy ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                        <span>Verifying Code...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span>Verify & Continue</span>
                                        <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                                    </div>
                                )}
                            </button>
                        )}

                        <p className="text-xs text-slate-500 font-medium text-center">
                            Already have Clero?{' '}
                            <a href={appDeepLink} className="text-teal-600 font-bold hover:underline">
                                Open in App
                            </a>
                        </p>
                    </div>
                </div>

                {/* Top-Level Receipt Modal Fallback */}
                <WebReceiptViewerModal
                    visible={viewingReceipt}
                    imageUrl={previewReceiptImageURL || previewReceiptThumbnailURL || session?.receiptImageURL || session?.receiptThumbnailURL || ''}
                    onClose={() => setViewingReceipt(false)}
                />

                <div id="recaptcha-container" className="hidden"></div>
            </div>
        );
    }

    const discountsAmount = Number((session as any).discounts || (session as any).receipt?.discounts || 0);
    const deductionsAmount = Number((session as any).deductions || (session as any).receipt?.deductions || 0);
    const subtotalAmount = Number(
        (session as any).totalSubtotal ??
        (session.items?.length
            ? session.items.reduce((acc: number, item: any) => acc + Number(item.amount || 0), 0)
            : (Number((session as any).amount || (session as any).totalAmount || 0)
                - Number((session as any).tax || 0)
                - Number((session as any).tip || 0)
                - Number((session as any).fees || 0)
                + discountsAmount
                + deductionsAmount))
    );
    const taxAmount = Number((session as any).tax || 0);
    const tipAmount = Number((session as any).tip || 0);
    const feesAmount = Number((session as any).fees || 0);
    const totalPurchaseAmount = Number((session as any).amount || (session as any).totalAmount || 0);
    const inviteProgress = resolveInviteProgress(invite, session);
    const hasFailedPayment = isCurrentSplitRestrictedFailedPayment || inviteProgress.hasFailedOutstanding;
    const payerAmounts = resolveFailedPayerAmounts({
        hasFailedPayment,
        totalAmount: inviteProgress.totalAmount,
        paidAmount: inviteProgress.paidAmount,
        remainingBaseAmount: inviteProgress.remainingBaseAmount,
        remainingFeeAmount: inviteProgress.remainingFeeAmount,
        failedBaseAmount: inviteProgress.failedBaseAmount,
        failedFeeAmount: inviteProgress.failedFeeAmount,
        failedAmount: inviteProgress.failedAmount,
    });
    const amountPaidSoFar = payerAmounts.paidAmount;
    const settledAmountPaidSoFar = inviteProgress.settledPaidAmount;
    const processingAmountSoFar = inviteProgress.processingAmount;
    const failedAmountSoFar = inviteProgress.failedAmount;
    const isGroupSettlementSession = String((session as any)?.splitSource || '').trim().toLowerCase() === 'group_settlement';
    const payerHasPremiumAccess = cleroPlusEnabled && hasActiveCleroPlusAccess(payerProfile?.subscription);
    const waiveParticipantFee = isGroupSettlementSession && payerHasPremiumAccess;

    const organizerNameFromParticipants = organizerPhoneNorm ? participants[organizerPhoneNorm]?.name : '';
    let organizerNameFromGatewayCache = '';
    try {
        const cachedGatewayMetaRaw = sessionStorage.getItem(`gateway_meta_${session.id}`);
        if (cachedGatewayMetaRaw) {
            const cachedGatewayMeta = JSON.parse(cachedGatewayMetaRaw);
            organizerNameFromGatewayCache = String(cachedGatewayMeta?.organizerName || '').trim();
        }
    } catch {
        // ignore sessionStorage parse failures
    }
    const rawOrganizerDisplayName = resolveParticipantDisplayName({
        phone: session.organizerPhone,
        viewerPhone: invite?.phone,
        localName: getLocalParticipantName(session.organizerPhone),
        canonicalName: organizerNameFromParticipants,
        persistedName: session.organizerName || organizerNameFromGatewayCache,
        fallbackLabel: 'Organizer',
    });
    const organizerDisplayName = formatOrganizerFirstName(rawOrganizerDisplayName) || 'Organizer';
    const organizerFirstName = organizerDisplayName;

    const isViewerCovered = Boolean(
        invite?.isCovered
        || invite?.isCoveredPaid
        || (myPhoneNorm && session?.coverageMap?.[myPhoneNorm])
        || (myPhoneNorm && session?.coveredByMap?.[myPhoneNorm])
    );
    const viewerCoveredByName = (() => {
        const myNorm = myPhoneNorm || (invite?.phone ? normalizePhone(invite.phone) : '');
        let rawCoveredBy = invite?.coveredByPhone || invite?.coveredBy || (myNorm && session?.coverageMap?.[myNorm]?.coveredByPhone) || (myNorm && session?.coverageMap?.[myNorm]?.coveredBy) || (myNorm && session?.coveredByMap?.[myNorm]);
        const covPhone = normalizePhone(String(rawCoveredBy || ''));
        const isCoveredByOrganizer = covPhone === 'organizer' || rawCoveredBy === 'organizer' || (organizerPhoneNorm && covPhone === organizerPhoneNorm);
        if (isCoveredByOrganizer) {
            return rawOrganizerDisplayName || organizerDisplayName || 'Organizer';
        }
        if (covPhone) {
            const covInv = (sessionInvites || []).find((i: any) => normalizePhone(getInvitePhone(i)) === covPhone);
            const covPersisted = getPersistedParticipantName(covPhone) || covInv?.name || covInv?.profileName;
            const covResolved = resolveParticipantDisplayName({
                phone: covPhone,
                viewerPhone: invite?.phone,
                localName: getLocalParticipantName(covPhone),
                persistedName: covPersisted,
                fallbackLabel: covPersisted || formatPhoneForDisplay(covPhone),
            });
            if (covResolved && covResolved.toLowerCase() !== 'someone' && covResolved.toLowerCase() !== 'true') {
                return covResolved;
            }
        }
        const fallbackRaw = String(session?.coverageMap?.[myNorm || '']?.coveredByName || invite?.coveredByName || '').trim();
        if (fallbackRaw && fallbackRaw.toLowerCase() !== 'true' && fallbackRaw.toLowerCase() !== 'someone') {
            return fallbackRaw;
        }
        return 'your friend';
    })();
    const isViewerCoveredPaid = Boolean(invite?.isCoveredPaid);
    const isViewerSettled = Boolean(paid || payerHeroStage === 'paid' || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || isViewerCoveredPaid);

    const remainingBaseAmount = isViewerCovered ? 0 : payerAmounts.remainingBaseAmount;
    const remainingFeeAmount = isViewerCovered ? 0 : (waiveParticipantFee ? 0 : payerAmounts.remainingFeeAmount);
    const payeeBaseAmount = remainingBaseAmount;
    const displayBaseToPay = roundMoney(payeeBaseAmount);
    const requestedPartialBaseAmount = partialPaymentEnabled
        ? roundMoney(Number(partialPaymentInput || 0))
        : remainingBaseAmount;
    const paymentQuote = resolveWebPaymentQuote({
        partialPaymentEnabled,
        requestedBaseAmount: requestedPartialBaseAmount,
        remainingBaseAmount,
        remainingFeeAmount,
        waiveFee: waiveParticipantFee,
    });
    const remainingOwedAmount = isViewerCovered ? 0 : roundMoney(remainingBaseAmount + remainingFeeAmount);
    const totalOwedAmount = roundMoney(amountPaidSoFar + remainingOwedAmount);
    const isPaymentFullyCoveredByProcessing = !paid
        && inviteProgress.isPaymentProcessing
        && remainingOwedAmount <= 0.0001
        && amountPaidSoFar > 0.0001;
    const paymentLimitSnapshot = paymentLimitsSnapshot || getPaymentLimitsSnapshot(payerProfile);
    const userAvailablePaymentLimitCents = getUserAvailablePaymentLimitCents(paymentLimitSnapshot);
    // Platform capacity queues server-side. Only payer limits block client-side.
    const maxPartialBaseAmount = getMaxBaseAmountForTotalLimit({
        baseAmountRemaining: remainingBaseAmount,
        feeAmountRemaining: remainingFeeAmount,
        totalLimitCents: userAvailablePaymentLimitCents,
        waiveFee: waiveParticipantFee,
    });
    const partialPaymentIsValid =
        (!partialPaymentEnabled) || (
            requestedPartialBaseAmount > 0
            && requestedPartialBaseAmount <= maxPartialBaseAmount + 0.0001
            && paymentQuote.baseAmount > 0
            && paymentQuote.baseAmount <= remainingBaseAmount + 0.0001
            && paymentQuote.totalAmount <= remainingOwedAmount + 0.0001
        );
    const paymentAmountForSubmit = paymentQuote.totalAmount;
    const paymentBaseAmountForSubmit = paymentQuote.baseAmount;
    const paymentAmountCentsForSubmit = Math.round(paymentAmountForSubmit * 100);
    const paymentBaseAmountCentsForSubmit = Math.round(paymentBaseAmountForSubmit * 100);
    const exceedsAvailablePaymentLimit =
        !paymentsAreDisabled
        && (
            (userAvailablePaymentLimitCents === 0 && paymentAmountCentsForSubmit > 0)
            || paymentAmountCentsForSubmit > userAvailablePaymentLimitCents
        );
    const paymentLimitLabel = formatPaymentLimitDollars(userAvailablePaymentLimitCents);
    const hasItemizedReceipt = Array.isArray(session.items) && session.items.length > 0;
    const hasReceiptBreakdown = hasItemizedReceipt && Boolean((session as any).isItemClaimingEnabled);
    const appliedMethod = String((invite as any)?.appliedPaymentMethod || (invite as any)?.appliedPaymentRail || (invite as any)?.paymentMethod || '').toLowerCase();
    const displayedAppliedMethod = paymentsAreDisabled && (paid || inviteStatus === 'settled_offline')
        ? 'venmo'
        : appliedMethod;
    const hasUsableSavedBank = Boolean(selectedPaymentBank);
    const isVenmoSettled = paymentsAreDisabled && (paid || inviteStatus === 'settled_offline');
    const toDateValue = (value: any): Date | null => {
        if (!value) return null;
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        if (typeof value?.toDate === 'function') {
            try {
                const next = value.toDate();
                return next instanceof Date && !Number.isNaN(next.getTime()) ? next : null;
            } catch {
                return null;
            }
        }
        const seconds = Number(value?.seconds ?? value?._seconds);
        if (Number.isFinite(seconds)) {
            const nanoseconds = Number(value?.nanoseconds ?? value?._nanoseconds ?? 0);
            const millis = (seconds * 1000) + Math.floor(nanoseconds / 1_000_000);
            const next = new Date(millis);
            return Number.isNaN(next.getTime()) ? null : next;
        }
        if (typeof value === 'number') {
            const next = new Date(value);
            return Number.isNaN(next.getTime()) ? null : next;
        }
        const parsed = Date.parse(String(value));
        if (Number.isNaN(parsed)) return null;
        const next = new Date(parsed);
        return Number.isNaN(next.getTime()) ? null : next;
    };
    const formatActivityRelativeTime = (value: any): string => {
        if (!value) return '';
        const d = toDateValue(value);
        if (!d) return '';
        const timestampMs = d.getTime();
        if (!timestampMs || Number.isNaN(timestampMs)) return '';

        const now = Date.now();
        const diffMs = Math.max(0, now - timestampMs);
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHours = Math.floor(diffMin / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;

        const isCurrentYear = d.getFullYear() === new Date().getFullYear();
        return d.toLocaleDateString('en-US', isCurrentYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const purchaseMerchantName = String(session.site || session.title || 'Clero Split').trim() || 'Clero Split';
    const purchasedOnLabel = formatDateOnly((session as any).purchaseDate || null);
    const createdOnLabel = formatDateOnly(session.createdAt || session.date || null);
    const expectedShareAmount = Math.max(
        0,
        totalOwedAmount > 0.0001
            ? totalOwedAmount
            : (Number((invite as any).amount || 0) - Number((invite as any).payeeFee || 0))
    );
    const viewerCoveredAmount = (() => {
        if (!isViewerCovered) return 0;
        let ownItemsTotal = 0;
        (session?.items || []).forEach((item: any) => {
            const sh = getEffectiveItemShares(item) || {};
            const myNorm = myPhoneNorm || (invite?.phone ? normalizePhone(invite.phone) : '');
            let fraction = 0;
            if (isOrganizerViewer) {
                if (sh['organizer']) fraction = Number(sh['organizer'].fraction || 0);
                else if (organizerPhoneNorm && sh[organizerPhoneNorm]) fraction = Number(sh[organizerPhoneNorm].fraction || 0);
            } else if (myNorm) {
                if (sh[myNorm]) fraction = Number(sh[myNorm].fraction || 0);
                else {
                    const matchedKey = Object.keys(sh).find((k) => normalizePhone(k) === myNorm);
                    if (matchedKey && sh[matchedKey]) fraction = Number(sh[matchedKey].fraction || 0);
                }
            }
            if (fraction > 0) {
                ownItemsTotal += Number(item.amount || 0) * fraction;
            }
        });

        const totalItemAmount = (session?.items || []).reduce((sum: number, it: any) => sum + Number(it?.amount || 0), 0);
        const tax = Number(session?.tax || 0);
        const tip = Number(session?.tip || (session as any)?.tipAmount || 0);
        const fees = Number(session?.fees || 0);
        const discounts = Number(session?.discounts || 0);
        const deductions = Number(session?.deductions || 0);
        const netExtras = tax + tip + fees - discounts - deductions;
        const ratio = totalItemAmount > 0 ? (ownItemsTotal / totalItemAmount) : 0;
        const proportionalExtras = roundMoney(netExtras * ratio);

        const inviteFee = Number(
            invite?.payeeFeeAmount
            || ((invite as any)?.feeAmountOwedCents ? Number((invite as any).feeAmountOwedCents) / 100 : 0)
        );
        const platformFee = inviteFee > 0 ? roundMoney(inviteFee) : 0;
        const totalCovered = Math.max(0, roundMoney(ownItemsTotal + proportionalExtras + platformFee));

        if (totalCovered > 0.0001) return totalCovered;
        const myNorm = myPhoneNorm || (invite?.phone ? normalizePhone(invite.phone) : '');
        const candidateCovered = Number(
            invite?.coveredBaseAmount
            ?? invite?.coveredAmount
            ?? (session as any)?.coveredBaseAmount
            ?? (myNorm ? session?.coverageMap?.[myNorm]?.coveredBaseAmount : undefined)
            ?? (myNorm ? session?.coverageMap?.[myNorm]?.amount : undefined)
            ?? (myNorm ? session?.coveredByMap?.[myNorm]?.amount : undefined)
            ?? (invite as any)?.amount
            ?? 0
        );
        return roundMoney(candidateCovered);
    })();
    const summaryBankAccount = selectedPaymentBank || eligiblePaymentBanks[0] || null;
    const failedPaymentRelinkAccount = plaidAccounts.find(doesPlaidAccountRequireRelink) || null;
    const shouldShowSummaryPaymentMethod = !isOrganizerViewer && Boolean(displayedAppliedMethod);
    const paymentSheetOrganizerPhoto = resolveParticipantPhoto(organizerPhoneNorm || '');
    const paymentSheetMerchantLogoUrl = String(session.receiptImageURL || session.merchantLogoUrl || '').trim();

    const viewerRealName = String(payerProfile?.displayName || invite?.name || (session as any)?.viewerName || '').trim();
    const viewerInitials = viewerRealName ? getInitials(viewerRealName) : (auth.currentUser?.phoneNumber ? auth.currentUser.phoneNumber.slice(-2) : 'U');

    const participantRowsForSheet: WebParticipantRow[] = (() => {
        const organizerAmount = resolveOrganizerParticipantAmount(session);

        const uniqueInvitesByPhone = new Map<string, any>();
        for (const inv of (sessionInvites || [])) {
            const rawP = getInvitePhone(inv);
            const phoneNorm = normalizePhone(rawP);
            if (!phoneNorm) continue;
            if (!uniqueInvitesByPhone.has(phoneNorm)) {
                uniqueInvitesByPhone.set(phoneNorm, inv);
            }
        }

        const inviteRows = Array.from(uniqueInvitesByPhone.entries()).map(([phoneNorm, inv]) => {
            const isMe = !isOrganizerViewer && Boolean(myPhoneNorm && phoneNorm === myPhoneNorm);
            const invName = getInviteName(inv);
            const resolvedPersistedName = getPersistedParticipantName(phoneNorm) || invite?.name || invName;
            const resolvedDisplayName = resolveParticipantDisplayName({
                phone: phoneNorm,
                viewerPhone: invite?.phone,
                localName: getLocalParticipantName(phoneNorm),
                persistedName: resolvedPersistedName,
                fallbackLabel: resolvedPersistedName || (isMe ? 'You' : formatPhoneForDisplay(phoneNorm)),
            });
            const displayName = resolvePayerParticipantDisplayName({
                isViewer: isMe,
                resolvedName: resolvedDisplayName,
            });

            // Item shares claimed by this participant
            const claimedItems: Array<{ name: string; amount: number; fraction: number }> = [];
            const renderedItems = (session?.items || []) as any[];
            for (const item of renderedItems) {
                const effectiveShares = getEffectiveItemShares(item);
                const share = effectiveShares?.[phoneNorm] || (isMe ? effectiveShares?.['payer'] : null);
                if (share && Number(share.fraction) > 0.0001) {
                    claimedItems.push({
                        name: item.name || 'Item',
                        amount: Number(item.amount || 0) * Number(share.fraction),
                        fraction: Number(share.fraction),
                    });
                }
            }

            const rawAmount = resolveInviteTotalAmount(inv);
            const payeeFee = resolveInvitePayeeFee(inv, session);
            const isParticipantCovered = Boolean(
                inv.isCovered
                || inv.isCoveredPaid
                || (phoneNorm && session?.coverageMap?.[phoneNorm])
                || (phoneNorm && session?.coveredByMap?.[phoneNorm])
            );

            let displayAmount = Math.max(0, rawAmount - payeeFee);
            if (isParticipantCovered) {
                const explicitCovered = Number(
                    inv.coveredBaseAmount ??
                    inv.coveredAmount ??
                    (phoneNorm && session?.coverageMap?.[phoneNorm]?.coveredBaseAmount) ??
                    (phoneNorm && session?.coverageMap?.[phoneNorm]?.amount) ??
                    0
                );
                if (explicitCovered > 0.0001) {
                    displayAmount = explicitCovered;
                } else if (claimedItems.length > 0) {
                    const itemSum = claimedItems.reduce((acc, it) => acc + Number(it.amount || 0), 0);
                    const tax = Number(session?.tax || 0);
                    const tip = Number(session?.tip || 0);
                    const totalSub = Number(session?.subtotal || (session?.items || []).reduce((acc: number, it: any) => acc + Number(it?.amount || it?.price || 0), 0) || 0);
                    const ratio = totalSub > 0 ? (itemSum / totalSub) : 0;
                    displayAmount = itemSum + (tax + tip) * ratio;
                }
            }

            const rawCoveredBy = inv.coveredByPhone || inv.coveredBy || (phoneNorm && session?.coverageMap?.[phoneNorm]?.coveredByPhone) || (phoneNorm && session?.coverageMap?.[phoneNorm]?.coveredBy) || (phoneNorm && session?.coveredByMap?.[phoneNorm]);
            const covPhone = normalizePhone(String(rawCoveredBy || ''));
            const isCoveredByOrganizer = covPhone === 'organizer' || rawCoveredBy === 'organizer' || (organizerPhoneNorm && covPhone === organizerPhoneNorm);
            const isCoveredByMe = Boolean(myPhoneNorm && covPhone === myPhoneNorm);

            let participantCoveredByName = '';
            if (isCoveredByOrganizer) {
                participantCoveredByName = isOrganizerViewer ? 'You' : (rawOrganizerDisplayName || organizerDisplayName || 'Organizer');
            } else if (isCoveredByMe) {
                participantCoveredByName = 'You';
            } else if (covPhone) {
                const covInv = (sessionInvites || []).find((i: any) => normalizePhone(getInvitePhone(i)) === covPhone);
                const covPersisted = getPersistedParticipantName(covPhone) || covInv?.name || covInv?.profileName;
                participantCoveredByName = resolveParticipantDisplayName({
                    phone: covPhone,
                    viewerPhone: invite?.phone,
                    localName: getLocalParticipantName(covPhone),
                    persistedName: covPersisted,
                    fallbackLabel: covPersisted || formatPhoneForDisplay(covPhone),
                });
            }
            if (!participantCoveredByName || participantCoveredByName.toLowerCase() === 'true' || participantCoveredByName.toLowerCase() === 'someone') {
                const fallbackCovName = String(session?.coverageMap?.[phoneNorm]?.coveredByName || inv.coveredByName || '').trim();
                if (fallbackCovName && fallbackCovName.toLowerCase() !== 'true' && fallbackCovName.toLowerCase() !== 'someone') {
                    participantCoveredByName = fallbackCovName;
                }
            }
            if (!participantCoveredByName) {
                participantCoveredByName = 'friend';
            }

            const coveringNames: string[] = (sessionInvites || [])
                .filter((otherInv: any) => {
                    const otherPhone = normalizePhone(getInvitePhone(otherInv));
                    if (!otherPhone || otherPhone === phoneNorm) return false;
                    const otherCovBy = otherInv.coveredByPhone || otherInv.coveredBy || (otherPhone && session?.coverageMap?.[otherPhone]?.coveredByPhone) || (otherPhone && session?.coverageMap?.[otherPhone]?.coveredBy) || (otherPhone && session?.coveredByMap?.[otherPhone]);
                    return normalizePhone(String(otherCovBy || '')) === phoneNorm;
                })
                .map((otherInv: any) => {
                    const otherPhone = normalizePhone(getInvitePhone(otherInv));
                    const otherPersisted = getPersistedParticipantName(otherPhone) || otherInv.name || otherInv.profileName;
                    return resolveParticipantDisplayName({
                        phone: otherPhone,
                        viewerPhone: invite?.phone,
                        localName: getLocalParticipantName(otherPhone),
                        persistedName: otherPersisted,
                        fallbackLabel: otherPersisted || formatPhoneForDisplay(otherPhone),
                    });
                })
                .filter(Boolean);

            const isParticipantCoveredPaid = Boolean(inv.isCoveredPaid);

            return {
                id: `inv-${phoneNorm}`,
                phone: phoneNorm,
                displayName,
                amount: Number(displayAmount.toFixed(2)),
                isMe,
                isOrganizer: false,
                status: String(inv.status || 'invited'),
                photoURL: resolveParticipantPhoto(phoneNorm, inv),
                isCovered: isParticipantCovered,
                isCoveredPaid: isParticipantCoveredPaid,
                coveredByName: participantCoveredByName,
                coveringNames,
                claimedItems,
            };
        });

        const organizerCoveringNames: string[] = organizerPhoneNorm
            ? (sessionInvites || [])
                .filter((otherInv: any) => {
                    const otherPhone = normalizePhone(getInvitePhone(otherInv));
                    if (!otherPhone || otherPhone === organizerPhoneNorm) return false;
                    const otherCovBy = otherInv.coveredByPhone || otherInv.coveredBy || (otherPhone && session?.coverageMap?.[otherPhone]?.coveredByPhone) || (otherPhone && session?.coverageMap?.[otherPhone]?.coveredBy) || (otherPhone && session?.coveredByMap?.[otherPhone]);
                    const normCov = normalizePhone(String(otherCovBy || ''));
                    return normCov === 'organizer' || otherCovBy === 'organizer' || (organizerPhoneNorm && normCov === organizerPhoneNorm);
                })
                .map((otherInv: any) => {
                    const otherPhone = normalizePhone(getInvitePhone(otherInv));
                    const otherPersisted = getPersistedParticipantName(otherPhone) || otherInv.name || otherInv.profileName;
                    return resolveParticipantDisplayName({
                        phone: otherPhone,
                        viewerPhone: invite?.phone,
                        localName: getLocalParticipantName(otherPhone),
                        persistedName: otherPersisted,
                        fallbackLabel: otherPersisted || formatPhoneForDisplay(otherPhone),
                    });
                })
                .filter(Boolean)
            : [];

        const organizerRows = shouldShowOrganizerParticipant(organizerAmount) || isOrganizerViewer
            ? [{
                id: 'organizer-row',
                phone: organizerPhoneNorm || '',
                displayName: isOrganizerViewer ? 'You (Organizer)' : (rawOrganizerDisplayName || organizerDisplayName || 'Organizer'),
                amount: organizerAmount,
                isMe: isOrganizerViewer,
                isOrganizer: true,
                status: 'organizer',
                photoURL: resolveParticipantPhoto(organizerPhoneNorm || ''),
                coveringNames: organizerCoveringNames,
                claimedItems: (() => {
                    const claimed: Array<{ name: string; amount: number; fraction: number }> = [];
                    const renderedItems = (session?.items || []) as any[];
                    for (const item of renderedItems) {
                        const effectiveShares = getEffectiveItemShares(item);
                        const share = effectiveShares?.['organizer'] || (organizerPhoneNorm ? effectiveShares?.[organizerPhoneNorm] : null);
                        if (share && Number(share.fraction) > 0.0001) {
                            claimed.push({
                                name: item.name || 'Item',
                                amount: Number(item.amount || 0) * Number(share.fraction),
                                fraction: Number(share.fraction),
                            });
                        }
                    }
                    return claimed;
                })(),
            }]
            : [];

        return [
            ...organizerRows,
            ...inviteRows,
        ];
    })();

    const viewerCoveringPersonsList: { phone: string; name: string; photoURL?: string }[] = (() => {
        const myNorm = normalizePhone(String(invite?.phone || auth.currentUser?.phoneNumber || ''));
        if (!myNorm) return [];

        const coverageMap = session?.coverageMap || {};
        const coveredByMap = session?.coveredByMap || {};
        const list: { phone: string; name: string; photoURL?: string }[] = [];

        Object.entries(coverageMap).forEach(([phone, rec]: [string, any]) => {
            const normCovBy = normalizePhone(rec?.coveredByPhone || rec?.coveredBy || '');
            if (normCovBy === myNorm) {
                const normCovPhone = normalizePhone(phone) || phone;
                const covPerson = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === normCovPhone);
                const covName = covPerson?.displayName || participants[normCovPhone]?.name || session?.participantsByPhone?.[normCovPhone]?.name || rec?.coveredPersonName || 'Friend';
                const covPhoto = covPerson?.photoURL || participants[normCovPhone]?.photoURL || session?.participantsByPhone?.[normCovPhone]?.photoURL;
                list.push({ phone: normCovPhone, name: covName, photoURL: covPhoto });
            }
        });

        if (list.length === 0 && coveredByMap) {
            Object.entries(coveredByMap).forEach(([phone, byPhone]: [string, any]) => {
                if (normalizePhone(String(byPhone)) === myNorm) {
                    const normCovPhone = normalizePhone(phone) || phone;
                    const covPerson = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === normCovPhone);
                    const covName = covPerson?.displayName || participants[normCovPhone]?.name || session?.participantsByPhone?.[normCovPhone]?.name || 'Friend';
                    const covPhoto = covPerson?.photoURL || participants[normCovPhone]?.photoURL || session?.participantsByPhone?.[normCovPhone]?.photoURL;
                    list.push({ phone: normCovPhone, name: covName, photoURL: covPhoto });
                }
            });
        }

        return list;
    })();

    const participantOrderMap = buildParticipantOrderMap({
        session,
        invites: sessionInvites,
        participants: participantRowsForSheet,
    });

    return (
        <div className={`min-h-[100dvh] font-sans text-gray-900 bg-[#F8FAFC] bg-[#F8FAFC] relative overflow-x-hidden ${!paid ? 'pb-64' : 'pb-24'}`}>
            <div className="mesh-gradient" />

            <div className="relative z-10">
                {isFounderPreview && (
                    <div className="sticky top-0 z-[70] border-b border-amber-200 bg-amber-50/95 backdrop-blur">
                        <div className="mx-auto flex max-w-md flex-col gap-2 px-6 py-3 text-[12px] text-amber-950">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                    <p className="font-bold uppercase tracking-[0.18em] text-amber-700">Founder preview</p>
                                    <p className="mt-1 text-[12px] font-medium text-amber-900">
                                        Founder payer session from admin. Actions are live and logged to the audit trail.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={exitFounderPreview}
                                    className="inline-flex shrink-0 items-center justify-center rounded-full border border-amber-300 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-900 transition hover:border-amber-400 hover:bg-amber-100"
                                >
                                    Back to admin
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sticky SubHeader (TabSubHeader Parity) */}
                {(() => {
                    const splitDateMs = getItemHistoryTimestampMs(session?.createdAt ?? session?.date, Date.now());
                    const formattedHeaderDate = new Date(splitDateMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const headerTopClass = isFounderPreview ? 'top-[73px]' : 'top-0';
                    const userPhone = auth.currentUser?.phoneNumber ? formatPhoneForDisplay(auth.currentUser.phoneNumber) : 'Verified User';
                    const userDisplayName = payerProfile?.displayName || payerProfile?.name || (payerProfile?.firstName ? `${payerProfile.firstName} ${payerProfile.lastName || ''}`.trim() : '') || auth.currentUser?.displayName || userPhone;
                    const userPhotoUrl = payerProfile?.photoURL || payerProfile?.photoUrl || auth.currentUser?.photoURL || null;
                    const userInitial = userDisplayName ? getInitials(userDisplayName) : (auth.currentUser?.phoneNumber ? auth.currentUser.phoneNumber.slice(-2) : 'U');

                    const headerSubtitle = session?.groupName
                        ? `${session.groupName} • Created ${formattedHeaderDate}`
                        : `Created ${formattedHeaderDate}`;

                    return (
                        <>
                            <div className={`fixed inset-x-0 ${headerTopClass} z-[60] border-b border-slate-100 bg-white/96 backdrop-blur-xl pt-[env(safe-area-inset-top,0px)]`}>
                                <div className="mx-auto max-w-md px-5 pt-3.5 pb-3 flex flex-col gap-3">
                                    {/* Row 1: Clero Wordmark Logo (left) + User Avatar (right) */}
                                    <div className="flex items-center justify-between">
                                        <div className="shrink-0 flex items-center pl-0.5">
                                            <CleroWordmarkSvg height={17} width={55} color="#0D9488" />
                                        </div>

                                        <div className="relative shrink-0" ref={headerUserMenuRef}>
                                            <button
                                                type="button"
                                                onClick={() => setIsHeaderUserMenuOpen((prev) => !prev)}
                                                className="w-8 h-8 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white overflow-hidden shadow-xs hover:scale-105 transition-all active:scale-95 cursor-pointer"
                                                aria-label={`User menu, ${userDisplayName || 'account'}`}
                                            >
                                                {userPhotoUrl ? (
                                                    <img src={userPhotoUrl} alt={userDisplayName} className="w-full h-full object-cover" />
                                                ) : (
                                                    userInitial
                                                )}
                                            </button>

                                            <WebUserMenuDropdown
                                                isOpen={isHeaderUserMenuOpen}
                                                onClose={() => setIsHeaderUserMenuOpen(false)}
                                                userDisplayName={userDisplayName}
                                                userPhone={userPhone}
                                                username={payerProfile?.username || payerProfile?.handle || ''}
                                                userPhotoUrl={userPhotoUrl}
                                                userInitial={userInitial}
                                                onLogout={() => { void handlePayerLogout(); }}
                                                loggingOut={loggingOut}
                                            />
                                        </div>
                                    </div>

                                    {/* Row 2: Merchant Info beneath on its own row */}
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                                            <PayerMerchantAvatar session={session} merchantName={purchaseMerchantName} className="w-full h-full" />
                                        </div>
                                        <div className="flex flex-col text-left min-w-0 flex-1">
                                            <h2 className="text-sm font-bold text-slate-800 leading-tight line-clamp-2 break-words" title={purchaseMerchantName}>
                                                {purchaseMerchantName}
                                            </h2>
                                            <p className="text-[11px] font-medium text-slate-600 dark:text-slate-400 mt-0.5 truncate" title={headerSubtitle}>
                                                {headerSubtitle}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={isFounderPreview ? 'h-[114px] sm:h-[114px]' : 'h-[114px]'} />
                        </>
                    );
                })()}

                <div className="max-w-md mx-auto px-6 pt-6 flex flex-col gap-6">
                    {/* App Takeover Banner (Full-Screen Modal Takeover on Mobile) */}
                    <WebAppTakeoverBanner
                        sessionToken={token}
                        appDeepLink={appDeepLink}
                        sessionId={session?.id}
                        merchantName={purchaseMerchantName}
                        amount={remainingOwedAmount}
                    />

                    {/* Amount Card (Aligned to SplitHeroCard Parity) */}
                    <div className="bg-gradient-to-br from-[#0D1D2E] to-[#12353B] text-white rounded-[2.25rem] p-7 shadow-xl shadow-gray-200/50 relative overflow-hidden">

                        <div className="relative z-10 space-y-4">
                            {payerHeroStage === 'paid' || paid || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || isViewerCoveredPaid ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-start">
                                        <div className="inline-flex items-center justify-center min-h-[24px] px-2.5 py-0.5 rounded-full text-xs font-semibold border-0 bg-[#0D9488] text-white">
                                            Paid
                                        </div>
                                    </div>

                                    <div className="space-y-1 text-left">
                                        <div className="text-4xl font-bold tracking-tight text-white flex items-baseline gap-2">
                                            {isViewerCovered ? null : <span className="font-semibold text-white">You paid </span>}
                                            <span className={`text-[#0D9488] font-black ${isViewerCovered ? 'line-through' : ''}`}>
                                                ${(isViewerCovered
                                                    ? (viewerCoveredAmount > 0.009 ? viewerCoveredAmount : expectedShareAmount)
                                                    : (amountPaidSoFar || lastPaidAmount || remainingOwedAmount || Number(invite?.amount || 0))
                                                ).toFixed(2)}
                                            </span>
                                        </div>
                                        <p className="text-sm font-semibold text-white/80 truncate">
                                            {isViewerCovered ? `${viewerCoveredByName} paid for your items` : `To ${rawOrganizerDisplayName || organizerDisplayName || 'Organizer'}`}
                                        </p>
                                        {viewerCoveringPersonsList.length > 0 && (
                                            <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm mt-1.5">
                                                <span className="text-xs font-semibold text-white/90">
                                                    Includes {viewerCoveringPersonsList.map((p) => p.name).join(', ')}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : payerHeroStage === 'counting_down' ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-white/80 tracking-normal">
                                            You owe
                                        </p>
                                        <div className="inline-flex items-center justify-center min-h-[24px] px-2.5 py-0.5 rounded-full text-xs font-semibold border-0 bg-white/15 text-white">
                                            Open
                                        </div>
                                    </div>

                                    <div className="space-y-2 text-left">
                                        <div className="text-5xl font-black tracking-tighter text-white font-mono animate-scaleUp">
                                            ${countdownDisplayAmount.toFixed(2)}
                                        </div>
                                        <p className="text-sm font-semibold text-white/80 truncate" title={`To ${rawOrganizerDisplayName || organizerDisplayName}`}>
                                            To {rawOrganizerDisplayName || organizerDisplayName}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-white/80 tracking-normal">
                                            {isOrganizerViewer ? 'Total split request' : isViewerCovered ? 'Coverage status' : isPaymentFullyCoveredByProcessing ? 'Payment processing' : 'You owe'}
                                        </p>
                                        <div className={`inline-flex items-center justify-center min-h-[24px] px-2.5 py-0.5 rounded-full text-xs font-semibold border-0 ${
                                            isOrganizerViewer
                                                ? 'bg-white/15 text-white'
                                                : isViewerCovered
                                                    ? 'bg-[#ECFDF5] text-[#047857]'
                                                    : isPaymentFullyCoveredByProcessing
                                                        ? 'bg-[#E0F2FE] text-[#0369A1]'
                                                        : (isDeclined || hasFailedPayment)
                                                            ? 'bg-[#FEF2F2] text-[#B91C1C]'
                                                            : 'bg-white/15 text-white'
                                        }`}>
                                            {isOrganizerViewer
                                                ? 'Organizer'
                                                : isViewerCovered
                                                    ? 'Covered'
                                                    : isPaymentFullyCoveredByProcessing ? 'Processing' : isDeclined ? 'Declined' : hasFailedPayment ? 'Failed' : 'Open'}
                                        </div>
                                    </div>

                                    {isOrganizerViewer ? (
                                        <div className="space-y-2">
                                            <div className="text-5xl font-black tracking-tighter text-white animate-scaleUp">
                                                ${(Number(session?.totalAmount || session?.subtotal || 0)).toFixed(2)}
                                            </div>
                                            <p className="text-sm font-semibold text-white/80 truncate">
                                                Created by you
                                            </p>
                                        </div>
                                    ) : isViewerCovered ? (
                                        <div className="space-y-1.5 py-2 text-center">
                                            <div className="text-3xl font-black tracking-tight text-white">
                                                Covered by {viewerCoveredByName}
                                            </div>
                                            <p className="text-xs text-slate-300 font-medium max-w-xs mx-auto leading-normal">
                                                Your share is covered by {viewerCoveredByName}. No payment needed from you.
                                            </p>
                                        </div>
                                    ) : remainingOwedAmount > 0 ? (
                                        <div className="space-y-2">
                                            <div className="text-5xl font-black tracking-tighter text-white animate-scaleUp">
                                                ${remainingOwedAmount.toFixed(2)}
                                            </div>
                                            <p className="text-sm font-semibold text-white/80 truncate" title={`To ${rawOrganizerDisplayName || organizerDisplayName}`}>
                                                To {rawOrganizerDisplayName || organizerDisplayName}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5 py-3 text-center">
                                            <div className="text-3xl font-black tracking-tight text-white">
                                                {isPaymentFullyCoveredByProcessing
                                                    ? 'Payment processing'
                                                    : 'Claim Items'}
                                            </div>
                                            {!isPaymentFullyCoveredByProcessing && (
                                                <p className="text-xs text-slate-300 font-medium max-w-xs mx-auto leading-normal">
                                                    Please select items below to settle your part of this split.
                                                </p>
                                            )}
                                            {isPaymentFullyCoveredByProcessing && (
                                                <p className="text-xs text-slate-300 font-medium max-w-xs mx-auto leading-normal">
                                                    Your payment is on its way.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {isOrganizerViewer ? (() => {
                                        const totalCollectedAmt = Object.values(session?.invites || {}).reduce<number>((acc, inv: any) => {
                                            const status = String(inv?.status || '').toLowerCase();
                                            const paidAmt = Number(inv?.amountPaid || inv?.amountPaidSoFar || (status === 'paid' ? inv?.amount : 0) || 0);
                                            return acc + paidAmt;
                                        }, 0);
                                        return (
                                            <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-6">
                                                <div className="text-left">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Collected</p>
                                                    <p className="text-lg font-bold text-white mt-0.5">${totalCollectedAmt.toFixed(2)}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Bill</p>
                                                    <p className="text-lg font-bold text-white mt-0.5">${Number(session?.totalAmount || session?.subtotal || 0).toFixed(2)}</p>
                                                </div>
                                            </div>
                                        );
                                    })() : (!paid && !isViewerCovered && !isPaymentFullyCoveredByProcessing && inviteStatus !== 'paid' && inviteStatus !== 'verified' && inviteStatus !== 'settled_offline' && amountPaidSoFar > 0.0001 && remainingOwedAmount > 0.0001) ? (
                                        <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-6">
                                            <div className="text-left">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Remaining</p>
                                                <p className="text-lg font-bold text-white mt-0.5">${remainingOwedAmount.toFixed(2)}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Paid</p>
                                                <p className="text-lg font-bold text-white mt-0.5">${amountPaidSoFar.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Activity Section on Top (matching SplitDashboardScreen parity) */}
                    {(!isOrganizerViewer && (payerHeroStage === 'paid' || paid || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || isViewerCoveredPaid)) && (() => {
                        const viewerPaymentEntries = (activityEntries || []).filter((e) => {
                            const type = String(e?.type || '').trim().toLowerCase();
                            const status = String(e?.status || '').trim().toLowerCase();
                            if (type === 'payment_failed_sent' || status === 'failed' || status === 'declined') return false;
                            return type === 'payment_sent';
                        });
                        const viewerActivityEntry = viewerPaymentEntries[0];

                        const parseDateToTime = (d: any): { dateObj: Date; millis: number } => {
                            const dt = toDateValue(d);
                            if (!dt) return { dateObj: new Date(0), millis: 0 };
                            const ms = isNaN(dt.getTime()) ? 0 : dt.getTime();
                            return { dateObj: dt, millis: ms };
                        };

                        const paidRows = participantRowsForSheet.filter((p) => {
                            if (p.isMe && isViewerCovered && amountPaidSoFar <= 0.0001 && !viewerActivityEntry) {
                                return false;
                            }
                            const s = String(p.status || '').toLowerCase();
                            return s === 'paid' || s === 'verified' || s === 'settled_offline' || p.isCoveredPaid || (p.isMe && (paid || payerHeroStage === 'paid'));
                        }).flatMap((p) => {
                            const isOffline = String(p.status || '').toLowerCase() === 'settled_offline';
                            if (p.isCoveredPaid) {
                                const inv = (sessionInvites || []).find((i) => normalizePhone(getInvitePhone(i)) === p.phone);
                                const rawDate = inv?.paidAt || inv?.verifiedAt || inv?.createdAt || session.date || inv?.updatedAt || session.updatedAt;
                                const { dateObj, millis } = parseDateToTime(rawDate);
                                const title = p.isMe
                                    ? (p.coveredByName ? `Covered by ${p.coveredByName}` : 'You were covered')
                                    : (p.coveredByName ? `${p.displayName} paid by ${p.coveredByName}` : `${p.displayName} was covered`);
                                return [{
                                    key: `paid-${p.phone || p.id}`,
                                    title,
                                    date: formatActivityRelativeTime(millis > 0 ? dateObj : new Date()) || 'Just now',
                                    sortMillis: millis > 0 ? millis : (p.isMe ? Date.now() : 0),
                                    displayName: p.displayName,
                                    photoURL: p.photoURL,
                                    initials: p.isMe ? viewerInitials : getInitials(p.displayName),
                                }];
                            }
                            if (p.isMe && viewerPaymentEntries.length > 0) {
                                return viewerPaymentEntries.map((entry, idx) => {
                                    const rawDate = entry.timestamp || entry.timestampMs;
                                    const { dateObj, millis } = parseDateToTime(rawDate);
                                    const myPaidAmt = Number(entry?.amount || entry?.appliedTotalAmount || 0);
                                    const title = `You ${isOffline || entry?.isOfflineSettled ? 'settled offline' : 'paid'} $${myPaidAmt.toFixed(2)}`;
                                    return {
                                        key: `paid-${p.phone || p.id}-${entry.id || idx}`,
                                        title,
                                        date: formatActivityRelativeTime(millis > 0 ? dateObj : new Date()) || 'Just now',
                                        sortMillis: millis > 0 ? millis : (Date.now() - idx),
                                        displayName: p.displayName,
                                        photoURL: p.photoURL,
                                        initials: p.isMe ? viewerInitials : getInitials(p.displayName),
                                    };
                                });
                            }
                            const inv = (sessionInvites || []).find((i) => normalizePhone(getInvitePhone(i)) === p.phone);
                            const rawDate = p.isMe && viewerActivityEntry
                                ? (viewerActivityEntry.timestamp || viewerActivityEntry.timestampMs)
                                : (isOffline
                                    ? (inv?.settledOfflineAt || inv?.paidAt || inv?.verifiedAt || inv?.createdAt || session.date || inv?.updatedAt || session.updatedAt)
                                    : (inv?.paidAt || inv?.verifiedAt || inv?.createdAt || session.date || inv?.updatedAt || session.updatedAt));
                            const { dateObj, millis } = parseDateToTime(rawDate);
                            let title = '';
                            if (p.isMe) {
                                const myPaidAmt = Number(viewerActivityEntry?.amount || viewerActivityEntry?.appliedTotalAmount || amountPaidSoFar || lastPaidAmount || p.amount || 0);
                                title = `You ${isOffline || viewerActivityEntry?.isOfflineSettled ? 'settled offline' : 'paid'} $${myPaidAmt.toFixed(2)}`;
                            } else {
                                title = `${p.displayName} ${isOffline ? 'settled offline' : 'paid'} $${(p.amount || 0).toFixed(2)}`;
                            }
                            return [{
                                key: `paid-${p.phone || p.id}`,
                                title,
                                date: formatActivityRelativeTime(millis > 0 ? dateObj : new Date()) || 'Just now',
                                sortMillis: millis > 0 ? millis : (p.isMe ? Date.now() : 0),
                                displayName: p.displayName,
                                photoURL: p.photoURL,
                                initials: p.isMe ? viewerInitials : getInitials(p.displayName),
                            }];
                        }).sort((a, b) => b.sortMillis - a.sortMillis);

                        const fallbackDateRaw = viewerActivityEntry
                            ? (viewerActivityEntry.timestamp || viewerActivityEntry.timestampMs)
                            : (session.date || session.updatedAt || new Date());
                        const { dateObj: fallbackDateObj } = parseDateToTime(fallbackDateRaw);

                        const displayRows = paidRows.length > 0 ? paidRows : [{
                            key: 'paid-viewer-fallback',
                            title: isViewerCovered
                                ? `${viewerCoveredByName} paid for your items`
                                : `You paid $${(Number(viewerActivityEntry?.amount || viewerActivityEntry?.appliedTotalAmount || 0) || amountPaidSoFar || lastPaidAmount || remainingOwedAmount || Number(invite?.amount || 0)).toFixed(2)}`,
                            date: formatActivityRelativeTime(fallbackDateObj) || 'Just now',
                            sortMillis: Date.now(),
                            displayName: 'You',
                            photoURL: resolveParticipantPhoto(myPhoneNorm || ''),
                            initials: viewerInitials,
                        }];

                        return (
                            <div className="space-y-2 order-[4.7]">
                                <div className="flex items-center justify-between px-1 mb-1">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Activity</h3>
                                </div>
                                <div className="bg-white rounded-3xl p-4 divide-y divide-slate-100/70">
                                    {displayRows.map((row) => (
                                        <div key={row.key} className="flex items-center space-x-3.5 py-1.5 first:pt-0 last:pb-0">
                                            <div className="w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white overflow-hidden shrink-0">
                                                {row.photoURL ? (
                                                    <img src={row.photoURL} alt={row.displayName} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span>{row.initials}</span>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-sm text-slate-900 leading-snug truncate">{row.title}</p>
                                                <p className="text-xs font-semibold text-slate-500 mt-0.5">{row.date}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Organizer Status Banner */}
                    {isOrganizerViewer && (
                        <div className="rounded-[1.75rem] border border-teal-100 bg-gradient-to-br from-teal-50/80 to-emerald-50/40 p-5 shadow-sm">
                            <div className="flex items-start gap-3.5">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-xs">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <h3 className="text-sm font-black text-slate-900">You're the organizer</h3>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-100/80 text-teal-800 text-[10px] font-bold">
                                            $0 Fees
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs font-medium leading-relaxed text-slate-600">
                                        Friends pay you directly into your bank account with zero fees. Open the Clero app to manage payouts, remind friends, or edit split items.
                                    </p>
                                    <div className="mt-3.5 flex items-center gap-2.5 flex-wrap">
                                        <a
                                            href={`clero://pay/${session?.id || token}`}
                                            className="inline-flex items-center justify-center rounded-full bg-teal-600 px-4 py-2 text-xs font-black text-white hover:bg-teal-700 transition shadow-xs active:scale-95"
                                        >
                                            Open in Clero App
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const shareUrl = window.location.href;
                                                navigator.clipboard.writeText(shareUrl).then(() => {
                                                    alert('Split link copied to clipboard!');
                                                }).catch(() => {});
                                            }}
                                            className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition active:scale-95"
                                        >
                                            Copy split link
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {isCurrentSplitRestrictedFailedPayment || inviteProgress.hasFailedOutstanding ? (
                        <div className="rounded-[1.75rem] border border-rose-200 bg-rose-50 p-5 shadow-sm" role="alert">
                            <div className="flex items-start gap-4">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-rose-600 shadow-sm">
                                    <AlertCircleIcon />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-[0.16em] text-rose-600">Action needed</p>
                                    <h3 className="mt-1 text-[18px] font-black text-slate-900">Payment failed</h3>
                                    <p className="mt-2 text-[13px] font-medium leading-5 text-slate-600">
                                        Your bank could not complete this payment. Review your bank connection, then try the remaining amount again.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (failedPaymentRelinkAccount) {
                                                void openBankRelink(failedPaymentRelinkAccount);
                                                return;
                                            }
                                            setShowConfirmPaymentModal(true);
                                        }}
                                        disabled={isSetupBusy || paying}
                                        className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-[13px] font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {failedPaymentRelinkAccount ? 'Reconnect bank' : 'Review payment'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Dashboard Covering Card (when covering someone, matching SplitDashboardScreen with Change button) */}
                    {(() => {
                        const myNorm = normalizePhone(String(invite?.phone || auth.currentUser?.phoneNumber || ''));
                        const isViewerCoveringPaid = Boolean(paid || payerHeroStage === 'paid' || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline');
                        const isSessionFullySettled = (sessionInvites || []).length > 0 && (sessionInvites || []).every((inv) => {
                            const s = String(inv?.status || '').toLowerCase();
                            return s === 'paid' || s === 'verified' || s === 'settled_offline' || (inv as any)?.isCoveredPaid === true;
                        });
                        if (!myNorm || isSessionFullySettled || isViewerCoveringPaid) return null;

                        const coverageMap = session?.coverageMap || {};
                        const coveredByMap = session?.coveredByMap || {};
                        const coveredPersonsList: { phone: string; name: string; photoURL?: string }[] = [];

                        Object.entries(coverageMap).forEach(([phone, rec]: [string, any]) => {
                            const normCovBy = normalizePhone(rec?.coveredByPhone || rec?.coveredBy || '');
                            if (normCovBy === myNorm) {
                                const normCovPhone = normalizePhone(phone) || phone;
                                const covPerson = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === normCovPhone);
                                const covName = covPerson?.displayName || participants[normCovPhone]?.name || session?.participantsByPhone?.[normCovPhone]?.name || rec?.coveredPersonName || 'Friend';
                                const covPhoto = covPerson?.photoURL || participants[normCovPhone]?.photoURL || session?.participantsByPhone?.[normCovPhone]?.photoURL;
                                coveredPersonsList.push({ phone: normCovPhone, name: covName, photoURL: covPhoto });
                            }
                        });

                        if (coveredPersonsList.length === 0 && coveredByMap) {
                            Object.entries(coveredByMap).forEach(([phone, byPhone]: [string, any]) => {
                                if (normalizePhone(String(byPhone)) === myNorm) {
                                    const normCovPhone = normalizePhone(phone) || phone;
                                    const covPerson = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === normCovPhone);
                                    const covName = covPerson?.displayName || participants[normCovPhone]?.name || session?.participantsByPhone?.[normCovPhone]?.name || 'Friend';
                                    const covPhoto = covPerson?.photoURL || participants[normCovPhone]?.photoURL || session?.participantsByPhone?.[normCovPhone]?.photoURL;
                                    coveredPersonsList.push({ phone: normCovPhone, name: covName, photoURL: covPhoto });
                                }
                            });
                        }

                        if (coveredPersonsList.length === 0) return null;

                        return (
                            <div className="space-y-2 order-[4.75]">
                                <div
                                    onClick={() => {
                                        if (isViewerCoveringPaid) return;
                                        void logFounderPreviewEvent('item_selector_opened', {
                                            sessionId: session?.id,
                                            invitePhone: invite?.phone || null,
                                        });
                                        setIsSelectingItems(true);
                                    }}
                                    className={`bg-white rounded-3xl p-4 flex items-center justify-between gap-3 ${
                                        isViewerCoveringPaid ? '' : 'cursor-pointer hover:bg-slate-50/80 transition-colors'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className="flex items-center shrink-0">
                                            {coveredPersonsList.slice(0, 2).map((person, idx) => {
                                                const initials = getInitials(person.name);
                                                return (
                                                    <div
                                                        key={person.phone}
                                                        className={`w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center overflow-hidden shrink-0 border-2 border-white ${
                                                            idx > 0 ? '-ml-2.5 z-10' : ''
                                                        }`}
                                                    >
                                                        {person.photoURL ? (
                                                            <img src={person.photoURL} alt={person.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span>{initials}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-sm text-slate-900 truncate">
                                                {isViewerCoveringPaid
                                                    ? (coveredPersonsList.length === 1
                                                        ? `Covered ${coveredPersonsList[0].name}`
                                                        : `Covered ${coveredPersonsList.length} people`)
                                                    : (coveredPersonsList.length === 1
                                                        ? `Covering ${coveredPersonsList[0].name}`
                                                        : `Covering ${coveredPersonsList.length} people`)}
                                            </p>
                                            <p className="text-xs font-semibold text-slate-500 mt-0.5 truncate">
                                                {isViewerCoveringPaid
                                                    ? 'Their items were included in your total'
                                                    : 'Their items are included in your total'}
                                            </p>
                                        </div>
                                    </div>
                                    {!isViewerCoveringPaid && (
                                        <div className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full text-xs font-bold text-slate-600 transition-colors shrink-0">
                                            <span>Change</span>
                                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 stroke-[2.5]" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Quick Actions Section */}
                    {hasItemizedReceipt && renderedSessionItems.length > 0 && !isDeclined && !paid && inviteStatus !== 'paid' && inviteStatus !== 'verified' && inviteStatus !== 'settled_offline' && !isViewerCoveredPaid && !isViewerCovered && !isOrganizerViewer && (() => {
                        const hasMyClaims = renderedSessionItems.some((item: any) => {
                            const claimContext = getItemClaimContext(item);
                            return Number(claimContext.myShare?.fraction || 0) > 0.0001;
                        });
                        return (
                            <div className="space-y-2 order-[5.0]">
                                <div className="flex items-center justify-between px-1 mb-1">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quick actions</h3>
                                </div>
                                <div className="bg-white rounded-3xl overflow-hidden p-1">
                                    <button
                                        onClick={() => {
                                            void logFounderPreviewEvent('item_selector_opened', {
                                                sessionId: session.id,
                                                invitePhone: invite.phone || null,
                                            });
                                            setIsSelectingItems(true);
                                        }}
                                        className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 rounded-2xl transition-colors text-left group"
                                    >
                                        <div className="flex items-center space-x-3.5 min-w-0 flex-1">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-800 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                                <Sliders className="w-5 h-5 stroke-[2.2]" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-sm text-slate-900 leading-snug">
                                                    {hasMyClaims ? 'Edit claims' : 'Claim items'}
                                                </p>
                                                <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">
                                                    {hasMyClaims ? 'Update which items you pay for' : 'Select which items you pay for'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-slate-900 group-hover:bg-slate-100 transition-all shrink-0 ml-2">
                                            <ChevronRight className="w-4 h-4" />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Participants (Single Row Button Parity) */}
                    <div className="space-y-2 order-[5.1]">
                        <div className="flex items-center justify-between px-1 mb-1">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Participants</h3>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsParticipantsSheetOpen(true)}
                            className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 rounded-3xl transition-colors text-left group"
                        >
                            <div className="flex items-center space-x-3.5 min-w-0 flex-1">
                                <div className="flex -space-x-2 shrink-0 overflow-hidden py-0.5 pl-0.5">
                                    {participantRowsForSheet.slice(0, 4).map((row, idx) => {
                                        const initials = row.isMe ? viewerInitials : getInitials(row.displayName);
                                        return (
                                            <div
                                                key={row.id || idx}
                                                className="w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white overflow-hidden shrink-0"
                                                style={{ zIndex: 10 - idx }}
                                            >
                                                {row.photoURL ? (
                                                    <img src={row.photoURL} alt={row.displayName} className="w-full h-full object-cover" />
                                                ) : initials ? (
                                                    <span>{initials}</span>
                                                ) : (
                                                    <User className="w-4 h-4 text-slate-500" />
                                                )}
                                            </div>
                                        );
                                    })}
                                    {participantRowsForSheet.length > 4 && (
                                        <div
                                            className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 font-bold text-xs flex items-center justify-center border-2 border-white shrink-0"
                                            style={{ zIndex: 5 }}
                                        >
                                            +{participantRowsForSheet.length - 4}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-sm text-slate-900 leading-snug">
                                        View all
                                    </p>
                                    <p className="text-xs font-semibold text-slate-500 mt-0.5 truncate">
                                        {participantRowsForSheet.length} {participantRowsForSheet.length === 1 ? 'person' : 'people'} in this request
                                    </p>
                                </div>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-slate-900 group-hover:bg-slate-100 transition-all shrink-0 ml-2">
                                <ChevronRight className="w-4 h-4" />
                            </div>
                        </button>
                    </div>

                    {/* Item Sections (Your Items, Covering For, Available to Claim, Claimed by others) */}
                    {hasItemizedReceipt && renderedSessionItems.length > 0 && (() => {
                        const allItems = Array.isArray(renderedSessionItems) ? renderedSessionItems : [];
                        const isViewerCoveringPaid = isViewerSettled;

                        const isSessionEqualSplit = Boolean(
                            (session as any)?.splitMethod === 'equal' ||
                            (!(session as any)?.isItemClaimingEnabled && !(session as any)?.splitMethod?.includes('items'))
                        );

                        if (isSessionEqualSplit) {
                            const isCollapsed = !showOtherBreakdownItems && allItems.length > 3;
                            const visibleItems = isCollapsed ? allItems.slice(0, 3) : allItems;

                            return (
                                <div className="space-y-3 order-[5.4]">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 px-1">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">RECEIPT ITEMS</span>
                                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                {allItems.length}
                                            </span>
                                        </div>
                                        <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                            {visibleItems.map((item: any) => (
                                                <div
                                                    key={`equal-item-${item.id}`}
                                                    onClick={isViewerSettled ? undefined : () => setIsSelectingItems(true)}
                                                    className={`p-4 flex items-center gap-3.5 ${
                                                        isViewerSettled
                                                            ? 'cursor-default'
                                                            : 'hover:bg-slate-50 transition-colors cursor-pointer group active:scale-[0.99]'
                                                    }`}
                                                >
                                                    <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                                    <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                                                        <p className="font-extrabold text-sm text-slate-900 truncate">{item.name || 'Item'}</p>
                                                        <p className="font-black text-sm text-slate-900 shrink-0">${Number(item.amount || 0).toFixed(2)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {allItems.length > 3 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                triggerUiHaptic();
                                                setShowOtherBreakdownItems(!showOtherBreakdownItems);
                                            }}
                                            className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-600 rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                                        >
                                            <span>{isCollapsed ? `Show all ${allItems.length} items` : 'Show less'}</span>
                                            {isCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                                        </button>
                                    )}
                                </div>
                            );
                        }

                        const yourItems = allItems.filter((item: any) => {
                            const myShare = resolveMyShare(item);
                            return Number(myShare?.fraction || 0) > 0.0001;
                        });

                        const coveredPhonesSet = new Set(
                            Array.from(Object.entries(session?.coverageMap || {}))
                                .filter(([_, rec]: [string, any]) => {
                                    const normCovBy = normalizePhone(rec?.coveredByPhone || rec?.coveredBy || '');
                                    return myNorm && normCovBy === myNorm;
                                })
                                .map(([phone]) => normalizePhone(phone) || phone)
                        );

                        const coveredSectionsByPerson = Array.from(coveredPhonesSet).map((ph) => {
                            const pObj = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === ph);
                            const pName = pObj?.displayName || participants[ph]?.name || session?.participantsByPhone?.[ph]?.name || 'Friend';
                            const personItems = allItems.filter((item: any) => {
                                const shares = getEffectiveItemShares(item) || {};
                                return Object.entries(shares).some(([sharePh, sh]: [string, any]) => {
                                    const normP = normalizePhone(sharePh) || sharePh;
                                    return normP === ph && Number(sh?.fraction || 0) > 0.0001;
                                });
                            });
                            return {
                                phone: ph,
                                name: pName,
                                items: personItems,
                            };
                        }).filter((sec) => sec.items.length > 0);

                        const isClaimedByViewer = (item: any) => {
                            const myShare = resolveMyShare(item);
                            return Number(myShare?.fraction || 0) > 0.0001;
                        };
                        const isClaimedByCovered = (item: any) => {
                            const shares = getEffectiveItemShares(item) || {};
                            return Object.entries(shares).some(([sharePh, sh]: [string, any]) => {
                                const normP = normalizePhone(sharePh) || sharePh;
                                return coveredPhonesSet.has(normP) && Number(sh?.fraction || 0) > 0.0001;
                            });
                        };

                        const availableToClaim = allItems.filter((item: any) => {
                            if (isClaimedByViewer(item) || isClaimedByCovered(item)) return false;
                            const claimContext = getItemClaimContext(item);
                            const totalClaimed = Object.values(claimContext.shares || {}).reduce<number>((s, v: any) => s + (Number(v?.fraction) || 0), 0);
                            return totalClaimed < 0.9999;
                        });

                        const claimedByOthers = allItems.filter((item: any) => {
                            if (isClaimedByViewer(item) || isClaimedByCovered(item)) return false;
                            const claimContext = getItemClaimContext(item);
                            const shares = claimContext.shares || {};
                            const totalClaimed = Object.values(shares).reduce<number>((s, v: any) => s + (Number(v?.fraction) || 0), 0);
                            if (totalClaimed < 0.9999) return false;
                            const hasOthersShare = Object.entries(shares).some(([sharePh, sh]: [string, any]) => {
                                const normP = normalizePhone(sharePh) || sharePh;
                                const isViewer = myNorm && normP === myNorm;
                                const isCovered = coveredPhonesSet.has(normP);
                                return !isViewer && !isCovered && Number(sh?.fraction || 0) > 0.0001;
                            });
                            return hasOthersShare;
                        });

                        const otherItemsCount = availableToClaim.length + claimedByOthers.length;
                        const hasViewerOrCoveredItems = yourItems.length > 0 || coveredSectionsByPerson.length > 0;

                        const renderSectionItemCard = (item: any, sectionType: 'your' | 'covered' | 'available' | 'other', targetCoveredPhone?: string) => {
                            const claimContext = getItemClaimContext(item);
                            const myShare = claimContext.myShare;
                            const myFraction = Number(myShare?.fraction || 0);
                            const myAmount = Number(item.amount || 0) * myFraction;
                            const isFullItem = myFraction >= 0.999;
                            const totalAssignedFraction = Object.values(claimContext.shares || {}).reduce<number>((s, v: any) => s + (Number(v?.fraction) || 0), 0);
                            const remainingDollars = Math.max(0, Number(item.amount || 0) * (1 - totalAssignedFraction));

                            let coveredPortionDollar = Number(item.amount || 0);
                            if (sectionType === 'covered' && targetCoveredPhone) {
                                const shares = getEffectiveItemShares(item) || {};
                                const share = shares[targetCoveredPhone] || Object.entries(shares).find(([ph]) => (normalizePhone(ph) || ph) === targetCoveredPhone)?.[1];
                                const frac = Number(share?.fraction || 0);
                                if (frac > 0) {
                                    coveredPortionDollar = Number(item.amount || 0) * frac;
                                }
                            }

                            const rawShareEntries = Object.entries(claimContext.shares || {}).filter(([_, sh]: [string, any]) => Number(sh?.fraction || 0) > 0.0001);
                            const shareEntries = sortShareEntries(rawShareEntries, participantOrderMap);
                            const isFullSingleClaim = sectionType === 'your' && isFullItem && shareEntries.length <= 1;

                            const singleOtherName = sectionType === 'other' && shareEntries.length === 1 ? (() => {
                                const [sharePhone, _sh] = shareEntries[0];
                                const pNorm = normalizePhone(sharePhone) || sharePhone;
                                const isOrganizerShare = sharePhone === 'organizer' || (organizerPhoneNorm && pNorm === organizerPhoneNorm);
                                const pObj = isOrganizerShare
                                    ? participantRowsForSheet.find((p) => p.isOrganizer || (organizerPhoneNorm && normalizePhone(p.phone || '') === organizerPhoneNorm))
                                    : participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === pNorm);
                                const resolved = resolveWebParticipantIdentity({
                                    participant: pObj || { phone: pNorm, isOrganizer: isOrganizerShare },
                                    fallbackName: _sh?.name,
                                    viewerPhone: myPhoneNorm || invite?.phone,
                                    viewerDisplayName: invite?.name || 'You',
                                    viewerPhotoURL: invite?.photoURL || invite?.photoUrl,
                                    organizerPhone: session?.organizerPhone,
                                    organizerName: organizerDisplayName,
                                    organizerPhotoURL: session?.organizerPhotoURL || session?.organizerPhotoUrl,
                                    session,
                                });
                                return resolved.displayName || '1 person';
                            })() : null;

                            return (
                                <div
                                    key={`${sectionType}-${targetCoveredPhone || ''}-${item.id}`}
                                    onClick={isViewerSettled ? undefined : () => {
                                        setIsSelectingItems(true);
                                    }}
                                    className={`p-4 flex items-center gap-3.5 ${
                                        isViewerSettled
                                            ? 'cursor-default'
                                            : 'hover:bg-slate-50 transition-colors cursor-pointer group active:scale-[0.99]'
                                    }`}
                                >
                                    <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={40} />
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="font-extrabold text-sm text-slate-900 truncate">{item.name}</p>
                                            <p className="font-black text-sm shrink-0 text-slate-900">
                                                ${sectionType === 'your' ? myAmount.toFixed(2) : sectionType === 'covered' ? coveredPortionDollar.toFixed(2) : Number(item.amount || 0).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs font-medium text-slate-500 truncate">
                                                {sectionType === 'your'
                                                    ? (isFullSingleClaim ? 'Full item' : `Your portion: $${myAmount.toFixed(2)} of $${Number(item.amount || 0).toFixed(2)}`)
                                                    : sectionType === 'available'
                                                    ? `$${remainingDollars.toFixed(2)} remaining`
                                                    : sectionType === 'other'
                                                    ? (shareEntries.length === 1 ? (singleOtherName || '1 person') : `${shareEntries.length} people`)
                                                    : `Covered portion: $${coveredPortionDollar.toFixed(2)} of $${Number(item.amount || 0).toFixed(2)}`}
                                            </p>
                                            {!isViewerSettled && (
                                                <div className="flex items-center gap-1 text-slate-400 group-hover:text-slate-900 transition-colors">
                                                    <ChevronRight className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                        {/* Avatars side-by-side for shared items */}
                                        {!isFullSingleClaim && shareEntries.length > 0 && (
                                            <div className="flex items-center gap-1.5 pt-1.5">
                                                <div className="flex items-center gap-1.5 overflow-visible">
                                                    {shareEntries.slice(0, 3).map(([sharePhone, _sh], sIdx) => {
                                                        const pNorm = normalizePhone(sharePhone) || sharePhone;
                                                        const isOrganizerShare = sharePhone === 'organizer' || (organizerPhoneNorm && pNorm === organizerPhoneNorm);
                                                        const pObj = isOrganizerShare
                                                            ? participantRowsForSheet.find((p) => p.isOrganizer || (organizerPhoneNorm && normalizePhone(p.phone || '') === organizerPhoneNorm))
                                                            : participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === pNorm);
                                                        const resolved = resolveWebParticipantIdentity({
                                                            participant: pObj || { phone: pNorm, isOrganizer: isOrganizerShare },
                                                            fallbackName: _sh?.name,
                                                            viewerPhone: myPhoneNorm || invite?.phone,
                                                            viewerDisplayName: invite?.name || 'You',
                                                            viewerPhotoURL: invite?.photoURL || invite?.photoUrl,
                                                            organizerPhone: session?.organizerPhone,
                                                            organizerName: organizerDisplayName,
                                                            organizerPhotoURL: session?.organizerPhotoURL || session?.organizerPhotoUrl,
                                                            session,
                                                        });
                                                        return (
                                                            <div
                                                                key={`${item.id}-${sharePhone}-${sIdx}`}
                                                                className="w-[26px] h-[26px] rounded-full bg-[#E2E8F0] text-slate-700 font-bold text-[10.5px] leading-none flex items-center justify-center overflow-hidden shrink-0"
                                                            >
                                                                {resolved.photoURL ? (
                                                                    <img src={resolved.photoURL} alt={resolved.displayName} className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <span className="tracking-tight">{resolved.initials}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    {shareEntries.length > 3 && (
                                                        <div
                                                            className="w-[26px] h-[26px] rounded-full bg-slate-100 text-slate-600 font-bold text-[10px] leading-none flex items-center justify-center shrink-0"
                                                        >
                                                            +{shareEntries.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        };

                        return (
                            <div className="space-y-4 order-[5.4]">
                                {yourItems.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 px-1">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">YOUR ITEMS</span>
                                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                {yourItems.length}
                                            </span>
                                        </div>
                                        <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                            {yourItems.map((item) => renderSectionItemCard(item, 'your'))}
                                        </div>
                                    </div>
                                )}

                                {coveredSectionsByPerson.map((sec) => (
                                    <div key={`cov-section-${sec.phone}`} className="space-y-2">
                                        <div className="flex items-center gap-2 px-1">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                {isViewerCoveringPaid ? 'COVERED FOR' : 'COVERING FOR'} {sec.name.toUpperCase()}
                                            </span>
                                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                {sec.items.length}
                                            </span>
                                        </div>
                                        <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                            {sec.items.map((item) => renderSectionItemCard(item, 'covered', sec.phone))}
                                        </div>
                                    </div>
                                ))}

                                {hasViewerOrCoveredItems ? (
                                    otherItemsCount > 0 && (
                                        !showOtherBreakdownItems ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    triggerUiHaptic();
                                                    setShowOtherBreakdownItems(true);
                                                }}
                                                className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-600 rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors border-0 shadow-none"
                                            >
                                                <span>Show {otherItemsCount} other item{otherItemsCount === 1 ? '' : 's'}</span>
                                                <ChevronDown className="w-4 h-4 text-slate-400" />
                                            </button>
                                        ) : (
                                            <div className="space-y-4">
                                                {availableToClaim.length > 0 && (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 px-1">
                                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                {isViewerSettled ? 'UNASSIGNED' : 'AVAILABLE TO CLAIM'}
                                                            </span>
                                                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                                {availableToClaim.length}
                                                            </span>
                                                        </div>
                                                        <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                                            {availableToClaim.map((item) => renderSectionItemCard(item, 'available'))}
                                                        </div>
                                                    </div>
                                                )}

                                                {claimedByOthers.length > 0 && (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 px-1">
                                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">CLAIMED BY OTHERS</span>
                                                            <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                                {claimedByOthers.length}
                                                            </span>
                                                        </div>
                                                        <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                                            {claimedByOthers.map((item) => renderSectionItemCard(item, 'other'))}
                                                        </div>
                                                    </div>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        triggerUiHaptic();
                                                        setShowOtherBreakdownItems(false);
                                                    }}
                                                    className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-600 rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors border-0 shadow-none"
                                                >
                                                    <span>Show fewer items</span>
                                                    <ChevronUp className="w-4 h-4 text-slate-400" />
                                                </button>
                                            </div>
                                        )
                                    )
                                ) : (
                                    <>
                                        {availableToClaim.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 px-1">
                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                        {isViewerSettled ? 'UNASSIGNED' : 'AVAILABLE TO CLAIM'}
                                                    </span>
                                                    <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                        {availableToClaim.length}
                                                    </span>
                                                </div>
                                                <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                                    {availableToClaim.map((item) => renderSectionItemCard(item, 'available'))}
                                                </div>
                                            </div>
                                        )}

                                        {claimedByOthers.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 px-1">
                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">CLAIMED BY OTHERS</span>
                                                    <span className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#334155] font-bold text-[11px] flex items-center justify-center">
                                                        {claimedByOthers.length}
                                                    </span>
                                                </div>
                                                <div className="bg-white rounded-3xl divide-y divide-slate-100/70 overflow-hidden">
                                                    {(!showOtherBreakdownItems && claimedByOthers.length > 3
                                                        ? claimedByOthers.slice(0, 3)
                                                        : claimedByOthers
                                                    ).map((item) => renderSectionItemCard(item, 'other'))}
                                                </div>
                                                {claimedByOthers.length > 3 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            triggerUiHaptic();
                                                            setShowOtherBreakdownItems(!showOtherBreakdownItems);
                                                        }}
                                                        className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-600 rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                                                    >
                                                        <span>
                                                            {!showOtherBreakdownItems
                                                                ? `Show ${claimedByOthers.length - 3} other item${claimedByOthers.length - 3 === 1 ? '' : 's'}`
                                                                : 'Show fewer items'}
                                                        </span>
                                                        {!showOtherBreakdownItems ? (
                                                            <ChevronDown className="w-4 h-4 text-slate-400" />
                                                        ) : (
                                                            <ChevronUp className="w-4 h-4 text-slate-400" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {/* Unified Request Summary Card (reviewSummaryCard Parity) */}
                    {(() => {
                        const isGroupSettlement = Boolean(session?.groupId || session?.groupName || session?.splitSource === 'group_settlement');
                        const isSettled = Boolean(paid || inviteStatus === 'paid' || inviteStatus === 'settled_offline');
                        const splitPurchaseDateObj = session.purchaseDate || session.createdAt || session.date;
                        const formattedSplitDate = splitPurchaseDateObj ? formatDateOnly(splitPurchaseDateObj) : '';
                        const peopleCount = Array.isArray(sessionInvites) ? sessionInvites.length + 1 : 1;
                        const splitMetaText = formattedSplitDate 
                            ? `${formattedSplitDate} · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` 
                            : `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}`;
                        const splitNotes = (session as any).notes || (session as any).note || '';
                        const summaryReceiptUrl = session.receiptThumbnailURL || session.receiptThumbnailUrl || session.receipt?.thumbnailUrl || (session as any).aiData?.thumbnailDataUrl || session.receiptImageURL || session.receiptImageUrl || session.receipt?.imageUrl;

                        const normalizedItemsForBreakdown = renderedSessionItems.map((item: any) => {
                            const effectiveShares = getEffectiveItemShares(item) || item?.shares || item?.baseShares || {};
                            return {
                                ...item,
                                shares: effectiveShares,
                            };
                        });

                        const participantTaxTipAllocations = computeParticipantTaxTipBreakdown({
                            items: normalizedItemsForBreakdown,
                            tax: Number((session as any)?.tax || 0),
                            tip: Number((session as any)?.tip || (session as any)?.tipAmount || 0),
                            fees: Number((session as any)?.fees || 0),
                            discounts: Number((session as any)?.discounts || 0),
                            deductions: Number((session as any)?.deductions || 0),
                            totalSubtotal: subtotalAmount,
                            organizerPhone: session?.organizerPhone,
                            organizerName: session?.organizerName,
                            invites: sessionInvites,
                            viewerPhone: invite?.phone || myPhoneNorm || auth.currentUser?.phoneNumber,
                        });

                        const viewerCoveredPhoneSet = new Set(
                            viewerCoveringPersonsList.map((p) => normalizePhone(p.phone) || p.phone)
                        );
                        const isViewerCoveringPaid = Boolean(paid || inviteStatus === 'paid' || inviteStatus === 'settled_offline');

                        const viewerOwnSubtotal = renderedSessionItems.reduce((sum: number, item: any) => {
                            const myShare = resolveMyShare(item);
                            const frac = Number(myShare?.fraction || 0);
                            return sum + ((Number(item.amount) || 0) * frac);
                        }, 0);

                        const safeTotalSubtotal = Math.max(0.01, subtotalAmount > 0 ? subtotalAmount : renderedSessionItems.reduce((s: number, it: any) => s + (Number(it.amount) || 0), 0));
                        const totalExtrasNetCents = Math.round((taxAmount + tipAmount + feesAmount - discountsAmount - deductionsAmount) * 100);

                        // Build covered persons breakdowns with exact penny-conservation matching backend formula
                        const coveredPersonsBreakdowns = viewerCoveringPersonsList.map((person) => {
                            const personPhoneNorm = normalizePhone(person.phone) || person.phone;
                            let personSubtotal = 0;
                            renderedSessionItems.forEach((item: any) => {
                                const shares = getEffectiveItemShares(item) || item?.shares || {};
                                const shareObj = shares[personPhoneNorm] || Object.entries(shares).find(([ph]) => (normalizePhone(ph) || ph) === personPhoneNorm)?.[1];
                                if (shareObj && Number(shareObj.fraction || 0) > 0) {
                                    personSubtotal += (Number(item.amount) || 0) * Number(shareObj.fraction || 0);
                                }
                            });

                            const ratio = safeTotalSubtotal > 0 ? (personSubtotal / safeTotalSubtotal) : 0;
                            const subtotal = roundMoney(personSubtotal);

                            const covInvite = Array.isArray(sessionInvites)
                                ? sessionInvites.find((inv: any) => normalizePhone(inv?.phone || inv?.phoneSnapshot || '') === personPhoneNorm)
                                : null;
                            const covCanonicalTotal = covInvite && Number(covInvite.coveredAmount || covInvite.amount || 0) > 0.009
                                ? Number(covInvite.coveredAmount || covInvite.amount)
                                : roundMoney(subtotal + (totalExtrasNetCents * ratio) / 100);

                            const taxShare = roundMoney(taxAmount * ratio);
                            const feesShare = roundMoney(feesAmount * ratio);
                            const discountsShare = roundMoney(discountsAmount * ratio);
                            const deductionsShare = roundMoney(deductionsAmount * ratio);
                            const tipShare = roundMoney(covCanonicalTotal - subtotal - taxShare - feesShare + discountsShare + deductionsShare);

                            return {
                                phone: person.phone,
                                name: person.name,
                                photoURL: person.photoURL,
                                itemsSubtotal: subtotal,
                                taxShare,
                                tipShare,
                                feesShare,
                                discountsShare,
                                deductionsShare,
                                total: covCanonicalTotal,
                            };
                        }).filter((p) => p.itemsSubtotal > 0.009);

                        // Direct viewer allocation with canonical total
                        const viewerDirectAllocMatch = participantTaxTipAllocations.find((p) => p.isViewer);
                        const viewerDirectRatio = safeTotalSubtotal > 0 ? (viewerOwnSubtotal / safeTotalSubtotal) : 0;
                        const viewerDirectSubtotal = viewerOwnSubtotal > 0.009 ? roundMoney(viewerOwnSubtotal) : (viewerDirectAllocMatch?.itemsSubtotal || 0);

                        const canonicalViewerPaidTotal = isViewerCoveringPaid
                            ? (amountPaidSoFar || lastPaidAmount || (invite?.amount ? Number(invite.amount) : 0))
                            : 0;

                        const totalCoveredSum = coveredPersonsBreakdowns.reduce((s, p) => s + p.total, 0);
                        const viewerDirectCanonicalTotal = canonicalViewerPaidTotal > 0
                            ? Math.max(0, roundMoney(canonicalViewerPaidTotal - totalCoveredSum))
                            : roundMoney(viewerDirectSubtotal + (totalExtrasNetCents * viewerDirectRatio) / 100);

                        const viewerDirectTax = roundMoney(taxAmount * viewerDirectRatio);
                        const viewerDirectFees = roundMoney(feesAmount * viewerDirectRatio);
                        const viewerDirectDiscounts = roundMoney(discountsAmount * viewerDirectRatio);
                        const viewerDirectDeductions = roundMoney(deductionsAmount * viewerDirectRatio);
                        const viewerDirectTip = roundMoney(viewerDirectCanonicalTotal - viewerDirectSubtotal - viewerDirectTax - viewerDirectFees + viewerDirectDiscounts + viewerDirectDeductions);

                        // Combined Viewer + Covered Allocations for the entire summary card
                        const combinedSubtotal = roundMoney(viewerDirectSubtotal + coveredPersonsBreakdowns.reduce((s, p) => s + p.itemsSubtotal, 0));
                        const combinedTax = roundMoney(viewerDirectTax + coveredPersonsBreakdowns.reduce((s, p) => s + p.taxShare, 0));
                        const combinedFees = roundMoney(viewerDirectFees + coveredPersonsBreakdowns.reduce((s, p) => s + p.feesShare, 0));
                        const combinedDiscounts = roundMoney(viewerDirectDiscounts + coveredPersonsBreakdowns.reduce((s, p) => s + p.discountsShare, 0));
                        const combinedDeductions = roundMoney(viewerDirectDeductions + coveredPersonsBreakdowns.reduce((s, p) => s + p.deductionsShare, 0));
                        const viewerCombinedAllocTotal = canonicalViewerPaidTotal > 0
                            ? canonicalViewerPaidTotal
                            : roundMoney(viewerDirectCanonicalTotal + totalCoveredSum);
                        const combinedTip = roundMoney(viewerCombinedAllocTotal - combinedSubtotal - combinedTax - combinedFees + combinedDiscounts + combinedDeductions);

                        const viewerAlloc = {
                            itemsSubtotal: combinedSubtotal,
                            taxShare: combinedTax,
                            tipShare: combinedTip,
                            feesShare: combinedFees,
                            discountsShare: combinedDiscounts,
                            deductionsShare: combinedDeductions,
                        };
                        const splitTaxPercentLabel = subtotalAmount > 0 && taxAmount > 0
                            ? ` (${((taxAmount / subtotalAmount) * 100).toFixed(2).replace(/\.?0+$/, '')}%)`
                            : '';
                        const splitTipPercentLabel = subtotalAmount > 0 && tipAmount > 0
                            ? ` (${((tipAmount / subtotalAmount) * 100).toFixed(2).replace(/\.?0+$/, '')}%)`
                            : '';

                        return (
                            <div className="space-y-2 order-6">
                                <div className="flex items-center justify-between px-1 mb-1">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        Summary
                                    </h3>
                                </div>
                            <div className="bg-white rounded-3xl p-6 space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[17px] font-bold text-gray-900 truncate">
                                            {session.title || 'Split request'}
                                        </p>
                                        <p className="text-[12px] text-gray-400 font-semibold mt-0.5">
                                            {splitMetaText}
                                        </p>
                                        {!!splitNotes && (
                                            <p className="text-xs text-gray-500 font-medium mt-1 leading-relaxed italic bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
                                                “{splitNotes}”
                                            </p>
                                        )}
                                    </div>
                                    {Boolean(summaryReceiptUrl && !summaryReceiptUrl.startsWith('file://')) && (
                                        <button 
                                            type="button"
                                            onClick={() => setViewingReceipt(true)}
                                            className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 overflow-hidden flex-shrink-0 active:scale-95 transition-transform"
                                        >
                                            {purchaseSummaryReceiptBroken ? (
                                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                    <Receipt className="w-5 h-5 text-slate-400" />
                                                </div>
                                            ) : (
                                                <img
                                                    src={summaryReceiptUrl}
                                                    alt="Receipt thumbnail"
                                                    className="w-full h-full object-cover"
                                                    onError={() => setPurchaseSummaryReceiptBroken(true)}
                                                />
                                            )}
                                        </button>
                                    )}
                                </div>

                                <div className="h-px bg-slate-100 my-1" />

                                <div className="space-y-3 pt-1">
                                    {session.checkin && session.checkout && (
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 font-medium">Trip dates</span>
                                            <span className="font-semibold text-gray-900">
                                                {formatDateOnly(session.checkin)} - {formatDateOnly(session.checkout)}
                                            </span>
                                        </div>
                                    )}
                                    {session?.payerPhone && normalizePhone(session.payerPhone) !== normalizePhone(session.organizerPhone || '') && (
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 font-medium">Paid by</span>
                                            <span className="font-semibold text-gray-900">
                                                {(() => {
                                                    const isMe = !!myPhoneNorm && normalizePhone(session.payerPhone) === myPhoneNorm;
                                                    if (isMe) return 'You';
                                                    const pPhone = normalizePhone(session.payerPhone);
                                                    const pObj = participantRowsForSheet.find((p) => (normalizePhone(p.phone || p.id) || p.id) === pPhone);
                                                    return pObj?.displayName || session.payerName || 'Payer';
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                    {subtotalAmount > 0.009 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Subtotal</span>
                                                {viewerAlloc && viewerAlloc.itemsSubtotal > 0.009 && Math.abs(viewerAlloc.itemsSubtotal - subtotalAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: ${subtotalAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                ${(viewerAlloc && viewerAlloc.itemsSubtotal > 0.009 ? viewerAlloc.itemsSubtotal : Math.max(0, subtotalAmount)).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {coveredPersonsBreakdowns.length > 0 && (
                                        <div className="space-y-2">
                                            {viewerDirectSubtotal > 0.009 && (
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-medium">Your items</span>
                                                    <span className="font-semibold text-gray-900">
                                                        ${viewerDirectSubtotal.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                            {coveredPersonsBreakdowns.map((cov) => (
                                                <div key={`cov_summary_row_${cov.phone}`} className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-medium">
                                                        {isViewerCoveringPaid ? 'Covered for' : 'Covering for'} {cov.name}
                                                    </span>
                                                    <span className="font-semibold text-gray-900">
                                                        ${cov.itemsSubtotal.toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}

                                            {/* Detailed breakdown button and view */}
                                            <div className="pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        triggerUiHaptic('selection');
                                                        setShowDetailedSummaryBreakdown(!showDetailedSummaryBreakdown);
                                                    }}
                                                    className="w-full py-2 px-3 rounded-2xl bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-xs font-semibold text-slate-600 transition-colors cursor-pointer"
                                                >
                                                    <span>{showDetailedSummaryBreakdown ? 'Hide detailed breakdown' : 'Tap to view detailed breakdown'}</span>
                                                    {showDetailedSummaryBreakdown ? (
                                                        <ChevronUp className="w-3.5 h-3.5 text-slate-400 stroke-[2.2]" />
                                                    ) : (
                                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 stroke-[2.2]" />
                                                    )}
                                                </button>

                                                {showDetailedSummaryBreakdown && (
                                                    <div className="mt-2 space-y-2">
                                                        {coveredPersonsBreakdowns.map((cov) => (
                                                            <div key={`cov_detail_${cov.phone}`} className="rounded-2xl bg-slate-50/80 p-3 space-y-1.5 border border-slate-100">
                                                                <div className="flex items-center justify-between font-bold text-xs text-slate-800">
                                                                    <span>{cov.name || 'Covered friend'}</span>
                                                                    <span>${cov.total.toFixed(2)}</span>
                                                                </div>
                                                                <div className="space-y-1 text-[11px] text-slate-500">
                                                                    <div className="flex justify-between">
                                                                        <span>Items subtotal</span>
                                                                        <span className="font-semibold text-slate-700">${cov.itemsSubtotal.toFixed(2)}</span>
                                                                    </div>
                                                                    {cov.taxShare > 0.009 && (
                                                                        <div className="flex justify-between">
                                                                            <span>Tax</span>
                                                                            <span className="font-semibold text-slate-700">${cov.taxShare.toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                    {cov.tipShare > 0.009 && (
                                                                        <div className="flex justify-between">
                                                                            <span>Tip</span>
                                                                            <span className="font-semibold text-slate-700">${cov.tipShare.toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {taxAmount > 0.009 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Tax{splitTaxPercentLabel}</span>
                                                {viewerAlloc && viewerAlloc.taxShare > 0.009 && Math.abs(viewerAlloc.taxShare - taxAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: ${taxAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                ${(viewerAlloc && viewerAlloc.taxShare > 0.009 ? viewerAlloc.taxShare : taxAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {tipAmount > 0.009 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Tip{splitTipPercentLabel}</span>
                                                {viewerAlloc && viewerAlloc.tipShare > 0.009 && Math.abs(viewerAlloc.tipShare - tipAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: ${tipAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                ${(viewerAlloc && viewerAlloc.tipShare > 0.009 ? viewerAlloc.tipShare : tipAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {feesAmount > 0 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Fees</span>
                                                {viewerAlloc && viewerAlloc.feesShare > 0.009 && Math.abs(viewerAlloc.feesShare - feesAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: ${feesAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                ${(viewerAlloc && viewerAlloc.feesShare > 0.009 ? viewerAlloc.feesShare : feesAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {discountsAmount > 0.009 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Discounts</span>
                                                {viewerAlloc && viewerAlloc.discountsShare > 0.009 && Math.abs(viewerAlloc.discountsShare - discountsAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: -${discountsAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                -${(viewerAlloc && viewerAlloc.discountsShare > 0.009 ? viewerAlloc.discountsShare : discountsAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {deductionsAmount > 0.009 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <div>
                                                <span className="text-slate-500 font-medium">Deductions</span>
                                                {viewerAlloc && viewerAlloc.deductionsShare > 0.009 && Math.abs(viewerAlloc.deductionsShare - deductionsAmount) > 0.01 ? (
                                                    <div className="text-[11px] font-semibold text-slate-400">Total: -${deductionsAmount.toFixed(2)}</div>
                                                ) : null}
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                -${(viewerAlloc && viewerAlloc.deductionsShare > 0.009 ? viewerAlloc.deductionsShare : deductionsAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    {shouldShowSummaryPaymentMethod && (
                                        displayedAppliedMethod === 'venmo' ? (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-slate-500 font-medium">Payment method</span>
                                                <span className="font-semibold text-gray-900">Venmo</span>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-medium">Payment method</span>
                                                </div>
                                                <div className="rounded-[1.2rem] bg-slate-50 px-4 py-3 flex items-center gap-3">
                                                    {summaryBankAccount && getPlaidAccountLogo(summaryBankAccount) ? (
                                                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0">
                                                            <img src={getPlaidAccountLogo(summaryBankAccount) || ''} alt={getPlaidAccountBankName(summaryBankAccount)} className="w-6 h-6 object-contain" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-slate-600 font-black text-[13px] shrink-0">
                                                            {getPlaidAccountBankName(summaryBankAccount).charAt(0).toUpperCase() || 'B'}
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-[13px] font-bold text-slate-900 truncate">
                                                            {getPlaidAccountBankName(summaryBankAccount)}
                                                        </p>
                                                        <p className="text-[12px] font-semibold text-slate-400 truncate">
                                                            {getPlaidAccountTypeLabel(summaryBankAccount)} {getPlaidAccountLast4(summaryBankAccount) !== '****' ? `•••• ${getPlaidAccountLast4(summaryBankAccount)}` : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                    <div className="h-px bg-slate-100 my-1" />
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className="text-sm font-extrabold text-slate-900">
                                                {isOrganizerViewer
                                                    ? (isSettled ? 'Total received back (net)' : 'Total expected back (net)')
                                                    : 'Your share'}
                                            </span>
                                            <div className="text-[11px] font-semibold text-slate-400 mt-0.5">
                                                Total: ${Number(session?.totalAmount ?? session?.amount ?? 0).toFixed(2)}
                                            </div>
                                            {(() => {
                                                const ai = session?.aiData;
                                                const topCurrency = session?.originalCurrency;
                                                const topAmount = session?.originalAmount;
                                                const origCurrency = String(topCurrency !== undefined && topCurrency !== null ? topCurrency : (ai?.original_currency || ai?.originalCurrency || '')).trim().toUpperCase();
                                                const origAmount = Number(topAmount !== undefined && topAmount !== null ? topAmount : (ai?.original_amount ?? ai?.originalAmount ?? 0));
                                                if (origCurrency && origCurrency !== 'USD' && origAmount > 0) {
                                                    return (
                                                        <div className="text-[11px] font-semibold text-slate-400 mt-0.5">
                                                            Original Receipt: {origCurrency} {origAmount.toFixed(2)}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        <div className="text-right">
                                            <span
                                                className={`text-base font-black ${
                                                    isViewerCovered ? 'line-through text-slate-400 font-semibold' : 'text-slate-900'
                                                }`}
                                            >
                                                ${(isOrganizerViewer ? Number(session?.amount || session?.totalAmount || 0) : (viewerCombinedAllocTotal > 0.009 ? viewerCombinedAllocTotal : expectedShareAmount)).toFixed(2)}
                                            </span>
                                            {isViewerCovered && (
                                                <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                                                    {viewerCoveredByName ? `Covered by ${viewerCoveredByName}` : 'Covered by friend'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                            );
                        })()}



                                        {errorMessage && (
                        <div className="p-4 bg-red-50 text-red-600 text-xs font-bold rounded-xl text-center">
                            {errorMessage}
                        </div>
                    )}

                    {isVenmoSettled && (
                        <div className="rounded-[2rem] border border-teal-100 bg-teal-50/70 p-4 order-[7]">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[13px] font-bold text-teal-900">Still need to send your Venmo payment?</p>
                                    <p className="mt-1 text-[12px] leading-5 text-teal-700">
                                        Tap here to reopen Venmo and complete the payment if you have not sent it yet.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        void reopenVenmoPayment().catch((err: any) => {
                                            setErrorMessage(resolveSafePaymentErrorMessage(err, 'Unable to open Venmo. Please try again.'));
                                        });
                                    }}
                                    className="shrink-0 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-4 py-2 text-[12px] font-bold text-teal-700 hover:bg-teal-50"
                                >
                                    <VenmoBadge compact />
                                    <span>Pay again</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {!paid && !isOrganizerViewer && inviteStatus !== 'declined' && (
                        <BottomAppShowcase className="mt-8 max-w-md mx-auto order-8 animate-fadeIn" />
                    )}
                </div>

                {/* Footer Pay Button */}
                {!isOrganizerViewer && shouldShowPayerPaymentFooter({
                    paid,
                    inviteStatus,
                    isPaymentFullyCoveredByProcessing,
                }) && (
                    <div
                        className="fixed bottom-0 left-0 right-0 px-6 pt-12 z-40 animate-slideUp pointer-events-none bg-gradient-to-t from-white via-white to-transparent"
                        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
                    >
                        <div className="max-w-md mx-auto relative pt-2 pointer-events-auto flex items-center gap-3">
                            {!isViewerCovered && (
                                <div className="flex-1 min-w-[100px]">
                                    <WebCta
                                        title="Decline"
                                        variant="neutral"
                                        onClick={handleDeclineSplit}
                                        disabled={paying || declining || inviteStatus === 'processing' || inviteStatus === 'declined' || !!(session as any).settledOffline || sessionStatus === 'completed'}
                                    />
                                </div>
                            )}

                            <div className="flex-[2.2] min-w-0">
                                <WebCta
                                    title={
                                        paying
                                            ? (paymentsAreDisabled ? 'Opening Venmo...' : 'Processing...')
                                            : isViewerCovered
                                                ? (isViewerCoveredPaid ? `Paid by ${viewerCoveredByName}` : `Covered by ${viewerCoveredByName}`)
                                                : remainingOwedAmount > 0
                                                    ? (paymentsAreDisabled ? `Pay in Venmo ($${remainingOwedAmount.toFixed(2)})` : `Pay ($${remainingOwedAmount.toFixed(2)})`)
                                                    : 'Select Items to Pay'
                                    }
                                    variant={isViewerCovered ? 'muted' : (paymentsAreDisabled && remainingOwedAmount > 0 ? 'info' : 'primary')}
                                    loading={paying}
                                    disabled={paying || declining || isViewerCovered}
                                    icon={!paying && !isViewerCovered ? <ArrowRight className="w-4 h-4 text-white" /> : undefined}
                                    iconPosition="right"
                                    onClick={() => {
                                        if (isViewerCovered) return;
                                        if (remainingOwedAmount <= 0 && hasItemizedReceipt) {
                                            triggerUiHaptic('selection');
                                            void logFounderPreviewEvent('item_selector_opened', {
                                                sessionId: session.id,
                                                invitePhone: invite.phone || null,
                                            });
                                            setIsSelectingItems(true);
                                            return;
                                        }
                                        triggerUiHaptic('impactMedium');
                                        void logFounderPreviewEvent('payment_modal_opened', {
                                            sessionId: session.id,
                                            invitePhone: invite.phone || null,
                                            rail: selectedPaymentRail,
                                        });
                                        setShowConfirmPaymentModal(true);
                                        void refreshPayerSetupState();
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Sticky CTA for downloading Clero when paid */}
                {!isOrganizerViewer && (paid || payerHeroStage === 'paid' || inviteStatus === 'paid' || inviteStatus === 'verified' || inviteStatus === 'settled_offline' || isViewerCoveredPaid) && (
                    <div
                        className="fixed bottom-0 left-0 right-0 px-6 pt-12 z-40 animate-slideUp pointer-events-none bg-gradient-to-t from-white via-white to-transparent"
                        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
                    >
                        <div className="max-w-md mx-auto relative pt-2 pointer-events-auto">
                            <WebCta
                                title={getPlatformAppDownloadLabel(detectedClientPlatform)}
                                variant="primary"
                                icon={<ArrowRight className="w-4 h-4 text-white" />}
                                iconPosition="right"
                                onClick={() => {
                                    window.open(getPlatformAppDownloadUrl(detectedClientPlatform), '_blank', 'noopener,noreferrer');
                                }}
                            />
                        </div>
                    </div>
                )}

                {showConfirmPaymentModal && !paid && inviteStatus !== 'declined' && (
                    <div
                        className={`fixed inset-0 z-[75] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 ${
                            isClosingPaymentModal ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
                        }`}
                        onClick={handleClosePaymentModal}
                    >
                        <div className="absolute inset-0" onClick={handleClosePaymentModal} />
                        <div 
                            className={`relative w-full max-w-md bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] px-5 pt-3.5 pb-7 max-h-[92dvh] overflow-y-auto ${
                                isClosingPaymentModal ? 'animate-sheet-exit' : 'animate-sheet-enter'
                            }`}
                            style={{ 
                                paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Grab handle bar at the top */}
                            <div className="w-10 h-1.5 bg-slate-200 rounded-full mx-auto mb-3.5" />

                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div className="min-w-0 flex-1">
                                    <p className="text-[2.25rem] leading-[1] font-black text-slate-900 tracking-tight">${paymentAmountForSubmit.toFixed(2)}</p>
                                    <p className="text-xs text-slate-500 font-semibold mt-1">Review and confirm your payment</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClosePaymentModal}
                                    className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors shrink-0"
                                    aria-label="Close"
                                >
                                    <X className="w-5 h-5 stroke-[2.2]" />
                                </button>
                            </div>

                            {paymentsBlockedByIdentity ? (
                                <div className="mt-4 space-y-4">
                                    <IdentityVerificationBlockedCard
                                        status={payerPaymentCapabilityStatus}
                                        onPrimaryAction={payerPaymentCapabilityStatus === 'document_required' ? () => { void continueDocumentVerification(); } : undefined}
                                        onLearnMore={() => setShowIdentityBlockedModal(true)}
                                    />
                                    <div className="rounded-[16px] p-4 bg-slate-50">
                                        <div className="flex justify-between text-sm font-semibold">
                                            <span className="text-slate-500">Amount</span>
                                            <span className="text-slate-900">${payeeBaseAmount.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between mt-3 pt-3 border-t border-slate-200/70 font-bold">
                                            <span className="text-slate-900">Total</span>
                                            <span className="text-lg text-slate-950">${payeeBaseAmount.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 pt-2">
                                        <button
                                            type="button"
                                            onClick={handleClosePaymentModal}
                                            className="h-12 rounded-full text-slate-700 text-sm font-bold bg-slate-100 hover:bg-slate-200 transition-colors"
                                        >
                                            Close
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowIdentityBlockedModal(true)}
                                            className="h-12 rounded-full bg-[#0D9488] text-sm font-bold text-white hover:bg-[#0F766E] transition-colors"
                                        >
                                            Learn more
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3.5">
                                    {/* TO / FOR Card Block */}
                                    <div className="rounded-2xl bg-slate-50 p-3.5 space-y-2.5">
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider w-7">To</span>
                                            <div className="w-8 h-8 rounded-full bg-[#D7E1EA] text-[#475569] flex items-center justify-center font-bold text-[13px] overflow-hidden shrink-0">
                                                {paymentSheetOrganizerPhoto ? (
                                                    <img src={paymentSheetOrganizerPhoto} alt={rawOrganizerDisplayName || organizerDisplayName} className="w-full h-full object-cover" />
                                                ) : (
                                                    getInitials(rawOrganizerDisplayName || organizerDisplayName)
                                                )}
                                            </div>
                                            <span className="text-[14px] font-bold text-slate-800 truncate">{rawOrganizerDisplayName || organizerDisplayName}</span>
                                        </div>
                                        <div className="h-px bg-slate-200/70" />
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider w-7">For</span>
                                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden shadow-2xs">
                                                {paymentSheetMerchantLogoUrl && !merchantLogoFailed && !paymentSheetMerchantLogoUrl.startsWith('file://') ? (
                                                    <img
                                                        src={paymentSheetMerchantLogoUrl}
                                                        alt={purchaseMerchantName}
                                                        className="w-full h-full object-cover"
                                                        onError={() => setMerchantLogoFailed(true)}
                                                        onLoad={(e) => {
                                                             if (
                                                                paymentSheetMerchantLogoUrl.includes('google.com/s2/favicons') ||
                                                                paymentSheetMerchantLogoUrl.includes('gstatic.com/favicon')
                                                            ) {
                                                                if (e.currentTarget.naturalWidth <= 16 && e.currentTarget.naturalHeight <= 16) {
                                                                    setMerchantLogoFailed(true);
                                                                }
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Receipt className="w-4 h-4 text-slate-500" />
                                                )}
                                            </div>
                                            <span className="text-[14px] font-bold text-slate-800 truncate">{purchaseMerchantName}</span>
                                        </div>
                                    </div>

                                    {/* Partial Payment Card Block */}
                                    <div className="rounded-2xl bg-slate-50 p-3.5 space-y-3">
                                        {!paymentsAreDisabled && remainingOwedAmount > 0.009 && (
                                            <>
                                                <div className="rounded-xl bg-white p-3.5 space-y-3 shadow-2xs">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-[13px] leading-[1.2] font-bold text-slate-900">{failedAmountSoFar > 0 ? 'Repay failed amount' : 'Pay partial amount'}</p>
                                                            <p className="text-[11px] leading-[1.3] text-slate-500 font-medium mt-0.5">
                                                                {failedAmountSoFar > 0 
                                                                    ? `A prior bank payment failed. Repay up to $${maxPartialBaseAmount.toFixed(2)}.` 
                                                                    : `Choose any amount up to $${maxPartialBaseAmount.toFixed(2)} of your share.`}
                                                            </p>
                                                        </div>
                                                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                                            <input
                                                                type="checkbox"
                                                                checked={partialPaymentEnabled}
                                                                onChange={(event) => {
                                                                    triggerSelectionHaptic();
                                                                    const nextEnabled = event.target.checked;
                                                                    setPartialPaymentEnabled(nextEnabled);
                                                                    setPartialPaymentInput(nextEnabled ? maxPartialBaseAmount.toFixed(2) : '');
                                                                }}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="w-[48px] h-[28px] bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-teal-600 peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-[24px] after:w-[24px] after:transition-all shadow-xs"></div>
                                                        </label>
                                                    </div>
                                                    {partialPaymentEnabled && (
                                                        <div className="space-y-3 pt-2 border-t border-slate-200/60">
                                                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block text-center">
                                                                Amount to pay
                                                            </label>
                                                            <div className="relative flex items-center justify-center py-2">
                                                                <div className="flex items-center justify-center">
                                                                    <span className={`text-3xl font-black mr-1 ${partialPaymentInput ? 'text-slate-900' : 'text-slate-300'}`}>$</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0.01"
                                                                        max={maxPartialBaseAmount.toFixed(2)}
                                                                        step="0.01"
                                                                        value={partialPaymentInput}
                                                                        onChange={(event) => setPartialPaymentInput(event.target.value)}
                                                                        className="w-48 text-center bg-transparent font-black text-4xl text-slate-900 focus:outline-none placeholder:text-slate-300 tracking-tight"
                                                                        placeholder={maxPartialBaseAmount.toFixed(2)}
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                {partialPaymentInput ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            triggerUiHaptic('selection');
                                                                            setPartialPaymentInput('');
                                                                        }}
                                                                        className="absolute right-1 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0"
                                                                        aria-label="Clear amount"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                ) : null}
                                                            </div>

                                                            {/* Quick Preset Shortcut Chips */}
                                                            {maxPartialBaseAmount > 0 && (
                                                                <div className="flex items-center justify-center gap-1.5">
                                                                    {maxPartialBaseAmount >= 10 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                triggerUiHaptic('selection');
                                                                                setPartialPaymentInput((maxPartialBaseAmount * 0.25).toFixed(2));
                                                                            }}
                                                                            className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                                                        >
                                                                            25%
                                                                        </button>
                                                                    )}
                                                                    {maxPartialBaseAmount >= 5 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                triggerUiHaptic('selection');
                                                                                setPartialPaymentInput((maxPartialBaseAmount * 0.5).toFixed(2));
                                                                            }}
                                                                            className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                                                        >
                                                                            50%
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            triggerUiHaptic('selection');
                                                                            setPartialPaymentInput(maxPartialBaseAmount.toFixed(2));
                                                                        }}
                                                                        className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                                                    >
                                                                        Full (${maxPartialBaseAmount.toFixed(2)})
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {!partialPaymentIsValid && partialPaymentInput ? (
                                                                <p className="text-[11px] font-semibold text-rose-600 text-center">
                                                                    Enter a share amount between $0.01 and ${maxPartialBaseAmount.toFixed(2)}.
                                                                </p>
                                                            ) : partialPaymentIsValid && requestedPartialBaseAmount > 0 && requestedPartialBaseAmount < maxPartialBaseAmount ? (
                                                                <div className="flex items-center justify-between px-3.5 py-2 rounded-xl bg-teal-50 dark:bg-teal-950/40 text-xs">
                                                                    <span className="font-semibold text-teal-800 dark:text-teal-300">Remaining after payment</span>
                                                                    <span className="font-bold text-teal-900 dark:text-teal-200">
                                                                        ${(maxPartialBaseAmount - requestedPartialBaseAmount).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="h-px bg-slate-200/70" />
                                            </>
                                        )}
                                        <div className="flex justify-between text-[13px] font-semibold text-slate-500">
                                            <span>Share to pay</span>
                                            <span>${paymentQuote.baseAmount.toFixed(2)}</span>
                                        </div>
                                        {paymentQuote.feeAmount > 0 ? (
                                            <div className="flex justify-between text-[13px] font-semibold text-slate-500">
                                                <span>Clero fee</span>
                                                <span>${paymentQuote.feeAmount.toFixed(2)}</span>
                                            </div>
                                        ) : waiveParticipantFee ? (
                                            <div className="flex justify-between text-[13px] font-semibold text-emerald-700">
                                                <span>Clero+ fee waiver</span>
                                                <span className="flex items-center gap-1.5">
                                                    {(() => {
                                                        const baseline = resolveInviteBaselineFee(invite, session);
                                                        return baseline > 0 ? (
                                                            <span className="line-through text-slate-400 font-medium">${baseline.toFixed(2)}</span>
                                                        ) : null;
                                                    })()}
                                                    <span>$0.00</span>
                                                </span>
                                            </div>
                                        ) : null}
                                        <div className="flex justify-between items-center text-[13px] font-bold text-slate-900 pt-0.5">
                                            <span>Total to pay</span>
                                            <span className="font-extrabold text-[16px] leading-none">${paymentAmountForSubmit.toFixed(2)}</span>
                                        </div>
                                        {exceedsAvailablePaymentLimit && (
                                            <p className="mt-1.5 text-[11px] font-semibold text-red-600">
                                                This payment is above your available limit of {paymentLimitLabel}.
                                            </p>
                                        )}
                                    </div>

                                    {/* Payment Method / Bank Card */}
                                    <div className="space-y-3">
                                        {paymentsAreDisabled ? (
                                            <div className="rounded-2xl border border-slate-100 shadow-xs bg-white p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                                        <VenmoBadge compact />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-900">Venmo</p>
                                                        <p className="text-[11px] text-slate-400 font-semibold">Open Venmo to pay</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <AppBankAccountPickerWeb
                                                accounts={eligiblePaymentBanks}
                                                selectedAccountId={selectedPaymentBank?.id || selectedPaymentBankId}
                                                selectedAccount={selectedPaymentBank}
                                                isOpen={showPaymentBankDropdown}
                                                onOpenChange={setShowPaymentBankDropdown}
                                                onMissingPress={() => { void launchPlaidForPayment(); }}
                                                onAddAccountPress={() => { void launchPlaidForPayment(); }}
                                                onSelectAccount={(account) => {
                                                    setSelectedPaymentBankId(String(account?.id || ''));
                                                    setShowPaymentBankDropdown(false);
                                                }}
                                                getAccountName={getPlaidAccountBankName}
                                                getAccountLast4={getPlaidAccountLast4}
                                                getAccountLogo={getPlaidAccountLogo}
                                                getAccountType={getPlaidAccountTypeLabel}
                                                getAccountWarning={(account) => (
                                                    doesPlaidAccountRequireRelink(account) ? 'Relink required' : null
                                                )}
                                                selectedSubtitle={
                                                    selectedPaymentBank
                                                        ? (doesPlaidAccountRequireRelink(selectedPaymentBank) ? 'Relink required' : 'Selected account')
                                                        : 'No bank connected yet'
                                                }
                                                emptyTitle={hasOnlyIneligiblePaymentBanks ? 'Bank unavailable' : 'No bank connected'}
                                                emptySubtitle={hasOnlyIneligiblePaymentBanks ? 'Link a bank in your legal name to continue' : 'Required before you can send payments'}
                                                emptyCtaLabel="Connect bank now"
                                                addAccountLabel="Add bank account"
                                                addAccountLoadingLabel="Opening bank connection..."
                                                disabled={paying || isSetupBusy}
                                                addDisabled={paying || isSetupBusy}
                                                loadingAdd={launchingPlaid}
                                                hideConnectCta={true}
                                                onRelinkPress={(account) => { void openBankRelink(account); }}
                                                doesAccountRequireRelink={doesPlaidAccountRequireRelink}
                                            />
                                        )}
                                        {hasOnlyIneligiblePaymentBanks ? (
                                            <button
                                                type="button"
                                                onClick={() => setShowIneligibleBankSheet(true)}
                                                className="w-full rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-left"
                                            >
                                                <p className="text-[12px] font-bold text-amber-900">Linked bank can&apos;t be used for payments</p>
                                                <p className="mt-1 text-[11px] font-semibold text-amber-700">Usually this means bank name does not match your verified legal name.</p>
                                            </button>
                                        ) : null}
                                    </div>

                                    {/* Action Button */}
                                    <div className="pt-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (paymentsAreDisabled) {
                                                    handleVenmoPayment();
                                                } else if (!selectedPaymentBank) {
                                                    launchPlaidForPayment();
                                                } else if (doesPlaidAccountRequireRelink(selectedPaymentBank)) {
                                                    openBankRelink(selectedPaymentBank);
                                                } else {
                                                    handleInitiatePayment();
                                                }
                                            }}
                                            className={`w-full h-12 min-h-[48px] rounded-full font-extrabold text-[15px] flex items-center justify-center gap-2 transition-all transform active:scale-[0.985] ${paying || isSetupBusy || (!paymentsAreDisabled && selectedPaymentBank && !doesPlaidAccountRequireRelink(selectedPaymentBank) && !partialPaymentIsValid)
                                                ? 'bg-slate-200 text-slate-400 shadow-none cursor-not-allowed'
                                                : exceedsAvailablePaymentLimit
                                                    ? 'bg-[#0d9488]/50 text-white'
                                                : (paymentsAreDisabled
                                                    ? 'bg-[#008CFF] text-white hover:bg-[#0A7FE8]'
                                                    : 'bg-[#0d9488] text-white hover:bg-[#0f766e]')
                                                }`}
                                        >
                                            {paying ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    <span>{paymentsAreDisabled ? 'Opening Venmo...' : 'Processing...'}</span>
                                                </>
                                            ) : (
                                                <>
                                                    {paymentsAreDisabled ? (
                                                        <span className="flex items-center gap-2">
                                                            <VenmoBadge compact />
                                                            <span>Pay in Venmo</span>
                                                        </span>
                                                    ) : !selectedPaymentBank ? (
                                                        'Connect bank'
                                                    ) : doesPlaidAccountRequireRelink(selectedPaymentBank) ? (
                                                        'Relink bank account'
                                                    ) : needsIdentityVerification ? (
                                                        'Verify identity'
                                                    ) : !hasPaymentAuthorization ? (
                                                        'Continue to setup'
                                                    ) : (
                                                        `Pay $${paymentAmountForSubmit.toFixed(2)}`
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {plaidLinkToken && (
                    <PlaidLinkLauncher
                        token={plaidLinkToken}
                        onSuccess={handlePlaidSuccess}
                        onExit={handlePlaidExit}
                    />
                )}

                <PayerIdentityVerificationSheet
                    visible={showIdentityVerificationSheet}
                    value={identityDraft}
                    onChange={(next) => {
                        setIdentityVerificationError(null);
                        setIdentityDraft(next);
                    }}
                    onClose={() => {
                        setIdentityVerificationError(null);
                        setShowIdentityVerificationSheet(false);
                    }}
                    onSubmit={handleSubmitIdentityVerification}
                    busy={submittingIdentity}
                    submitError={identityVerificationError}
                />

                <AstraAuthorizationSheet
                    visible={showAstraAuthorizationSheet}
                    bankName={selectedPaymentBank?.bankName || selectedPaymentBank?.bankDisplayName || 'your bank'}
                    last4={selectedPaymentBank?.bankLast4 || selectedPaymentBank?.last4 || ''}
                    onClose={() => setShowAstraAuthorizationSheet(false)}
                    onContinue={handleContinueToAstra}
                    busy={authorizingAstra}
                />
                <IdentityVerificationBlockedModal
                    visible={showIdentityBlockedModal}
                    onClose={() => {
                        setShowIdentityBlockedModal(false);
                        setIdentityBlockedStatusOverride(null);
                    }}
                    status={identityBlockedStatusOverride || (identityVerificationWaitMode === 'timed_out' ? 'pending' : payerPaymentCapabilityStatus)}
                    onPrimaryAction={identityVerificationWaitMode === 'timed_out' || payerPaymentCapabilityStatus === 'pending'
                        ? (() => { void refreshIdentityVerificationStatus(); })
                        : payerPaymentCapabilityStatus === 'document_required'
                            ? (() => { void continueDocumentVerification(); })
                            : undefined}
                    primaryActionBusy={refreshingIdentityStatus}
                    errorMessage={identityVerificationWaitMode === 'timed_out' ? identityVerificationError : null}
                />

                <IdentityVerificationProgressModal
                    visible={identityVerificationWaitMode === 'active'}
                    stage={IDENTITY_VERIFICATION_STAGES[identityVerificationStageIndex]}
                    progress={identityVerificationProgress}
                />
                <InsufficientFundsSheetWeb
                    visible={!!insufficientFundsDetails}
                    details={insufficientFundsDetails}
                    onClose={() => setInsufficientFundsDetails(null)}
                    onChooseAnotherBank={() => {
                        setInsufficientFundsDetails(null);
                        setShowConfirmPaymentModal(true);
                        requestAnimationFrame(() => setShowPaymentBankDropdown(true));
                    }}
                />
                <UserLimitExceededSheetWeb
                    visible={!!limitExceededDetails}
                    limitType={limitExceededDetails?.limitType ?? null}
                    limitAmountCents={limitExceededDetails?.limitAmountCents ?? 0}
                    resetDate={limitExceededDetails?.resetDate ?? null}
                    onClose={() => setLimitExceededDetails(null)}
                />
                <ClaimablePaymentConfirmationSheetWeb
                    visible={showClaimablePaymentSheet}
                    counterpartyName={String(organizerDisplayName || session?.organizerName || 'This person').trim() || 'This person'}
                    claimWindowDays={claimablePaymentWindowDays}
                    continueTitle="Send claimable payment"
                    onContinue={() => {
                        setShowClaimablePaymentSheet(false);
                        setTimeout(() => {
                            void handleInitiatePayment({ claimableAcknowledged: true });
                        }, 180);
                    }}
                    onCancel={() => {
                        setShowClaimablePaymentSheet(false);
                    }}
                />
                <OrganizerRejectedSheetWeb
                    visible={showOrganizerRejectedSheet}
                    message={organizerRejectedMessage}
                    onClose={() => setShowOrganizerRejectedSheet(false)}
                />
                <IneligibleBankSheetWeb
                    visible={showIneligibleBankSheet}
                    onClose={() => setShowIneligibleBankSheet(false)}
                />
                <PaymentNoticeSheetWeb
                    notice={paymentNotice}
                    onClose={() => setPaymentNotice(null)}
                />
                <WebPaymentSuccessOverlay
                    visible={showPaymentInitiatedSuccessOverlay}
                    onDone={async () => {
                        setShowPaymentInitiatedSuccessOverlay(false);
                        const animParams = pendingSuccessHeroAnimationRef.current;
                        pendingSuccessHeroAnimationRef.current = null;
                        if (animParams) {
                            await runPaymentSuccessHeroAnimation(animParams);
                        }
                        void refreshPayerInviteUntilSettled();
                    }}
                />

                {setupTransitionMessage ? (
                    <div className="fixed inset-0 z-[94] bg-slate-950/45 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="w-full max-w-sm rounded-[2rem] border border-slate-200 bg-white px-6 py-7 text-center shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
                            <div className="mx-auto h-11 w-11 animate-spin rounded-full border-4 border-slate-200 border-t-teal-500" />
                            <p className="mt-4 text-[18px] font-black tracking-tight text-slate-900">One moment</p>
                            <p className="mt-2 text-[14px] leading-6 text-slate-500">{setupTransitionMessage}</p>
                        </div>
                    </div>
                ) : null}

                {inviteStatus === 'declined' && (
                    <div
                        className="fixed bottom-0 left-0 right-0 px-6 pt-12 z-40 pointer-events-none bg-gradient-to-t from-white via-white to-transparent"
                        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
                    >
                        <div className="max-w-md mx-auto rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-semibold text-amber-800 pointer-events-auto">
                            You declined this split.
                        </div>
                    </div>
                )}

                {/* Receipt Modal */}
                <WebReceiptViewerModal
                    visible={viewingReceipt}
                    imageUrl={
                        previewReceiptImageURL ||
                        session?.receiptImageURL ||
                        session?.receiptImageUrl ||
                        session?.receipt?.imageUrl ||
                        previewReceiptThumbnailURL ||
                        session?.receiptThumbnailURL ||
                        session?.receiptThumbnailUrl ||
                        session?.receipt?.thumbnailUrl ||
                        ''
                    }
                    onClose={() => setViewingReceipt(false)}
                />

                {/* Remove Bank Modal */}
                {isRemoveModalOpen && (
                    <div className="fixed inset-0 z-[95] flex items-center justify-center p-6 bg-black/20 backdrop-blur-sm">
                        <div className="bg-white p-6 rounded-3xl max-w-xs w-full shadow-2xl space-y-4">
                            <h3 className="text-lg font-bold text-gray-900 text-center">Unlink Bank Account?</h3>
                            <p className="text-xs text-center text-gray-500">
                                You will need to re-connect your bank for future payments.
                            </p>
                            <div className="flex space-x-3">
                                <button onClick={() => setIsRemoveModalOpen(false)} className="flex-1 py-3 bg-gray-50 rounded-xl text-xs font-bold text-gray-500">Cancel</button>
                                <button onClick={handleRemoveBank} disabled={isRemoving} className="flex-1 py-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold">
                                    {isRemoving ? '...' : 'Unlink'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Full-Screen Review Items View */}
                {isSelectingItems && !isDeclined && !isViewerSettled && renderedSessionItems.length > 0 && (
                    <WebReviewItemsView
                        session={activeClaimSession || session}
                        invite={invite}
                        sessionInvites={sessionInvites}
                        participants={participants}
                        onClose={() => closeClaimSheet(true)}
                        onCommitClaims={async (mutations, coveragePayload, options) => {
                            if (!session?.id || !invite) return;
                            const clientCommitId = `web_commit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                            const commitClaimDraftCallable = httpsCallable(functions, 'commitClaimDraftV1');
                            const result = await withSentrySpan(
                                {
                                    op: 'split.claim',
                                    name: 'split.commit_claim_draft',
                                    attributes: {
                                        sessionId: String(session.id || ''),
                                        mutationCount: Array.isArray(mutations) ? mutations.length : 0,
                                        hasExplicitCoveragePayload: Boolean(coveragePayload?.coverageMap),
                                    },
                                },
                                () => commitClaimDraftCallable({
                                    sessionId: session.id,
                                    inviteId: invite.id,
                                    clientCommitId,
                                    baseSessionVersion: Number(options?.baseSessionVersion ?? claimDraftSnapshotRef.current?.sessionVersion ?? session?.version ?? 0),
                                    itemMutations: mutations,
                                    ...(coveragePayload?.coverageMap ? {
                                        coverageMap: coveragePayload.coverageMap,
                                        coveredByMap: coveragePayload.coveredByMap,
                                    } : {}),
                                }),
                            );
                            const payload = (result?.data || {}) as any;
                            const conflicts = Array.isArray(payload?.conflicts) ? payload.conflicts : [];
                            if (conflicts.length > 0) {
                                setClaimConflictModal({
                                    open: true,
                                    message: conflicts.map((c: any) => c.message || 'Item updated by others').join(', '),
                                });
                                try {
                                    await refreshPayerInviteData();
                                } catch (refreshErr) {
                                    payerConsole.warn('[PayerPage] Failed to refresh invite data after commitClaimDraft', refreshErr);
                                }
                                return { success: false, conflicts };
                            }
                            // Refresh invite and session data so the Dashboard immediately updates with new claims and coverage
                            try {
                                await refreshPayerInviteData();
                            } catch (refreshErr) {
                                payerConsole.warn('[PayerPage] Failed to refresh invite data after commitClaimDraft', refreshErr);
                            }
                            closeClaimSheet(true);
                            return { success: true };
                        }}
                        onViewReceipt={() => setViewingReceipt(true)}
                        getItemIconEmoji={getItemIconEmoji}
                    />
                )}

                {/* Claim Conflict Modal */}
                {claimConflictModal.open && (
                    <div className="fixed inset-0 z-[90] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4">
                            <h3 className="text-lg font-black text-gray-900 text-center">Some items were not updated</h3>
                            <p className="text-sm text-gray-600 text-center">
                                Your valid changes were saved. Some items could not be applied.
                                <br />
                                {claimConflictModal.message}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setClaimConflictModal({ open: false, message: '' });
                                        resetClaimDraftFromLatestSession();
                                    }}
                                    className="flex-1 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold"
                                >
                                    Edit Claims Again
                                </button>
                                <button
                                    onClick={() => {
                                        setClaimConflictModal({ open: false, message: '' });
                                        closeClaimSheet(true);
                                    }}
                                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Claim Discard Modal */}
                {claimDiscardModalOpen && (
                    <div className="fixed inset-0 z-[90] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4">
                            <h3 className="text-lg font-black text-gray-900 text-center">Discard changes?</h3>
                            <p className="text-sm text-gray-600 text-center">
                                You have unsaved claim changes. If you leave now, these edits will be discarded.
                             </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setClaimDiscardModalOpen(false)}
                                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold"
                                >
                                    Keep Editing
                                </button>
                                <button
                                    onClick={() => closeClaimSheet(true)}
                                    className="flex-1 py-3 bg-gray-900 text-white rounded-xl text-sm font-bold"
                                >
                                    Discard Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Item Participants Modal (Nested) */}
                <ItemParticipantsModal
                    itemId={viewingItemParticipants}
                    renderedSessionItems={renderedSessionItems}
                    getEffectiveItemShares={getEffectiveItemShares}
                    organizerPhoneNorm={organizerPhoneNorm}
                    session={session}
                    myNorm={myNorm}
                    invite={invite}
                    getLocalParticipantName={getLocalParticipantName}
                    getPersistedParticipantName={getPersistedParticipantName}
                    organizerDisplayName={organizerDisplayName}
                    resolveParticipantDisplayName={resolveParticipantDisplayName}
                    formatPhoneForDisplay={formatPhoneForDisplay}
                    resolveParticipantPhoto={resolveParticipantPhoto}
                    getInitials={getInitials}
                    activeClaimSession={activeClaimSession}
                    getItemHistoryTimestampMs={getItemHistoryTimestampMs}
                    formatItemHistoryTimestamp={formatItemHistoryTimestamp}
                    onClose={() => setViewingItemParticipants(null)}
                />

                {/* Dedicated Participants View Parity */}
                {isParticipantsSheetOpen && (
                    <WebParticipantsView
                        onClose={() => setIsParticipantsSheetOpen(false)}
                        participants={participantRowsForSheet}
                        totalAmount={previewTotalAmount ?? session?.total ?? session?.amount}
                        organizerName={rawOrganizerDisplayName || organizerDisplayName}
                        organizerPhone={organizerPhoneNorm || session?.organizerPhone}
                        viewerPhone={myPhoneNorm || invite?.phone}
                        viewerDisplayName={isOrganizerViewer ? 'You (Organizer)' : 'You'}
                        sessionTitle={session?.title || purchaseMerchantName}
                        session={session}
                    />
                )}
            </div>
        </div >
    );
};

const ItemParticipantsModal = ({
    itemId,
    renderedSessionItems,
    getEffectiveItemShares,
    organizerPhoneNorm,
    session,
    myNorm,
    invite,
    getLocalParticipantName,
    getPersistedParticipantName,
    organizerDisplayName,
    resolveParticipantDisplayName,
    formatPhoneForDisplay,
    resolveParticipantPhoto,
    getInitials,
    activeClaimSession,
    getItemHistoryTimestampMs,
    formatItemHistoryTimestamp,
    onClose,
}: any) => {
    const { isRendered, isClosing, handleClose } = useSheetLifecycle(Boolean(itemId), onClose, 220);
    const lastItemIdRef = useRef(itemId);
    if (itemId) lastItemIdRef.current = itemId;
    const activeItemId = itemId || lastItemIdRef.current;

    if (!isRendered || !activeItemId) return null;
    const item = renderedSessionItems.find((i: any) => i.id === activeItemId);
    if (!item) return null;

    const shares = Object.entries(getEffectiveItemShares(item) || {})
        .filter(([, share]: any) => Number(share?.fraction || 0) > 0.0001);
    const currentClaims = shares
        .map(([p, s]: any) => {
            const cleanP = normalizePhone(p);
            const isOrganizerShare = p === 'organizer' || (!!organizerPhoneNorm && cleanP === organizerPhoneNorm);
            const resolvedPhone = isOrganizerShare ? (session.organizerPhone || '') : (cleanP || p);
            const normalizedPhoneToUse = normalizePhone(resolvedPhone) || resolvedPhone;
            const isMe = !isOrganizerShare && (!!myNorm && normalizedPhoneToUse === myNorm);
            const displayName = isMe
                ? resolveParticipantDisplayName({
                    phone: myNorm,
                    viewerPhone: invite?.phone,
                    localName: getLocalParticipantName(myNorm || ''),
                    persistedName: invite?.name,
                    fallbackLabel: invite?.name || 'Payer',
                })
                : (isOrganizerShare
                    ? organizerDisplayName
                    : resolveParticipantDisplayName({
                        phone: normalizedPhoneToUse,
                        viewerPhone: invite?.phone,
                        localName: getLocalParticipantName(normalizedPhoneToUse),
                        persistedName: getPersistedParticipantName(normalizedPhoneToUse) || s?.name,
                        fallbackLabel: getPersistedParticipantName(normalizedPhoneToUse) || s?.name || formatPhoneForDisplay(normalizedPhoneToUse),
                    }));
            const participantPhoto = resolveParticipantPhoto(normalizedPhoneToUse);
            const percent = Math.round(Number(s?.fraction || 0) * 100);
            const amount = Number(item.amount || 0) * Number(s?.fraction || 0);
            return {
                key: p,
                displayName,
                isOrganizer: isOrganizerShare,
                photoURL: participantPhoto,
                percent,
                amount,
            };
        })
        .sort((a, b) => b.percent - a.percent);

    const historyRows = ((activeClaimSession as any)?.claimLogs || (session as any)?.claimLogs || [])
        .map((log: any, index: number) => ({ ...log, __index: index }))
        .filter((log: any) => String(log?.itemId || '') === String(item.id || ''))
        .sort((a: any, b: any) => getItemHistoryTimestampMs(b?.createdAt, Number(b?.__index || 0)) - getItemHistoryTimestampMs(a?.createdAt, Number(a?.__index || 0)))
        .map((log: any) => {
            const rawActorPhone = String(log?.actorPhone || '');
            const actorPhone = normalizePhone(rawActorPhone);
            const isOrganizerActor = rawActorPhone === 'organizer' || (!!organizerPhoneNorm && actorPhone === organizerPhoneNorm);
            const actorResolvedPhone = isOrganizerActor ? (session.organizerPhone || '') : (actorPhone || rawActorPhone);
            const actorNormalizedPhone = normalizePhone(actorResolvedPhone) || actorResolvedPhone;
            const isMe = !isOrganizerActor && (!!myNorm && actorNormalizedPhone === myNorm);
            const persistedActorName = getPersistedParticipantName(actorNormalizedPhone) || log?.actorName;
            const actorName = isMe
                ? resolveParticipantDisplayName({
                    phone: myNorm,
                    viewerPhone: invite?.phone,
                    localName: getLocalParticipantName(myNorm || ''),
                    persistedName: invite?.name,
                    fallbackLabel: invite?.name || 'Payer',
                })
                : (isOrganizerActor
                    ? organizerDisplayName
                    : resolveParticipantDisplayName({
                        phone: actorNormalizedPhone,
                        viewerPhone: invite?.phone,
                        localName: getLocalParticipantName(actorNormalizedPhone),
                        persistedName: persistedActorName,
                        fallbackLabel: persistedActorName || formatPhoneForDisplay(actorNormalizedPhone),
                    }));

            const requestedPercent = Number.isFinite(Number(log?.requestedFraction))
                ? Math.round(Number(log.requestedFraction) * 100)
                : null;
            let summary = `${actorName} updated this item`;
            if (log?.source === 'organizer_seed') {
                summary = `${actorName} created the initial request`;
            } else if (log?.action === 'unclaim' || log?.source === 'participant_exit' || log?.source === 'participant_decline' || log?.source === 'group_member_left') {
                summary = isMe ? `${actorName} removed your share` : `${actorName} removed their share`;
            } else if (log?.action === 'claim' && log?.rebalanceMode === 'equalize') {
                summary = requestedPercent !== null
                    ? `${actorName} split this item evenly at ${requestedPercent}%`
                    : `${actorName} split this item evenly`;
            } else if (log?.action === 'claim' && requestedPercent !== null) {
                summary = `${actorName} claimed ${requestedPercent}%`;
            }

            return {
                id: String(log?.id || `${item.id}-${log.__index}`),
                summary,
                timestamp: formatItemHistoryTimestamp(log?.createdAt),
            };
        });

    return (
        <div
            className={`fixed inset-0 z-[80] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 ${
                isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
            }`}
            onClick={handleClose}
        >
            <div
                className={`w-full max-w-md mx-auto bg-slate-50 rounded-t-[2.25rem] sm:rounded-[2.25rem] shadow-2xl max-h-[92dvh] overflow-hidden relative ${
                    isClosing ? 'animate-sheet-exit' : 'animate-sheet-enter'
                }`}
                style={{
                    paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="px-5 pt-3 pb-4">
                    <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4" />
                    <div className="overflow-y-auto max-h-[calc(92dvh-44px)] pr-1">
                        <div className="flex items-center gap-3 px-1">
                            <WebItemIconAvatar name={item.name} iconKey={item.iconKey} size={36} />
                            <p className="text-[16px] font-black tracking-tight text-slate-900 truncate">{item.name}</p>
                        </div>

                        <div className="h-px bg-slate-100 mt-4 mb-5" />

                        <div className="rounded-[1.6rem] border border-[#D7E4F4] bg-[#F8FBFF] px-5 py-4">
                            <p className="text-[11px] font-bold tracking-wide text-slate-500">Item total</p>
                            <p className="mt-1 text-[32px] leading-none font-black tracking-tight text-slate-950">${Number(item.amount || 0).toFixed(2)}</p>
                        </div>

                        <div className="mt-7">
                            <p className="px-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Current Claims</p>
                            <div className="mt-3 rounded-[1.6rem] border border-[#D7E4F4] bg-[#F8FBFF] overflow-hidden">
                                {currentClaims.map((row: any, index: number) => (
                                    <div
                                        key={row.key}
                                        className={`flex items-center justify-between gap-3 px-4 py-3.5 ${index > 0 ? 'border-t border-slate-200/80' : ''}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-10 h-10 rounded-full bg-[#D9E5F1] flex items-center justify-center overflow-hidden shrink-0">
                                                {row.photoURL ? (
                                                    <img src={row.photoURL} alt={row.displayName} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-[15px] font-black text-slate-600">{getInitials(row.displayName)}</span>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[14px] font-bold text-slate-900 truncate">
                                                    {row.displayName}
                                                    {row.isOrganizer && (
                                                        <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[9px] font-black tracking-wide text-slate-500 align-middle">ORG</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="shrink-0 flex items-baseline gap-2.5 text-right">
                                            <span className="text-[14px] font-bold text-slate-900">${row.amount.toFixed(2)}</span>
                                            <span className="text-[13px] font-bold text-slate-500">{row.percent}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-7 pb-3">
                            <p className="px-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Claim History</p>
                            <div className="mt-3 rounded-[1.6rem] border border-[#D7E4F4] bg-[#F8FBFF] overflow-hidden">
                                {historyRows.length > 0 ? historyRows.map((row: any, index: number) => (
                                    <div
                                        key={row.id}
                                        className={`px-4 py-4 ${index > 0 ? 'border-t border-slate-200/80' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="mt-1.5 w-3 h-3 rounded-full bg-[#089981] shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-[14px] font-bold leading-5 text-slate-900">{row.summary}</p>
                                                {row.timestamp ? (
                                                    <p className="mt-1 text-[12px] font-semibold text-slate-400">{row.timestamp}</p>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="px-4 py-4">
                                        <p className="text-[13px] font-semibold text-slate-400">No claim history yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PaymentSheetShell = ({
    visible,
    onClose,
    children,
}: {
    visible: boolean;
    onClose: () => void;
    children: React.ReactNode;
}) => {
    const { isRendered, isClosing, handleClose } = useSheetLifecycle(visible, onClose, 220);
    if (!isRendered) return null;
    return (
        <div
            className={`fixed inset-0 z-[92] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 ${
                isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
            }`}
            onClick={handleClose}
        >
            <div
                className={`w-full max-w-md mx-auto bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] shadow-2xl px-5 pt-3.5 pb-6 max-h-[92dvh] overflow-y-auto relative ${
                    isClosing ? 'animate-sheet-exit' : 'animate-sheet-enter'
                }`}
                style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-3.5" />
                {children}
            </div>
        </div>
    );
};

const formatMoneyFromCents = (amountCents: number | null) => {
    if (!Number.isFinite(amountCents as number)) return 'Unavailable';
    return `$${(Number(amountCents) / 100).toFixed(2)}`;
};

const formatBankLabel = (details: InsufficientFundsErrorDetails | null) => {
    const bankName = String(details?.bankName || 'Connected Bank').trim() || 'Connected Bank';
    const bankLast4 = String(details?.bankLast4 || '').trim();
    return bankLast4 ? `${bankName} •••• ${bankLast4}` : bankName;
};

const ChevronLeftCircleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6" />
    </svg>
);

const AlertCircleIcon = ({ className = '' }: { className?: string }) => (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v5" />
        <path d="M12 16h.01" />
    </svg>
);

const ShieldAlertIcon = ({ className = '' }: { className?: string }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
    </svg>
);

const InsufficientFundsSheetWeb = ({
    visible,
    details,
    onClose,
    onChooseAnotherBank,
}: {
    visible: boolean;
    details: InsufficientFundsErrorDetails | null;
    onClose: () => void;
    onChooseAnotherBank: () => void;
}) => {
    const balanceSourceLabel = String(details?.balanceSource || 'available').trim().toLowerCase() === 'current'
        ? 'Current balance'
        : 'Available balance';
    return (
        <PaymentSheetShell visible={visible} onClose={onClose}>
            <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={onChooseAnotherBank} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-slate-100">
                    <ChevronLeftCircleIcon />
                </button>
                <p className="text-[15px] font-bold text-slate-900">Insufficient funds</p>
                <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
            </div>
            <div className="px-1 pt-2">
                <div className="flex flex-col items-center text-center">
                    <div className="w-[76px] h-[76px] rounded-full bg-[#FFF7ED] flex items-center justify-center text-[#B45309]">
                        <AlertCircleIcon />
                    </div>
                    <p className="mt-5 max-w-[320px] text-[14px] leading-[1.3] font-medium text-slate-500">
                        This bank account does not have enough available balance for this payment.
                    </p>
                </div>
                <div className="mt-6 rounded-[1.4rem] border border-slate-100 bg-white shadow-[0_8px_18px_rgba(15,23,42,0.07)] overflow-hidden">
                    <div className="flex items-center justify-between gap-4 px-4 py-4">
                        <span className="text-[13px] text-slate-500">Bank</span>
                        <span className="text-[13px] font-bold text-slate-900 text-right">{formatBankLabel(details)}</span>
                    </div>
                    <div className="h-px bg-slate-200/80 mx-4" />
                    <div className="flex items-center justify-between gap-4 px-4 py-4">
                        <span className="text-[13px] text-slate-500">{balanceSourceLabel}</span>
                        <span className="text-[13px] font-bold text-slate-900 text-right">{formatMoneyFromCents(details?.effectiveBalanceCents ?? null)}</span>
                    </div>
                    <div className="h-px bg-slate-200/80 mx-4" />
                    <div className="flex items-center justify-between gap-4 px-4 py-4">
                        <span className="text-[13px] text-slate-500">Payment amount</span>
                        <span className="text-[13px] font-bold text-slate-900 text-right">{formatMoneyFromCents(details?.requiredAmountCents ?? null)}</span>
                    </div>
                </div>
                <div className="mt-6 space-y-3">
                    <button type="button" onClick={onChooseAnotherBank} className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold">
                        Choose another bank
                    </button>
                    <button type="button" onClick={onClose} className="w-full h-[56px] rounded-[1.45rem] border border-sky-100 bg-slate-50 text-slate-500 text-[15px] font-bold">
                        Close
                    </button>
                </div>
            </div>
        </PaymentSheetShell>
    );
};

const UserLimitExceededSheetWeb = ({
    visible,
    limitType,
    limitAmountCents,
    resetDate,
    onClose,
}: {
    visible: boolean;
    limitType: LimitExceededErrorDetails['limitType'] | null;
    limitAmountCents: number;
    resetDate: string | Date | null;
    onClose: () => void;
}) => {
    const formattedResetDate = (() => {
        if (!resetDate) return 'soon';
        const date = resetDate instanceof Date ? resetDate : new Date(resetDate);
        if (Number.isNaN(date.getTime())) return 'soon';
        return date.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    })();
    const limitLabel = getLimitTypeLabel(limitType);
    return (
        <PaymentSheetShell visible={visible} onClose={onClose}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10" />
                <p className="text-[15px] font-bold text-slate-900">Limit reached</p>
                <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
            </div>
            <div className="px-1 pt-2">
                <div className="flex flex-col items-center text-center">
                    <div className="w-[76px] h-[76px] rounded-full bg-[#FFF7ED] flex items-center justify-center text-[#B45309]">
                        <AlertCircleIcon />
                    </div>
                    <p className="mt-5 max-w-[320px] text-[14px] leading-[1.3] font-medium text-slate-500">
                        This payment would exceed your {limitLabel} bank transfer limit.
                    </p>
                </div>
                <div className="mt-6 rounded-[1.4rem] border border-slate-100 bg-white shadow-[0_8px_18px_rgba(15,23,42,0.07)] overflow-hidden">
                    <div className="flex items-start gap-3 px-4 py-4">
                        <span className="text-slate-400 text-[16px] leading-none mt-0.5">•</span>
                        <p className="text-[13px] text-slate-500">Your limit: <span className="font-bold text-slate-900">{formatPaymentLimitDollars(limitAmountCents)} per {limitLabel}</span></p>
                    </div>
                    <div className="h-px bg-slate-200/80 mx-4" />
                    <div className="flex items-start gap-3 px-4 py-4">
                        <span className="text-slate-400 text-[16px] leading-none mt-0.5">•</span>
                        <p className="text-[13px] text-slate-500">Next reset: <span className="font-bold text-slate-900">{formattedResetDate}</span></p>
                    </div>
                </div>
                <div className="mt-6">
                    <button type="button" onClick={onClose} className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold">
                        Got it
                    </button>
                </div>
            </div>
        </PaymentSheetShell>
    );
};

const ClaimablePaymentConfirmationSheetWeb = ({
    visible,
    counterpartyName,
    claimWindowDays = 20,
    onContinue,
    onCancel,
    continueTitle = 'Continue',
}: {
    visible: boolean;
    counterpartyName: string;
    claimWindowDays?: number;
    onContinue: () => void;
    onCancel: () => void;
    continueTitle?: string;
}) => {
    const resolvedName = String(counterpartyName || '').trim() || 'This person';
    return (
        <PaymentSheetShell visible={visible} onClose={onCancel}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10" />
                <p className="text-[15px] font-bold text-slate-900">Confirm payment</p>
                <button type="button" onClick={onCancel} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
            </div>
            <div className="px-1 pt-2">
                <div className="flex flex-col items-center text-center">
                    <div className="w-[76px] h-[76px] rounded-full bg-[#FFF7ED] flex items-center justify-center text-[#B45309]">
                        <ShieldAlertIcon />
                    </div>
                    <p className="mt-5 max-w-[320px] text-[20px] leading-[1.2] font-bold text-slate-900">{resolvedName} isn&apos;t verified to receive payments yet.</p>
                    <p className="mt-2 max-w-[320px] text-[14px] leading-[1.35] font-medium text-slate-500">
                        Send it now, and they can finish verification and claim it later in Clero.
                    </p>
                </div>
                <div className="mt-6 rounded-[1.4rem] border border-slate-100 bg-white shadow-[0_8px_18px_rgba(15,23,42,0.07)] p-4">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-[13px] font-bold text-slate-900">Claim window</span>
                        <span className="text-[13px] font-bold text-slate-900">{claimWindowDays} days</span>
                    </div>
                    <div className="h-px bg-slate-200/80 my-4" />
                    <div className="rounded-[1rem] bg-emerald-50 p-4 flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
                            <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                                <CheckIcon size={11} />
                            </div>
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-slate-900">Refund protection</p>
                            <p className="mt-1 text-[12px] leading-[1.35] text-slate-500">
                                If they don&apos;t claim it within {claimWindowDays} days, it&apos;s refunded to you automatically.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-6 space-y-3">
                    <button type="button" onClick={onContinue} className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold">
                        {continueTitle}
                    </button>
                    <button type="button" onClick={onCancel} className="w-full h-[56px] rounded-[1.45rem] border border-sky-100 bg-slate-50 text-slate-500 text-[15px] font-bold">
                        Cancel
                    </button>
                </div>
            </div>
        </PaymentSheetShell>
    );
};

const OrganizerRejectedSheetWeb = ({
    visible,
    message,
    onClose,
}: {
    visible: boolean;
    message: string;
    onClose: () => void;
}) => (
    <PaymentSheetShell visible={visible} onClose={onClose}>
        <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10" />
            <p className="text-[15px] font-bold text-slate-900">Payment unavailable</p>
            <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
        </div>
        <div className="px-1 pt-2">
            <div className="flex flex-col items-center text-center">
                <div className="w-[76px] h-[76px] rounded-full bg-[#FFF7ED] flex items-center justify-center text-[#B45309]">
                    <ShieldAlertIcon />
                </div>
                <p className="mt-5 max-w-[320px] text-[20px] leading-[1.2] font-bold text-slate-900">{message}</p>
                <p className="mt-2 max-w-[320px] text-[14px] leading-[1.35] font-medium text-slate-500">
                    You cannot complete payment for this request at this time. They must be verified on Clero before payments can continue.
                </p>
            </div>
            <div className="mt-6">
                <button type="button" onClick={onClose} className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold">
                    OK
                </button>
            </div>
        </div>
    </PaymentSheetShell>
);

const IneligibleBankSheetWeb = ({
    visible,
    onClose,
}: {
    visible: boolean;
    onClose: () => void;
}) => (
    <PaymentSheetShell visible={visible} onClose={onClose}>
        <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10" />
            <p className="text-[15px] font-bold text-slate-900">Bank account ineligible</p>
            <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
        </div>
        <div className="px-1 pt-2">
            <div className="rounded-[1.4rem] border border-slate-100 bg-white shadow-[0_8px_18px_rgba(15,23,42,0.07)] p-4 space-y-4">
                <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    <div>
                        <p className="text-[13px] font-bold text-slate-900">Name mismatch</p>
                        <p className="mt-1 text-[12px] leading-[1.35] text-slate-500">
                            The name on this bank account doesn&apos;t match the legal name verified on your Clero profile.
                        </p>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    <div>
                        <p className="text-[13px] font-bold text-slate-900">Read-only transactions</p>
                        <p className="mt-1 text-[12px] leading-[1.35] text-slate-500">
                            You can still view this account&apos;s transactions, but payments are disabled.
                        </p>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    <div>
                        <p className="text-[13px] font-bold text-slate-900">How to fix</p>
                        <p className="mt-1 text-[12px] leading-[1.35] text-slate-500">
                            Link a bank account matching your legal name, or update your verified name and then relink this bank.
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-6">
                <button type="button" onClick={onClose} className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold">
                    Got it
                </button>
            </div>
        </div>
    </PaymentSheetShell>
);

const PaymentNoticeSheetWeb = ({
    notice,
    onClose,
}: {
    notice: PaymentNoticeState | null;
    onClose: () => void;
}) => {
    if (!notice) return null;
    const isSupport = notice.category === 'support_required';
    const isWarning = notice.category === 'validation' || notice.category === 'bank_action_required' || notice.category === 'verification_required';
    return (
        <PaymentSheetShell visible={!!notice} onClose={onClose}>
            <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10" />
                <p className="text-[15px] font-bold text-slate-900">{notice.title}</p>
                <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 text-[22px] hover:bg-slate-100">✕</button>
            </div>
            <div className="px-1 pt-2">
                <div className="flex flex-col items-center text-center">
                    <div className={`w-[76px] h-[76px] rounded-full flex items-center justify-center ${isSupport ? 'bg-rose-50 text-rose-600' : isWarning ? 'bg-[#FFF7ED] text-[#B45309]' : 'bg-sky-50 text-sky-600'}`}>
                        <AlertCircleIcon />
                    </div>
                    <p className="mt-5 max-w-[320px] text-[14px] leading-[1.35] font-medium text-slate-500">
                        {notice.body}
                    </p>
                </div>
                <div className="mt-6">
                    <div className="space-y-3">
                        {notice.actionLabel && notice.onAction ? (
                            <button
                                type="button"
                                onClick={notice.onAction}
                                className="w-full h-[56px] rounded-[1.45rem] bg-slate-950 text-white text-[15px] font-bold"
                            >
                                {notice.actionLabel}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={onClose}
                            className={`w-full h-[56px] rounded-[1.45rem] text-[15px] font-bold ${notice.actionLabel && notice.onAction ? 'border border-sky-100 bg-slate-50 text-slate-500' : 'bg-slate-950 text-white'}`}
                        >
                            {notice.actionLabel && notice.onAction ? 'Close' : (notice.retryable ? 'OK' : 'Got it')}
                        </button>
                    </div>
                </div>
            </div>
        </PaymentSheetShell>
    );
};

const IdentityVerificationProgressModal = ({
    visible,
    stage,
    progress,
}: {
    visible: boolean;
    stage: string;
    progress: number;
}) => {
    if (!visible) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-[32px] border border-slate-200 bg-white px-7 py-9 text-center shadow-[0_28px_70px_rgba(15,23,42,0.2)]">
                <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
                    <div className="absolute inset-0 animate-ping rounded-full border border-teal-200 opacity-40" />
                    <div className="absolute inset-2 animate-spin rounded-full border-2 border-transparent border-r-teal-300 border-t-teal-600" />
                    <div className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-teal-50 text-teal-700">
                        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-8 w-8">
                            <path d="M12 3 5 6v5c0 4.6 2.8 8.1 7 10 4.2-1.9 7-5.4 7-10V6l-7-3Z" />
                            <path d="m9 12 2 2 4-4" />
                        </svg>
                    </div>
                </div>
                <h3 className="mt-6 text-[28px] font-black leading-tight tracking-tight text-slate-900">Verifying your identity</h3>
                <p className="mt-3 text-[15px] leading-6 text-slate-600">This usually takes less than 30 seconds.</p>
                <p className="mt-1 text-[14px] leading-6 text-slate-500">Hold tight while we confirm everything with Astra.</p>
                <div className="mt-7 rounded-2xl bg-slate-50 px-4 py-3 text-[13px] font-bold text-teal-700" aria-live="polite">{stage}</div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-700 transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
                </div>
                <p className="mt-2 text-[12px] font-bold text-slate-400">{Math.round(progress)}%</p>
                <p className="mt-6 text-[12px] font-medium text-slate-400">🔒 Your data is encrypted and secure</p>
            </div>
        </div>
    );
};

export default PayerPage;
